import { useState, useRef, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { apiService } from '@/services/api';
import contentCalendarService from '@/services/contentCalendarService';
import { smmApi } from '@/services/smmApi';
import { CREDIT_COSTS, CREDIT_THRESHOLDS } from '@/config/creditCosts';
import LowCreditModal from '@/components/LowCreditModal';
import CreditDeductionConfirmModal from '@/components/CreditDeductionConfirmModal';
import { subscriptionService } from '@/services/subscriptionService';
import { creditService } from '@/services/creditService';
import { useCredits } from '@/hooks/useCredits';
// Async infographic generation — POST returns immediately (status=pending),
// the final S3 URL is delivered by polling GET /.../status/{post_id}.
import { useInfographicGeneration } from '@/hooks/useInfographicGeneration';
// Async carousel generation — same dispatch-then-poll pattern as infographic.
// POST returns immediately (status=pending); the per-slide URLs are delivered
// by polling GET /content-generation/carousel/status/{post_id}.
import { useCarouselGeneration } from '@/hooks/useCarouselGeneration';
import { useStaticPostGeneration } from '@/hooks/useStaticPostGeneration';
import { useVideoGeneration } from '@/hooks/useVideoGeneration';
import { useLogoCorners } from '@/hooks/useLogoCorners';
import LogoPlacementSection from './LogoPlacementSection';
import type { ObservanceItem } from '@/components/observances/ObservancePicker';
import { DEFAULT_LOGO_STYLE, sameLogoStyle, type ApplyLogoResponse, type LogoPosition, type LogoStyle } from '@/types/logoPlacement';

// Mirrors db_layer/schemas/utilities/credits_schemas.py EntitlementSummary —
// returned by GET /subscriptions/current as `entitlements`.
// A theme as people read it, one name however it was stored ("EducationalPost",
// "Educational Posts" → "Educational Post"; "Brand story" → "Brand Story") —
// the Performance Intelligence Engine names themes the same way.
const themeLabel = (theme?: string | null): string => {
  const words = (theme || '').replace(/_/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2').split(/\s+/).filter(Boolean);
  if (!words.length) return 'Other';
  if (words[words.length - 1].toLowerCase() === 'posts') words[words.length - 1] = 'Post';
  return words.map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
};

interface EntitlementRow {
  module_id: number;
  module_code: string;
  module_name: string;
  billing_model: 'FREE_UNLIMITED' | 'INCLUDED_QUOTA' | 'CREDIT_ONLY' | string;
  monthly_limit: number | null;
  used_this_cycle: number;
  remaining: number | null;
  credit_cost: number | null;
  pricing_inr: number | null;
}

// Maps a post's content format (+ carousel_slides, for Carousel) to the
// entitlement module code that generating it would actually consume —
// mirrors carousel_controller.py's _SLIDES_TO_MODULE_CODE and the format
// codes registered in module_master. Single point of truth for this
// mapping on the frontend; keep in sync with the backend if either changes.
const resolveModuleCode = (format: string | undefined, carouselSlides?: number | null): string | null => {
  const fmt = (format || '').toLowerCase();
  if (fmt === 'static image' || fmt === 'static') return 'STATIC_POST';
  if (fmt === 'infographic') return 'INFOGRAPHIC';
  if (fmt === 'carousel') {
    if (carouselSlides === 2) return 'CAROUSEL_2_SLIDES';
    if (carouselSlides === 6) return 'CAROUSEL_6_SLIDES';
    return 'CAROUSEL_4_SLIDES'; // default when unset — matches CAROUSEL_TOTAL_SLIDES fallback
  }
  if (fmt === 'video' || fmt === 'reel' || fmt === 'short') return 'VIDEO_POST_15SEC';
  if (fmt === 'article' || fmt === 'miniblog') return 'SMM_STATIC_IMAGE';
  return null; // formats with no credit-gated generate action in this tab
};

// Returns true for post formats whose generated asset is a video file (MP4) and
// therefore must be rendered with a <video> element rather than <img> in tiles.
const isVideoAssetFormat = (fmt?: string): boolean =>
  !!fmt && ['video', 'reel', 'short', 'tutorial'].includes(fmt.toLowerCase());

type ToastType = 'success' | 'error' | 'info';

// A user-facing message for a failed action (an error or a message string).
// The backend reports a short balance as the bare code "INSUFFICIENT_CREDITS";
// anything else already has a readable message (see apiService.request).
const friendlyError = (err: any): string => {
  const message = typeof err === 'string' ? err : err?.message;
  if (String(message || '').includes('INSUFFICIENT_CREDITS')) {
    return 'Not enough credits for this. Buy credits or upgrade your plan.';
  }
  return message || 'Something went wrong. Please try again.';
};

// Classifies a calendar load error by its HTTP status and error code (they
// travel in err.response; err.message is only readable text).
const calendarErrorKind = (err: any): 'expired' | 'exhausted' | null => {
  const status = err?.response?.status;
  const detail = err?.response?.data?.detail;
  const code = String((detail && typeof detail === 'object' ? detail.errorCode : '') || err?.response?.data?.errorCode || '');
  const text = String(err?.message || '').toLowerCase();
  if (status === 402 || code === 'CPE-010' || text.includes('subscription has expired')) return 'expired';
  if (status === 503 || code === 'CPE-009' || text.includes('gemini api key')) return 'exhausted';
  return null;
};

// ── Video generation loading card helpers ───────────────────────────────────
// Video is the only SMM format slow enough (often 4-5+ min, up to the 15 min
// poll timeout) to need more than a plain spinner while it works.

const formatElapsedTime = (totalSeconds: number): string => {
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
};

// Rotates every 25s so the wait reads as active progress rather than a
// stuck spinner. Purely cosmetic — the backend has no per-stage progress to
// report, so this describes the pipeline's real stages in a rough,
// approximate order rather than claiming precise live status.
const VIDEO_GEN_STAGE_MESSAGES = [
  'Directing the scenes...',
  'Generating video with AI...',
  'Adding motion and polish...',
  'Mixing in audio...',
  'Finalizing the render...',
];

const getVideoGenStageMessage = (elapsedSec: number): string =>
  VIDEO_GEN_STAGE_MESSAGES[
    Math.min(Math.floor(elapsedSec / 25), VIDEO_GEN_STAGE_MESSAGES.length - 1)
  ];

// Estimated-progress bar — there's no real per-job progress to report (the
// backend is a single opaque background task), so this is a deliberately
// honest approximation: an asymptotic curve that climbs fast early, slows
// down, and never claims 100% until the job actually reports done. Tuned so
// ~90s (a fast run) reads ~63%, ~3min (typical) reads ~86%, and it keeps
// creeping toward 95% even on an unusually slow run rather than stalling.
const getVideoGenProgressPct = (elapsedSec: number): number =>
  Math.round(95 * (1 - Math.exp(-elapsedSec / 90)));

// Per-post video generation start time, persisted so the elapsed timer and
// progress bar survive closing/reopening the modal and page refreshes.
// Cleared once the job reports done or failed.
const videoGenStartKey = (postId: string): string => `smm_video_gen_started_at:${postId}`;

interface Post {
  id: string;
  date: string;
  dayName: string;
  platform: string;
  platformIcon: string;
  themeType: string;
  format: string;
  formatIcon: string;
  status: string;
  caption: string;
  creativeCopy: string;
  script: string;
  hashtags: string[];
  time: string;
  scheduledTime?: string | null; // "HH:MM" user override; null = suggested time
  imageUrl?: string;
  cta: string;
  creativeAsset?: any;
  captionLength?: string;
  structureFormat?: string;
  engagementStyle?: string;
  visualRequirement?: string;
  rewriteCount?: number;
  userEdited?: boolean;
  governanceStatus?: string;
  carouselSlides?: number | null;
  copyChangedSinceImage?: boolean;
  creative_copy?: string;
  carousel_slide_urls?: string[];
  carouselSlideUrls?: string[];
  is_manual?: boolean;
  isManual?: boolean;
  // Persisted async generation-job status (ContentPost.image_gen_status /
  // image_gen_error), used to resume the "Generating..." UI after a page
  // refresh instead of falling back to showing the Generate button.
  imageGenStatus?: string;
  imageGenError?: string | null;
  // Only meaningful for StaticImage-format posts — the campaign_id needed
  // to resume polling a StaticImage generation job after a page refresh.
  staticCampaignId?: string | null;
  imageOptions?: Array<{ url: string; angle?: string; issues?: string[]; slide_number?: number; selected?: boolean }> | null;
  image_options?: Array<{ url: string; angle?: string; issues?: string[]; slide_number?: number; selected?: boolean }> | null;
  selectedImageIndex?: number | null;
  selected_image_index?: number | null;
  // Logo redesign (static posts): the logo-free image and the chosen corner.
  // rawImageUrl is NULL for legacy posts whose image has the logo baked in.
  rawImageUrl?: string | null;
  logoPosition?: LogoPosition | null;
  // Logo redesign (Carousel): one pre-logo image + independently-chosen
  // corner PER SLIDE, index-aligned with carouselSlideUrls. An entry is null
  // for a slide not yet resolved (e.g. still faulty) or for a legacy
  // carousel generated before this existed.
  rawCarouselSlideUrls?: (string | null)[] | null;
  carouselLogoPositions?: (LogoPosition | null)[] | null;
}


const monthShortNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Builds every calendar date from startIso to endIso inclusive (both
// "YYYY-MM-DD"). The subscription period is a rolling ~30-day window that
// can start/end on any day of any month — never assume it aligns to a
// calendar-month boundary.
const buildDateRange = (startIso: string, endIso: string): string[] => {
  const dates: string[] = [];
  if (!startIso || !endIso) return dates;
  const cur = new Date(`${startIso}T00:00:00`);
  const end = new Date(`${endIso}T00:00:00`);
  if (isNaN(cur.getTime()) || isNaN(end.getTime())) return dates;
  while (cur <= end) {
    const y = cur.getFullYear();
    const m = String(cur.getMonth() + 1).padStart(2, '0');
    const d = String(cur.getDate()).padStart(2, '0');
    dates.push(`${y}-${m}-${d}`);
    cur.setDate(cur.getDate() + 1);
  }
  return dates;
};

// "27 Jul" style label — always shows the month since the range can span
// calendar-month boundaries.
const formatDayMonthLabel = (iso: string): string => {
  const parts = iso.split('-');
  if (parts.length !== 3) return iso;
  const monthIdx = parseInt(parts[1], 10) - 1;
  const day = parseInt(parts[2], 10);
  return `${day} ${monthShortNames[monthIdx] ?? ''}`.trim();
};

const getFormatOptions = (currentFormat: string) => {
  const normalized = currentFormat?.toLowerCase();
  if (normalized === 'video') {
    return [
      { value: 'Video', label: 'Video' },
      { value: 'Static Image', label: 'Static Image' },
      { value: 'Infographic', label: 'Infographic' },
      { value: 'Carousel', label: 'Carousel' }
    ];
  } else if (normalized === 'carousel') {
    return [
      { value: 'Carousel', label: 'Carousel' },
      { value: 'Static Image', label: 'Static Image' },
      { value: 'Infographic', label: 'Infographic' }
    ];
  } else if (normalized === 'static image' || normalized === 'static') {
    return [
      { value: 'Static Image', label: 'Static Image' },
      { value: 'Infographic', label: 'Infographic' }
    ];
  } else if (normalized === 'infographic') {
    return [
      { value: 'Infographic', label: 'Infographic' },
      { value: 'Static Image', label: 'Static Image' }
    ];
  } else {
    return [
      { value: 'Static Image', label: 'Static Image' },
      { value: 'Video', label: 'Video' },
      { value: 'Infographic', label: 'Infographic' },
      { value: 'Carousel', label: 'Carousel' }
    ];
  }
};

// Internal default time (the time editor's starting value) when a platform has
// no suggested window. A user override is stored in ContentPost.scheduled_time
// as "HH:MM" (24h). Never shown as a suggestion: see postTimeLabel.
const SUGGESTED_POST_TIME = '12:00';

// "HH:MM" (24h) → "h:mm AM/PM"; null/empty falls back to `fallback` (the
// platform's real suggested window start when the caller has one via
// getSuggestedResetTime — the bare default here is only for callers with no
// platform context at all).
const formatPostTime = (hhmm?: string | null, fallback: string = SUGGESTED_POST_TIME): string => {
  const [h, m] = (hhmm || fallback).split(':').map(Number);
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`;
};

// Communication Direction's `best_posting_times` (communication_direction_mapper.py)
// only ever takes one of two shapes:
//   - static default: one shared AM/PM suffix, e.g. "Fri-Sat, 12-3 PM"
//   - analytics-computed: an independent AM/PM suffix per number,
//     e.g. "Mon-Fri, 9 AM-11 AM"
// The day-range part (before the last comma) is intentionally ignored here —
// the time editor only lets a user change the hour, never the post's date.
const parseSuggestedTimeWindow = (
  bestPostingTimes?: string | null
): { startHour: number; endHour: number } | null => {
  if (!bestPostingTimes) return null;
  const timePart = bestPostingTimes.split(',').pop() || '';
  const match = timePart.match(/(\d{1,2})\s*(AM|PM)?\s*-\s*(\d{1,2})\s*(AM|PM)/i);
  if (!match) return null;
  const [, h1Str, ampm1, h2Str, ampm2] = match;
  const startAmPm = (ampm1 || ampm2 || '').toUpperCase(); // shared-suffix form omits ampm1
  const endAmPm = ampm2.toUpperCase();
  const to24Hour = (h: number, amPm: string): number => (amPm === 'PM' ? (h % 12) + 12 : h % 12);
  return {
    startHour: to24Hour(parseInt(h1Str, 10), startAmPm),
    endHour: to24Hour(parseInt(h2Str, 10), endAmPm),
  };
};

const formatHourLabel = (hour: number): string => `${hour % 12 || 12} ${hour >= 12 ? 'PM' : 'AM'}`;

const formatSuggestedWindowLabel = (window: { startHour: number; endHour: number }): string =>
  `${formatHourLabel(window.startHour)}-${formatHourLabel(window.endHour)}`;

// Inclusive of both ends — "12-3 PM" is understood to include exactly 3 PM.
const isHourWithinWindow = (hhmm: string, window: { startHour: number; endHour: number }): boolean => {
  const hour = parseInt(hhmm.split(':')[0], 10);
  const { startHour, endHour } = window;
  return startHour <= endHour
    ? hour >= startHour && hour <= endHour
    : hour >= startHour || hour <= endHour; // defensive: window wrapping past midnight
};

const getSuggestedCTA = (platform: string, theme: string): string => {
  const plat = platform?.toLowerCase();
  const th = theme?.toLowerCase();

  if (plat === 'linkedin') {
    if (th?.includes('educational') || th?.includes('learn')) {
      return 'What are your thoughts on this? Let us know in the comments.';
    }
    if (th?.includes('authority') || th?.includes('thought')) {
      return 'Save this post for later inspiration.';
    }
    return 'Tag a teammate who needs to see this.';
  } else if (plat === 'instagram') {
    if (th?.includes('engagement') || th?.includes('community')) {
      return 'Double-tap if you found this helpful!';
    }
    return 'Bookmark this for your next strategy session.';
  } else if (plat === 'youtube') {
    return 'Subscribe to our channel for more weekly insights!';
  } else if (plat === 'facebook') {
    return 'Share your experience in the comments below.';
  }
  return 'Save this post for later inspiration.';
};

interface ContentCalendarWorkspaceProps {
  // Post to open in the editor once posts load (notification deep link).
  // Matches a post id, or a static_campaign_id for Static Post notifications
  // created before they carried the post id.
  focusPostId?: string | null;
  onFocusPostHandled?: () => void;
}

export default function ContentCalendarWorkspace({ focusPostId = null, onFocusPostHandled }: ContentCalendarWorkspaceProps = {}) {
  const { invalidateCredits, balance, isLoadingBalance } = useCredits();
  const [viewMode, setViewMode] = useState<'calendar' | 'list'>('calendar');

  // Low Credit Modal States
  const [modalOpen, setModalOpen] = useState(false);
  const [modalCredits, setModalCredits] = useState(0);
  const [modalRequired, setModalRequired] = useState(0);
  const [modalIsBlocker, setModalIsBlocker] = useState(false);

  const userId = localStorage.getItem('user_id') || '';

  const checkCreditsForAction = async (required: number): Promise<{ allowed: boolean; current: number }> => {
    try {
      if (!userId) return { allowed: true, current: 0 };
      const balanceData = await creditService.getBalance();
      const current = balanceData?.total_credits_balance ?? 0;

      if (current < required) {
        setModalCredits(current);
        setModalRequired(required);
        setModalIsBlocker(true);
        setModalOpen(true);
        return { allowed: false, current };
      }
      return { allowed: true, current };
    } catch (err) {
      console.error("Failed to check credits:", err);
    }
    return { allowed: true, current: 0 };
  };

  const checkLowCreditWarning = async (current: number) => {
    if (current < CREDIT_THRESHOLDS.LOW_CREDIT_WARNING) {
      setModalCredits(current);
      setModalIsBlocker(false);
      setModalOpen(true);
    }
  };

  const postActionCheck = async () => {
    try {
      const balanceData = await creditService.getBalance();
      const current = balanceData?.total_credits_balance ?? 0;
      await checkLowCreditWarning(current);
    } catch (err) {
      console.error(err);
    }
  };

  // Per-module entitlement state (billing_model/remaining/credit_cost per
  // SMM module code) — single source of truth for the Media tab's per-button
  // lock state, sourced from the same /subscriptions/current data
  // EntitlementService.check_entitlement() itself is built from. Refreshed
  // whenever the edit modal opens and after every successful generation, so
  // the lock state reflects real usage-to-date rather than a stale snapshot.
  const [entitlements, setEntitlements] = useState<EntitlementRow[]>([]);
  // Without entitlements every module looks locked — say so in the Media tab.
  const [entitlementsError, setEntitlementsError] = useState(false);
  const [isGenerateConfirmOpen, setIsGenerateConfirmOpen] = useState(false);

  const refreshEntitlements = async () => {
    try {
      // GET /subscriptions/current (not /subscription/me/{userId} — that's a
      // different, older endpoint/schema with no entitlements field). Reads
      // the user from the auth token, so it takes no userId argument.
      const subData = await subscriptionService.getCurrentSubscription();
      setEntitlements(subData?.entitlements || []);
      setEntitlementsError(false);
    } catch (err) {
      console.error('Failed to refresh entitlements:', err);
      setEntitlementsError(true);
    }
  };

  const getEntitlementForModule = (code: string | null): EntitlementRow | null => {
    if (!code) return null;
    return entitlements.find((e) => e.module_code === code) || null;
  };

  // Locked = generating this post right now would draw on credits (either
  // always CREDIT_ONLY, or an INCLUDED_QUOTA pool that's exhausted for the
  // cycle). Unknown/unmapped modules fail closed (locked) rather than
  // silently allowing a free generation.
  const isGenerationLocked = (code: string | null): boolean => {
    const ent = getEntitlementForModule(code);
    if (!ent) return Boolean(code); // no entitlement row for a real module code -> fail closed
    if (ent.billing_model === 'FREE_UNLIMITED') return false;
    if (ent.billing_model === 'INCLUDED_QUOTA') return (ent.remaining ?? 0) <= 0;
    return true; // CREDIT_ONLY or anything else -> locked
  };

  const getLockedCreditCost = (code: string | null): number | null => {
    return getEntitlementForModule(code)?.credit_cost ?? null;
  };
  // The Content Calendar renders as a single continuous view of the active
  // subscription period (subscription_start_date -> subscription_end_date),
  // not a toggleable calendar month — that period can start/end on any date
  // and may span two calendar months.
  const [subscriptionRange, setSubscriptionRange] = useState<{ start: string; end: string } | null>(null);
  // Festivals, holidays and observance days on each date (small labels on the days).
  const [dayEvents, setDayEvents] = useState<Record<string, ObservanceItem[]>>({});
  const [eventsPopupDate, setEventsPopupDate] = useState<string | null>(null);
  const [dayEventsVersion, setDayEventsVersion] = useState(0); // bump to reload the labels
  // Every event of the cycle (not only the picks) — Add Post can link a post to one.
  const [cycleEvents, setCycleEvents] = useState<ObservanceItem[]>([]);
  // A date's observances for Add Post: the day's labels (picks can be from another country's list) first,
  // then the cycle's, public holidays first.
  const festivalsOn = (date: string) => {
    const seen = new Set<string>();
    const onDate = cycleEvents.filter((o) => o.date === date)
      .sort((a, b) => Number(b.type === 'Public holiday') - Number(a.type === 'Public holiday'));
    return [...(dayEvents[date] || []), ...onDate].filter((o) => !seen.has(o.name) && seen.add(o.name));
  };
  const [addPostFestival, setAddPostFestival] = useState('');
  const [calendarId, setCalendarId] = useState<string | null>(null);
  const [posts, setPosts] = useState<Post[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoadedPosts, setHasLoadedPosts] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null);
  const toastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Drag & drop state
  const [draggingPostId, setDraggingPostId] = useState<string | null>(null);
  const [dragOverDay, setDragOverDay] = useState<string | null>(null);

  // Gemini API key exhaustion & Subscription expiration states
  const [isSubscriptionExpired, setIsSubscriptionExpired] = useState(false);
  const [isApiExhausted, setIsApiExhausted] = useState(false);
  const [isRegeneratingCalendar, setIsRegeneratingCalendar] = useState(false);
  // The cycle's calendar is being created on first visit (see loadPosts).
  const [isCreatingCalendar, setIsCreatingCalendar] = useState(false);
  const triedCreatingCalendar = useRef(false);

  // Edit modal state
  const [editPostId, setEditPostId] = useState<string | null>(null);
  const [editTab, setEditTab] = useState<'content' | 'cta' | 'hashtags' | 'media' | 'article'>('content');
  const [showSaveConfirm, setShowSaveConfirm] = useState(false);
  const [showNoLogoPublishConfirm, setShowNoLogoPublishConfirm] = useState(false);
  const [editCaption, setEditCaption] = useState('');
  const [editCreativeCopy, setEditCreativeCopy] = useState('');
  const [editScript, setEditScript] = useState('');
  const [editHashtags, setEditHashtags] = useState('');
  const [editCta, setEditCta] = useState('');

  // Posting-time override — saved on its own (not via Save Changes) so it
  // also works for already-locked posts.
  const [isEditingTime, setIsEditingTime] = useState(false);
  const [timeDraft, setTimeDraft] = useState(SUGGESTED_POST_TIME);
  const [isSavingTime, setIsSavingTime] = useState(false);
  // Raw ContentPost.user_edited: the PATCH endpoint resets user_edited to
  // false when it's missing from the payload, so a time-only save echoes it.
  const [editUserEditedRaw, setEditUserEditedRaw] = useState(false);

  // Platform (lowercased) -> raw `best_posting_times` string from
  // Communication Direction, used to check the time draft against the
  // real per-platform suggested window instead of a single hardcoded time.
  const [bestPostingTimesByPlatform, setBestPostingTimesByPlatform] = useState<Record<string, string>>({});

  // AI regeneration states
  const [aiCaptionPrompt, setAiCaptionPrompt] = useState('');
  const [aiCreativePrompt, setAiCreativePrompt] = useState('');
  const [aiScriptPrompt, setAiScriptPrompt] = useState('');
  const [aiCtaPrompt, setAiCtaPrompt] = useState('');
  const [isRegeneratingCaption, setIsRegeneratingCaption] = useState(false);
  const [isRegeneratingCreative, setIsRegeneratingCreative] = useState(false);
  const [isRegeneratingScript, setIsRegeneratingScript] = useState(false);
  const [isRegeneratingCta, setIsRegeneratingCta] = useState(false);
  const [isRegeneratingHashtags, setIsRegeneratingHashtags] = useState(false);

  // Video generation and upload states
  const [generatedVideoUrl, setGeneratedVideoUrl] = useState<string>('');
  // Surfaced error from the FAL video-generation path (was only logged before,
  // which made failures look like a locked / "credits required" post state).
  const [falVideoError, setFalVideoError] = useState<string | null>(null);
  // Drives the friendly progress card (elapsed timer, rotating status line,
  // estimated-progress bar) shown while a video is generating — video is the
  // only SMM format slow enough (often 2-4+ min) to need this rather than a
  // plain spinner. The start time is a wall-clock timestamp kept per post in
  // localStorage (videoGenStartKey), so it survives re-renders, refreshes and
  // switching between posts without drifting.
  const [videoGenElapsedSec, setVideoGenElapsedSec] = useState(0);
  // True once the user has clicked generate/regenerate in this session
  // so the button can show "Regenerate (credits)" after a failure instead of "CREDITS REQUIRED".
  const [hasAttemptedGenerate, setHasAttemptedGenerate] = useState(false);

  // Photo upload state
  const [selectedImageUrl, setSelectedImageUrl] = useState<string>('');
  const [selectedVideoUrl, setSelectedVideoUrl] = useState<string>('');
  const [enhancedImageUrl, setEnhancedImageUrl] = useState<string>('');
  const [isEnhancing, setIsEnhancing] = useState(false);

  // Async generation jobs (dispatch then poll), tracked per post: generating
  // one post never blocks or overwrites another post's job. Anything shown in
  // the editor must read the OPEN post's job (isPendingFor(editingPost.id)).
  // Async infographic generation job (dispatch then poll).
  const infographicGeneration = useInfographicGeneration();
  // Async carousel generation job (dispatch then poll).
  const carouselGeneration = useCarouselGeneration();
  // Async static post generation job (dispatch then poll) — hotfix: all SMM
  // generation now runs as a background job to avoid gateway timeouts.
  const staticPostGeneration = useStaticPostGeneration();
  // Async video generation job (dispatch then poll) — same hotfix.
  const videoGeneration = useVideoGeneration();
  const [articleContent, setArticleContent] = useState<string>('');
  const [isGeneratingArticle, setIsGeneratingArticle] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [articleRegenerationInput, setArticleRegenerationInput] = useState<string>('');
  const [carouselSlideUrls, setCarouselSlideUrls] = useState<string[]>([]);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [aiPrompt, setAiPrompt] = useState('');
  const [regenerateInstructions, setRegenerateInstructions] = useState('');
  const videoInputRef = useRef<HTMLInputElement>(null);

  // Manual photo upload states
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const [isManualImageUploaded, setIsManualImageUploaded] = useState(false);
  const [isDraggingMedia, setIsDraggingMedia] = useState(false);
  const [photoUploadError, setPhotoUploadError] = useState<string | null>(null);
  const photoInputRef = useRef<HTMLInputElement>(null);
  // The post whose image is being enhanced (the job can outlive the open editor).
  const [enhancingPostId, setEnhancingPostId] = useState<string | null>(null);
  // Post whose Enhance Image stopped being watched before it finished.
  const [enhanceTimedOutPostId, setEnhanceTimedOutPostId] = useState<string | null>(null);

  // Delete post states
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeletingPost, setIsDeletingPost] = useState(false);

  // Add Post modal states
  const [showAddPostModal, setShowAddPostModal] = useState(false);
  const [addPostPlatform, setAddPostPlatform] = useState('LinkedIn');
  const [addPostFormat, setAddPostFormat] = useState('Static Image');
  const [addPostDate, setAddPostDate] = useState('');
  const [addPostTheme, setAddPostTheme] = useState('Brand story');
  const [addPostCaption, setAddPostCaption] = useState('');
  const [addPostCreativeCopy, setAddPostCreativeCopy] = useState('');
  const [isAddingPost, setIsAddingPost] = useState(false);
  const [addPostError, setAddPostError] = useState<string | null>(null);
  const [isManualMode, setIsManualMode] = useState(false);
  const [availableSlotsInfo, setAvailableSlotsInfo] = useState<{
    available_add_slots: number;
    total_posts_allotted: number;
    remaining_posts_count: number;
    deleted_slots: any[];
  } | null>(null);

  // Hold the post being edited for read-only reference
  const [editingPost, setEditingPost] = useState<Post | null>(null);
  const [isSavingCreativeCopy, setIsSavingCreativeCopy] = useState(false);

  // Generation state of the post open in the editor (per-post, see above).
  const openPostId = editingPost?.id ?? null;
  const isGeneratingFALVideo = videoGeneration.isPendingFor(openPostId);
  const isPostGenerating = (postId: string) =>
    staticPostGeneration.isPendingFor(postId) ||
    carouselGeneration.isPendingFor(postId) ||
    infographicGeneration.isPendingFor(postId) ||
    videoGeneration.isPendingFor(postId);

  // A job can finish after the user has moved to another post, so its
  // callbacks only touch editor state when their post is still the one open
  // (the calendar tile is always updated).
  const editingPostIdRef = useRef<string | null>(null);
  useEffect(() => {
    editingPostIdRef.current = openPostId;
  }, [openPostId]);
  const isOpenInEditor = (postId: string) => editingPostIdRef.current === postId;

  // Publish state
  const [isPublishing, setIsPublishing] = useState(false);
  const [publishedPostIds, setPublishedPostIds] = useState<Set<string>>(new Set());
  // Live posts (published here, or marked posted in Governance) can't be
  // published again: the editor shows "Published" instead of the button.
  const isEditingPostPublished = Boolean(
    editingPost && (editingPost.governanceStatus === 'SCHEDULED_MANUAL' || publishedPostIds.has(editingPost.id)),
  );

  // V2 state extensions
  const [aiVariations, setAiVariations] = useState<any[]>([]);
  const [isGeneratingVariations, setIsGeneratingVariations] = useState(false);
  const [isSelectingVariation, setIsSelectingVariation] = useState(false);
  const [editCtaType, setEditCtaType] = useState<string>('SOFT_ENGAGEMENT');

  const mapFormatFromBackend = (fmt: string): string => {
    switch (fmt?.toUpperCase()) {
      case 'VIDEO': return 'Video';
      case 'STATIC': return 'Static Image';
      case 'INFOGRAPHIC': return 'Infographic';
      case 'CAROUSEL': return 'Carousel';
      case 'REEL': return 'Reel';
      case 'SHORT': return 'Short';
      case 'ARTICLE': return 'Article';
      case 'THREAD': return 'Thread';
      case 'POLL': return 'Poll';
      case 'TUTORIAL': return 'Tutorial';
      case 'SINGLE_TWEET': case 'SINGLE TWEET': return 'Single Tweet';
      case 'STORY_SERIES': case 'STORY SERIES': return 'Story Series';
      case 'EVENT': return 'Event';
      case 'DOCUMENT': return 'Document';
      case 'INTERVIEW': return 'Interview';
      default:
        if (!fmt) return 'Static Image';
        return fmt.charAt(0).toUpperCase() + fmt.slice(1).toLowerCase();
    }
  };

  const mapFormatToBackend = (fmt: string): string => {
    switch (fmt) {
      case 'Video': return 'VIDEO';
      case 'Static Image': return 'STATIC';
      case 'Infographic': return 'INFOGRAPHIC';
      case 'Carousel': return 'CAROUSEL';
      case 'Reel': return 'REEL';
      case 'Short': return 'SHORT';
      case 'Article': return 'ARTICLE';
      case 'Thread': return 'THREAD';
      case 'Poll': return 'POLL';
      case 'Tutorial': return 'TUTORIAL';
      case 'Single Tweet': return 'SINGLE_TWEET';
      case 'Story Series': return 'STORY_SERIES';
      case 'Event': return 'EVENT';
      case 'Document': return 'DOCUMENT';
      case 'Interview': return 'INTERVIEW';
      default: return fmt?.toUpperCase() || 'STATIC';
    }
  };

  const mapStatusFromBackend = (status: string): string => {
    switch (status?.toUpperCase()) {
      case 'DONE': return 'scheduled';
      case 'SCHEDULED_MANUAL': return 'published';
      case 'POSTED': return 'published';
      case 'IN_PROGRESS': return 'approved';
      case 'NOT_STARTED': return 'draft';
      default: return 'draft';
    }
  };

  const mapPlatformFromBackend = (plat: string): string => {
    switch (plat?.toUpperCase()) {
      case 'YOUTUBE': return 'YouTube';
      case 'FACEBOOK': return 'Facebook';
      case 'INSTAGRAM': return 'Instagram';
      case 'LINKEDIN': return 'LinkedIn';
      default: return plat || 'LinkedIn';
    }
  };

  const mapPlatformToBackend = (plat: string): string => {
    return plat?.toUpperCase() || 'LINKEDIN';
  };

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'published': return 'bg-sky-100 text-sky-700 border-sky-200';
      case 'scheduled': return 'bg-emerald-100 text-emerald-700 border-emerald-200';
      case 'approved': return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'draft': return 'bg-amber-100 text-amber-700 border-amber-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'published': return 'Published';
      case 'scheduled': return 'Scheduled';
      case 'approved': return 'Approved';
      case 'draft': return 'Draft';
      default: return status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Draft';
    }
  };

  const getPlatformColor = (platform: string) => {
    switch (platform) {
      case 'LinkedIn': return 'bg-sky-50 text-sky-700 border-sky-200';
      case 'Instagram': return 'bg-pink-50 text-pink-700 border-pink-200';
      case 'Twitter': return 'bg-gray-100 text-gray-700 border-gray-300';
      case 'YouTube': return 'bg-red-50 text-red-700 border-red-200';
      case 'Facebook': return 'bg-indigo-50 text-indigo-700 border-indigo-200';
      default: return 'bg-gray-50 text-gray-700 border-gray-200';
    }
  };

  const getFormatBadge = (format: string) => {
    switch (format) {
      case 'Video': return 'bg-red-50 text-red-600';
      case 'Reel': return 'bg-rose-50 text-rose-600';
      case 'Carousel': return 'bg-teal-50 text-teal-600';
      case 'Thread': return 'bg-gray-50 text-gray-600';
      case 'Article': return 'bg-sky-50 text-sky-600';
      case 'Poll': return 'bg-violet-50 text-violet-600';
      case 'Story Series': return 'bg-amber-50 text-amber-600';
      case 'Event': return 'bg-emerald-50 text-emerald-600';
      case 'Document': return 'bg-slate-50 text-slate-600';
      case 'Interview': return 'bg-orange-50 text-orange-600';
      default: return 'bg-gray-50 text-gray-600';
    }
  };

  const getPlatformIcon = (platform: string) => {
    switch (platform) {
      case 'LinkedIn': return 'ri-linkedin-box-line';
      case 'Instagram': return 'ri-instagram-line';
      case 'Twitter': return 'ri-twitter-x-line';
      case 'YouTube': return 'ri-youtube-line';
      case 'Facebook': return 'ri-facebook-circle-line';
      default: return 'ri-global-line';
    }
  };

  const getDayName = (dateStr: string) => {
    if (!dateStr) return 'Mon';
    const d = new Date(`${dateStr}T00:00:00`);
    return isNaN(d.getTime()) ? 'Mon' : d.toLocaleDateString('en-US', { weekday: 'short' });
  };

  // Resolve the active subscription's current ~30-day cycle once — the
  // calendar renders exactly this window, whatever its length or month
  // boundaries.
  const loadSubscriptionRange = useCallback(async () => {
    setError(null);
    try {
      const subData = await subscriptionService.getCurrentSubscription();
      // current_cycle_start/current_cycle_end is the ~30-day content cycle
      // containing today — start_date/end_date is the subscription's full
      // (potentially multi-year) term and must not be used as the calendar
      // render window.
      if (subData?.current_cycle_start && subData?.current_cycle_end) {
        setSubscriptionRange({ start: subData.current_cycle_start, end: subData.current_cycle_end });
      }
    } catch (err) {
      console.error('Failed to load subscription period:', err);
      setError(`Could not determine your subscription period. ${friendlyError(err)}`);
    }
  }, []);

  useEffect(() => {
    loadSubscriptionRange();
  }, [loadSubscriptionRange]);

  // The cycle's festivals / holidays / days, by date — public holidays first.
  useEffect(() => {
    if (!subscriptionRange) return;
    let cancelled = false;
    apiService.getCalendarObservances(subscriptionRange.start, subscriptionRange.end)
      .then((res) => {
        if (cancelled) return;
        const byDay: Record<string, ObservanceItem[]> = {};
        (res.observances || []).forEach((obs) => { (byDay[obs.date] ||= []).push(obs); });
        Object.values(byDay).forEach((list) =>
          list.sort((a, b) => Number(b.type === 'Public holiday') - Number(a.type === 'Public holiday')));
        setDayEvents(byDay);
      })
      .catch((err) => console.error('Failed to load observances for the calendar:', err));
    return () => { cancelled = true; };
  }, [subscriptionRange, dayEventsVersion]);

  useEffect(() => {
    if (!subscriptionRange) return;
    apiService.getCurrentCycleObservances()
      .then((res) => setCycleEvents(res.observances || []))
      .catch((err) => console.error('Failed to load the cycle\'s observances:', err));
  }, [subscriptionRange]);

  // Best-posting-time windows per platform, for the time editor's suggested-
  // time check below. Non-fatal on failure — the check simply stays off
  // (formatSuggestedWindowLabel/isHourWithinWindow both no-op on an empty map).
  useEffect(() => {
    (async () => {
      try {
        const userId = localStorage.getItem('user_id') || '';
        if (!userId) return;
        const direction = await smmApi.getCommunicationDirection(userId);
        const byPlatform: Record<string, string> = {};
        for (const p of direction?.platforms || []) {
          if (p?.name && p?.best_posting_times) {
            byPlatform[String(p.name).toLowerCase()] = p.best_posting_times;
          }
        }
        setBestPostingTimesByPlatform(byPlatform);
      } catch (err) {
        console.error('Failed to load best-posting-times:', err);
      }
    })();
  }, []);

  // Load posts dynamically from backend
  const loadPosts = useCallback(async () => {
    if (!subscriptionRange) return;
    const userId = localStorage.getItem('user_id') || 'default_user_id';
    // "month" is only used by the backend to resolve the (single, active)
    // calendar row — it already returns every post across the full
    // subscription window regardless of which month is passed here.
    const monthYear = subscriptionRange.start.slice(0, 7);

    setIsLoading(true);
    setError(null);
    setIsSubscriptionExpired(false);
    setIsApiExhausted(false);

    try {
      // Resolve the calendarId for this user+month before fetching workspace posts.
      // No calendar for the current cycle yet (a new subscription, or a
      // renewal) — create it here, as the Content Planning Engine page does;
      // otherwise the workspace just stays blank. Once per visit.
      let calendar = await contentCalendarService.fetchOrNull(userId, monthYear);
      if (!calendar && !triedCreatingCalendar.current) {
        triedCreatingCalendar.current = true;
        setIsCreatingCalendar(true);
        try {
          calendar = await contentCalendarService.generateCalendar(userId, monthYear, false);
          showToast('Content Calendar successfully generated!');
        } finally {
          setIsCreatingCalendar(false);
        }
      }
      if (!calendar?.id) {
        throw new Error('No content calendar for this subscription cycle yet.');
      }
      const resolvedCalendarId = calendar.id;
      setCalendarId(resolvedCalendarId);
      const data = await apiService.getImplementationWorkspace(resolvedCalendarId, 'list');
      const normalizedPosts: Post[] = (data.posts || []).map((wv: any) => {
        const platformName = mapPlatformFromBackend(wv.platform);
        const formatName = mapFormatFromBackend(wv.format);
        const statusName = mapStatusFromBackend(wv.governance_status);

        let initialCarouselSlides: string[] = [];
        if (wv.carousel_slide_urls && Array.isArray(wv.carousel_slide_urls) && wv.carousel_slide_urls.length > 0) {
          initialCarouselSlides = wv.carousel_slide_urls;
        } else if (wv.asset_s3_key) {
          try {
            const parsed = JSON.parse(wv.asset_s3_key);
            if (Array.isArray(parsed) && parsed.length > 0) {
              initialCarouselSlides = parsed;
            }
          } catch (e) {}
        }
        if (initialCarouselSlides.length === 0 && (wv.image_url || wv.creative_asset?.url)) {
          const fallbackUrl = wv.image_url || wv.creative_asset?.url;
          if (fallbackUrl && (formatName === 'Carousel' || wv.format?.toUpperCase() === 'CAROUSEL')) {
            initialCarouselSlides = [fallbackUrl];
          }
        }

        const initialImageUrl = initialCarouselSlides.length > 0
          ? initialCarouselSlides[0]
          : (wv.creative_asset?.url || wv.image_url || undefined);

        return {
          id: wv.post_id,
          date: wv.date,
          dayName: getDayName(wv.date),
          platform: platformName,
          platformIcon: getPlatformIcon(platformName),
          themeType: wv.theme || 'Brand story',
          format: formatName,
          formatIcon: '',
          status: statusName,
          caption: wv.full_caption || wv.caption_preview || '',
          creativeCopy: wv.creative_copy || wv.full_caption || wv.caption_preview || '',
          script: wv.script || '',
          hashtags: wv.hashtags || [],
          time: formatPostTime(wv.scheduled_time, getSuggestedResetTime(platformName)),
          scheduledTime: wv.scheduled_time || null,
          imageUrl: initialImageUrl,
          carousel_slide_urls: initialCarouselSlides.length > 0 ? initialCarouselSlides : undefined,
          carouselSlideUrls: initialCarouselSlides.length > 0 ? initialCarouselSlides : undefined,
          creativeAsset: wv.creative_asset || (initialImageUrl ? { url: initialImageUrl } : undefined),
          cta: wv.cta || '',
          captionLength: wv.caption_length || '',
          structureFormat: wv.structure_format || '',
          engagementStyle: wv.engagement_style || '',
          visualRequirement: wv.visual_requirement || '',
          rewriteCount: wv.rewrite_count || 0,
          userEdited: Boolean(wv.user_edited || wv.is_locked),
          governanceStatus: wv.governance_status || 'NOT_STARTED',
          copyChangedSinceImage: wv.copy_changed_since_image || false,
          is_manual: Boolean(wv.is_manual),
          isManual: Boolean(wv.is_manual),
          imageGenStatus: wv.image_gen_status || 'NOT_STARTED',
          imageGenError: wv.image_gen_error || null,
          staticCampaignId: wv.static_campaign_id || null,
          imageOptions: wv.image_options || wv.imageOptions || null,
          image_options: wv.image_options || wv.imageOptions || null,
          selectedImageIndex: wv.selected_image_index ?? wv.selectedImageIndex ?? null,
          selected_image_index: wv.selected_image_index ?? wv.selectedImageIndex ?? null,
        };
      });

      // Keep only posts that fall within the active subscription window
      // (defensive — the backend already scopes generation to this range).
      const filteredPosts = normalizedPosts.filter(
        p => p.date >= subscriptionRange.start && p.date <= subscriptionRange.end
      );

      // Sort posts by date/time ascending
      filteredPosts.sort((a, b) => a.date.localeCompare(b.date));
      setPosts(filteredPosts);
    } catch (err: any) {
      const errKind = calendarErrorKind(err);
      if (errKind === 'expired') {
        setIsSubscriptionExpired(true);
        setError('Your subscription has expired. Renew your subscription to continue using the Content Calendar.');
      } else if (errKind === 'exhausted') {
        setIsApiExhausted(true);
        setError('Content generation failed due to API key credit exhaustion or temporary service unavailability.');
      } else {
        setError(`Couldn't load your posts. ${friendlyError(err)}`);
      }
    } finally {
      setIsLoading(false);
      setHasLoadedPosts(true);
    }
  }, [subscriptionRange]);

  const handleRetryCalendarGeneration = async () => {
    if (!subscriptionRange) return;
    const userId = localStorage.getItem('user_id') || 'default_user_id';
    const monthYear = subscriptionRange.start.slice(0, 7);

    setIsRegeneratingCalendar(true);
    setIsLoading(true);
    setError(null);
    setIsApiExhausted(false);

    try {
      await contentCalendarService.generateCalendar(userId, monthYear, true);
      await loadPosts();
      showToast('Content Calendar successfully generated!');
    } catch (err: any) {
      const errKind = calendarErrorKind(err);
      if (errKind === 'expired') {
        setIsSubscriptionExpired(true);
        setError('Your subscription has expired. Renew your subscription to continue using the Content Calendar.');
      } else if (errKind === 'exhausted') {
        setIsApiExhausted(true);
        setError('Content generation failed due to API key credit exhaustion or temporary service unavailability.');
      } else {
        setError(`Couldn't regenerate the content calendar. ${friendlyError(err)}`);
      }
    } finally {
      setIsLoading(false);
      setIsRegeneratingCalendar(false);
    }
  };

  useEffect(() => {
    loadPosts();
  }, [loadPosts]);

  // Retry for the load error banner: the posts can only load once the
  // subscription period is known.
  const handleRetryLoad = () => {
    if (subscriptionRange) loadPosts();
    else loadSubscriptionRange();
  };

  const stats = {
    thisMonthPosts: posts.length,
    // Saved / Ready = posts marked as DONE in governance (ready to publish) or user-saved
    totalScheduled: posts.filter((p) => p.governanceStatus === 'DONE' || p.userEdited === true).length,
    // In Draft = posts not yet completed or published (still in draft/refinement)
    totalDraft: posts.filter((p) => p.governanceStatus === 'NOT_STARTED' || p.governanceStatus === 'IN_PROGRESS').length,
    // Published = posts marked as POSTED / SCHEDULED_MANUAL in governance execution
    totalPublished: posts.filter((p) => p.governanceStatus === 'POSTED' || p.governanceStatus === 'SCHEDULED_MANUAL' || p.status === 'published').length,
  };

  const postsByDay: Record<string, Post[]> = {};
  posts.forEach((post) => {
    if (!postsByDay[post.date]) postsByDay[post.date] = [];
    postsByDay[post.date].push(post);
  });

  // Rolling day sequence for the whole subscription window — a flat list of
  // ISO dates from start to end, with no "days in month" math and no
  // restart/offset at each calendar-month boundary.
  const calendarDays: string[] = subscriptionRange
    ? buildDateRange(subscriptionRange.start, subscriptionRange.end)
    : [];

  // Leading blanks (once, at the very start of the range) so the sequence
  // still lines up under the Sun-Sat header row; unlike the old month-grid
  // logic, this offset is never re-applied at a month change.
  const leadingBlanks = subscriptionRange
    ? new Date(`${subscriptionRange.start}T00:00:00`).getDay()
    : 0;
  const calendarCells: (string | null)[] = [
    ...Array(leadingBlanks).fill(null),
    ...calendarDays,
  ];

  const todayIso = new Date().toISOString().slice(0, 10);

  const subscriptionRangeLabel = subscriptionRange
    ? `${formatDayMonthLabel(subscriptionRange.start)} – ${formatDayMonthLabel(subscriptionRange.end)}`
    : error ? 'Period unavailable' : 'Loading...';

  // One toast at a time: a new one replaces the old and restarts the timer.
  // Errors stay up longer so they can be read.
  const showToast = (msg: string, type: ToastType = 'success', durationMs?: number) => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToast({ message: msg, type });
    toastTimerRef.current = setTimeout(() => setToast(null), durationMs ?? (type === 'error' ? 8000 : 2500));
  };

  // Toast from a generation job. Names the post when it isn't the one open,
  // so "generated successfully" isn't mistaken for the post on screen.
  const showJobToast = (postId: string, msg: string, type: ToastType = 'success', durationMs?: number) => {
    const post = isOpenInEditor(postId) ? null : posts.find((p) => p.id === postId);
    showToast(post ? `${post.platform} ${post.format} · ${formatDayMonthLabel(post.date)}: ${msg}` : msg, type, durationMs);
  };

  const handleDownload = async (post: Post) => {
    // For Article and MiniBlog posts — use dedicated article download
    const fmt = post.format?.toLowerCase() || post.contentFormat?.toLowerCase() || '';
    if (fmt === 'article' || fmt === 'miniblog') {
      try {
        showToast('Preparing article download...', 'info');
        const fullPost = await apiService.getPostCard(post.id);
        await handleDownloadArticlePost(fullPost);
      } catch (err: any) {
        console.error('Failed to fetch full post for download:', err);
        showToast(err?.message || 'Failed to download article', 'error');
      }
      return;
    }

    try {
      showToast('Preparing download package...', 'info');
      const blob = await apiService.exportSinglePostZip(post.id);

      const platformStr = (post.platform || 'POST').toUpperCase();
      const formatStr = (post.format || 'CONTENT').toUpperCase().replace(/\s+/g, '_');
      const dateStr = post.date || new Date().toISOString().split('T')[0];
      const shortId = `post_${post.id.slice(0, 8)}`;
      const fileName = `${platformStr}_${formatStr}_${dateStr}_${shortId}.zip`;

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      a.click();
      URL.revokeObjectURL(url);
      showToast('Post package downloaded');
    } catch (err: any) {
      console.error('Failed to download post zip:', err);
      showToast(err?.message || 'Failed to export post', 'error');
    }
  };

  const handleEditClick = async (post: Post) => {
    // Other posts' jobs keep running in the background. Only this post's
    // finished (error/timeout) state is dropped so it opens clean; a job of
    // its own that is still pending keeps showing as "Generating...".
    staticPostGeneration.clear(post.id);
    carouselGeneration.clear(post.id);
    infographicGeneration.clear(post.id);
    setFalVideoError(null);
    setEnhanceTimedOutPostId(null);
    setHasAttemptedGenerate(false);
    setIsLoading(true);
    setAiVariations([]);
    try {
      const fullPost = await apiService.getPostCard(post.id);
      setEditPostId(post.id);
      setEditTab('content');
      setEditCaption(fullPost.full_caption || '');
      // Load creative_copy independently — do NOT fall back to full_caption here.
      // Falling back would copy the caption text into the Creative Copy textarea,
      // which then gets overwritten when the user regenerates creative copy.
      setEditCreativeCopy(fullPost.creative_copy || '');
      setEditScript(fullPost.script || '');
      setEditHashtags(fullPost.hashtags ? fullPost.hashtags.map((tag: string) => tag.startsWith('#') ? tag : `#${tag}`).join(' ') : '');
      const suggestedCta = fullPost.cta || getSuggestedCTA(post.platform, post.themeType);
      setEditCta(suggestedCta);
      setIsEditingTime(false);
      setEditUserEditedRaw(Boolean(fullPost.user_edited));
      setEditCtaType(fullPost.cta_type || 'SOFT_ENGAGEMENT');
      setSelectedImageUrl(fullPost.image_url || fullPost.creative_asset?.url || '');
      const fmt = fullPost.contentFormat?.toLowerCase() || '';
      if (fmt === 'article' || fmt === 'miniblog') {
        // article_text is a separate field if backend returns it
        // otherwise creative_copy holds the article for these formats
        setArticleContent(
          fullPost.article_text ||
          fullPost.articleText ||
          fullPost.creative_copy ||
          fullPost.creativeCopy ||
          ''
        );
      }
      if (fullPost.contentFormat?.toLowerCase() === 'video' ||
        fullPost.contentFormat?.toLowerCase() === 'reel' ||
        fullPost.contentFormat?.toLowerCase() === 'short') {
        setSelectedVideoUrl(fullPost.imageUrl || '');
      } else {
        setSelectedVideoUrl('');
      }
      setEnhancedImageUrl('');
      setAiPrompt('');
      setRegenerateInstructions('');
      setIsEnhancing(false);
      setPhotoUploadError(null);
      setIsUploadingPhoto(false);
      setIsDraggingMedia(false);
      setShowDeleteConfirm(false);
      setIsDeletingPost(false);

      // Set carousel slide URLs if present
      let loadedCarouselSlides: string[] = [];
      if (fullPost.carousel_slide_urls && Array.isArray(fullPost.carousel_slide_urls) && fullPost.carousel_slide_urls.length > 0) {
        loadedCarouselSlides = fullPost.carousel_slide_urls;
      } else if (fullPost.asset_s3_key) {
        try {
          const parsed = JSON.parse(fullPost.asset_s3_key);
          if (Array.isArray(parsed) && parsed.length > 0) {
            loadedCarouselSlides = parsed;
          } else if (typeof fullPost.asset_s3_key === 'string' && fullPost.asset_s3_key.startsWith('http')) {
            loadedCarouselSlides = [fullPost.asset_s3_key];
          }
        } catch (e) {}
      }

      if (loadedCarouselSlides.length === 0 && (fullPost.image_url || fullPost.creative_asset?.url || post.imageUrl)) {
        const fallbackUrl = fullPost.image_url || fullPost.creative_asset?.url || post.imageUrl;
        if (fallbackUrl) {
          loadedCarouselSlides = [fallbackUrl];
        }
      }

      const isCarousel = (fullPost.format || post.format)?.toLowerCase() === 'carousel';
      if (isCarousel) {
        setCarouselSlideUrls(loadedCarouselSlides);
        setSelectedImageUrl(loadedCarouselSlides.length > 0 ? loadedCarouselSlides[0] : '');
      } else {
        setCarouselSlideUrls([]);
        setSelectedImageUrl(fullPost.image_url || fullPost.creative_asset?.url || post.imageUrl || '');
      }
      setCurrentSlideIndex(0);

      const loadedSlides = loadedCarouselSlides.length > 0 ? loadedCarouselSlides : (fullPost.image_url ? [fullPost.image_url] : []);
      const isManualMediaUrl = Boolean(
        [
          fullPost.image_url,
          fullPost.creative_asset?.url,
          post.imageUrl,
          ...loadedSlides,
        ].some((url) => Boolean(url && typeof url === 'string' && (url.includes('smm-uploads') || url.includes('/smm-uploads/'))))
      );
      setIsManualImageUploaded(isManualMediaUrl);

      setEditingPost({
        ...post,
        time: formatPostTime(fullPost.scheduled_time, getSuggestedResetTime(post.platform)),
        scheduledTime: fullPost.scheduled_time || null,
        caption: fullPost.full_caption || '',
        creativeCopy: fullPost.creative_copy || '',
        script: fullPost.script || '',
        hashtags: fullPost.hashtags || [],
        imageUrl: fullPost.image_url || fullPost.creative_asset?.url || undefined,
        creativeAsset: fullPost.creative_asset || undefined,
        cta: fullPost.cta || '', // Keep original cta here to detect if we suggested it
        captionLength: fullPost.caption_length || '',
        structureFormat: fullPost.structure_format || '',
        engagementStyle: fullPost.engagement_style || '',
        visualRequirement: fullPost.visual_requirement || '',
        rewriteCount: fullPost.ai_rewrite_history ? fullPost.ai_rewrite_history.length : 0,
        carouselSlides: fullPost.carousel_slides ?? null,
        copyChangedSinceImage: fullPost.copy_changed_since_image || false,
        userEdited: Boolean(fullPost.user_edited || fullPost.userEdited || fullPost.is_locked || post.userEdited),
        imageGenStatus: fullPost.image_gen_status || fullPost.imageGenStatus || post.imageGenStatus || 'NOT_STARTED',
        imageGenError: fullPost.image_gen_error ?? fullPost.imageGenError ?? post.imageGenError ?? null,
        staticCampaignId: fullPost.static_campaign_id || fullPost.staticCampaignId || post.staticCampaignId || null,
        imageOptions: fullPost.image_options || fullPost.imageOptions || post.imageOptions || post.image_options || null,
        image_options: fullPost.image_options || fullPost.imageOptions || post.imageOptions || post.image_options || null,
        selectedImageIndex: fullPost.selected_image_index ?? fullPost.selectedImageIndex ?? post.selectedImageIndex ?? post.selected_image_index ?? null,
        selected_image_index: fullPost.selected_image_index ?? fullPost.selectedImageIndex ?? post.selectedImageIndex ?? post.selected_image_index ?? null,
        rawImageUrl: fullPost.raw_image_url ?? null,
        logoPosition: fullPost.logo_position ?? null,
        rawCarouselSlideUrls: fullPost.raw_carousel_slide_urls ?? null,
        carouselLogoPositions: fullPost.carousel_logo_positions ?? null,
      });

      // Keep post item in posts list synced with full details
      setPosts(prev => prev.map(p => p.id === post.id ? {
        ...p,
        imageGenStatus: fullPost.image_gen_status || fullPost.imageGenStatus || p.imageGenStatus || 'NOT_STARTED',
        imageGenError: fullPost.image_gen_error ?? fullPost.imageGenError ?? p.imageGenError ?? null,
        imageOptions: fullPost.image_options || fullPost.imageOptions || p.imageOptions || p.image_options || null,
        image_options: fullPost.image_options || fullPost.imageOptions || p.imageOptions || p.image_options || null,
        selectedImageIndex: fullPost.selected_image_index ?? fullPost.selectedImageIndex ?? p.selectedImageIndex ?? p.selected_image_index ?? null,
        selected_image_index: fullPost.selected_image_index ?? fullPost.selectedImageIndex ?? p.selectedImageIndex ?? p.selected_image_index ?? null,
      } : p));

      // Reset AI prompts
      setAiCaptionPrompt('');
      setAiCreativePrompt('');
      setAiScriptPrompt('');
      setAiCtaPrompt('');

      // Refresh entitlement state so the Media tab's generate button lock
      // reflects real usage-to-date, not a stale snapshot from page load.
      await refreshEntitlements();
    } catch (err: any) {
      console.error('Failed to fetch post details:', err);
      showToast(`Error: ${err?.message || 'Failed to load post details'}`, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // Notification deep link: open the post's editor once the workspace posts
  // have loaded. Posts from a previous (regenerated) calendar no longer exist.
  useEffect(() => {
    if (!focusPostId || !hasLoadedPosts || isLoading) return;
    onFocusPostHandled?.();
    const target = posts.find((p) => p.id === focusPostId || p.staticCampaignId === focusPostId);
    if (target) {
      handleEditClick(target);
    } else {
      showToast('That post is no longer in your current content calendar.', 'info');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusPostId, hasLoadedPosts, isLoading]);

  const handleSaveEdit = async () => {
    if (!editPostId || !editingPost) return;

    const cleanHashtags = editHashtags.split(/\s+/).filter(Boolean).map(tag => tag.startsWith('#') ? tag.slice(1) : tag);
    const isCarousel = editingPost.format?.toLowerCase() === 'carousel';

    const payload: Record<string, any> = {
      full_caption: editCaption,
      creative_copy: editCreativeCopy,
      script: editScript,
      cta: editCta,
      hashtags: cleanHashtags,
      user_edited: true,
      governance_status: 'DONE',
      image_options: null,
    };

    const isVideoPost = ['VIDEO', 'REEL', 'SHORT'].includes(mapFormatToBackend(editingPost.format));
    // A video post's media is also kept in editingPost.imageUrl, which every path that sets it updates.
    let activeImageUrl = enhancedImageUrl || selectedImageUrl || (isVideoPost ? editingPost.imageUrl || '' : '');
    // Local, synchronously-updated mirror of carouselSlideUrls — the state
    // setter below won't be visible via the closure until a re-render, but
    // the carousel payload branch a few lines down needs the composited end
    // card URL immediately, in this same call.
    let activeCarouselSlideUrls = carouselSlideUrls;

    // If logo placement is eligible and a corner was selected (on the
    // currently-viewed slide, for a carousel) that differs from its current
    // saved corner:
    if (hasUnappliedLogoChoice && pendingLogoCorner) {
      try {
        const view = isCarousel
          ? await apiService.applyCarouselLogo(editPostId, pendingLogoCorner, currentSlideNumber, pendingStyle)
          : await apiService.applyLogo(editPostId, pendingLogoCorner, pendingStyle);
        if (isCarousel && view.carousel_slide_urls?.length) {
          activeCarouselSlideUrls = view.carousel_slide_urls;
          setCarouselSlideUrls(view.carousel_slide_urls);
          editingPost.carouselLogoPositions = view.carousel_logo_positions ?? editingPost.carouselLogoPositions;
          editingPost.rawCarouselSlideUrls = view.raw_carousel_slide_urls ?? editingPost.rawCarouselSlideUrls;
        } else if (view.image_url) {
          activeImageUrl = view.image_url;
          setSelectedImageUrl(view.image_url);
          setEnhancedImageUrl('');
          editingPost.logoPosition = (view.logo_position ?? pendingLogoCorner) as LogoPosition;
        }
      } catch (logoErr) {
        // Don't lock the post without the logo the user picked.
        console.warn('Failed to apply logo placement during save:', logoErr);
        showToast(`Couldn't apply the logo: ${friendlyError(logoErr)}`, 'error');
        return;
      }
    }

    const isArticle = editingPost.format?.toLowerCase() === 'article' || editingPost.format?.toLowerCase() === 'miniblog';
    if (isArticle) {
      payload.creative_copy = articleContent || editCreativeCopy || editCaption;
      payload.full_caption = editCaption || articleContent || '';
      payload.creative_asset = null;
      payload.image_url = '';
      payload.carousel_slide_urls = [];
      payload.asset_s3_key = '';
    } else if (isCarousel) {
      if (activeCarouselSlideUrls.length > 0) {
        payload.carousel_slide_urls = activeCarouselSlideUrls;
        payload.asset_s3_key = JSON.stringify(activeCarouselSlideUrls);
        payload.image_url = activeCarouselSlideUrls[0];
        payload.creative_asset = {
          url: activeCarouselSlideUrls[0],
          asset_type: 'CAROUSEL_SLIDE',
          file_name: 'carousel_slides',
        };
      } else {
        payload.carousel_slide_urls = [];
        payload.asset_s3_key = '';
        payload.image_url = '';
        payload.creative_asset = null;
      }
    } else {
      const finalImageUrl = activeImageUrl;
      if (finalImageUrl) {
        payload.creative_asset = {
          url: finalImageUrl,
          asset_type: mapFormatToBackend(editingPost.format) === 'VIDEO' ? 'VIDEO_MP4' : 'STATIC_IMAGE',
          file_name: editingPost.creativeAsset?.file_name || 'media_asset',
        };
        payload.image_url = finalImageUrl;
        payload.carousel_slide_urls = [];
        payload.asset_s3_key = '';
      } else {
        payload.creative_asset = null;
        payload.image_url = '';
        payload.carousel_slide_urls = [];
        payload.asset_s3_key = '';
      }
    }

    try {
      // First update generic fields
      const updatedPost = await apiService.updatePostCard(editPostId, payload);

      // Sync CTA type as well (uses V2 CTA endpoint)
      let ctaTypeError: unknown = null;
      try {
        await apiService.editCTA(editPostId, editCta, editCtaType);
      } catch (ctaErr) {
        console.warn('Failed to update CTA type validation, falling back to generic update:', ctaErr);
        ctaTypeError = ctaErr;
      }

      const finalImageUrl = enhancedImageUrl || selectedImageUrl;
      setPosts((prev) =>
        prev.map((p) =>
          p.id === editPostId
            ? {
              ...p,
              caption: updatedPost.full_caption,
              creativeCopy: updatedPost.creative_copy || updatedPost.full_caption,
              script: updatedPost.script || '',
              hashtags: cleanHashtags,
              imageUrl: isCarousel
                ? (carouselSlideUrls.length > 0 ? carouselSlideUrls[0] : undefined)
                : (finalImageUrl || undefined),
              carousel_slide_urls: isCarousel ? (carouselSlideUrls.length > 0 ? carouselSlideUrls : []) : undefined,
              carouselSlideUrls: isCarousel ? (carouselSlideUrls.length > 0 ? carouselSlideUrls : []) : undefined,
              creativeAsset: isCarousel
                ? (carouselSlideUrls.length > 0 ? { url: carouselSlideUrls[0], asset_type: 'CAROUSEL_SLIDE', file_name: 'carousel_slides' } : undefined)
                : (finalImageUrl ? { url: finalImageUrl, asset_type: mapFormatToBackend(editingPost.format) === 'VIDEO' ? 'VIDEO_MP4' : 'STATIC_IMAGE', file_name: 'media_asset' } : undefined),
              cta: updatedPost.cta || '',
              userEdited: true,
              governanceStatus: 'DONE',
              imageOptions: null,
              image_options: null,
            }
            : p
        )
      );
      setEditPostId(null);
      setEditingPost(null);
      if (ctaTypeError) {
        showToast(`Post saved, but its CTA type couldn't be updated: ${friendlyError(ctaTypeError)}`, 'error');
      } else {
        showToast('Post locked and saved successfully');
      }
    } catch (err: any) {
      console.error('Failed to save manual edits:', err);
      showToast(`Error: ${friendlyError(err)}`, 'error');
    }
  };

  // The real per-platform suggested window (from Communication Direction),
  // parsed on demand — null when we don't have data for this platform yet
  // (fetch still in flight, or failed), in which case the time check below
  // simply stays off rather than warning against the wrong window.
  const getSuggestedWindowForPlatform = (platform?: string) => {
    if (!platform) return null;
    return parseSuggestedTimeWindow(bestPostingTimesByPlatform[platform.toLowerCase()]);
  };

  // "No override" reset target: the start of the platform's real suggested
  // window when known, falling back to the old fixed default otherwise.
  const getSuggestedResetTime = (platform?: string): string => {
    const window = getSuggestedWindowForPlatform(platform);
    return window ? `${String(window.startHour).padStart(2, '0')}:00` : SUGGESTED_POST_TIME;
  };

  // What a post shows as its posting time: the user's own time, else the start of the
  // platform's real suggested window — "No suggested time" when we have neither, rather
  // than showing the internal 12:00 default as if it were a suggestion.
  const postTimeLabel = (post: { platform?: string; scheduledTime?: string | null }): string => {
    if (post.scheduledTime) return formatPostTime(post.scheduledTime);
    return getSuggestedWindowForPlatform(post.platform)
      ? formatPostTime(getSuggestedResetTime(post.platform))
      : 'No suggested time';
  };

  const handleSaveTime = async () => {
    if (!editPostId || !editingPost) return;
    // Picking the suggested window's start stores no override (it keeps following the
    // suggestion). With no suggestion for this platform every pick is stored — even 12:00,
    // which would otherwise be dropped and shown as "No suggested time".
    const scheduledTime =
      getSuggestedWindowForPlatform(editingPost.platform) && timeDraft === getSuggestedResetTime(editingPost.platform)
        ? null
        : timeDraft;
    setIsSavingTime(true);
    try {
      await apiService.updatePostCard(editPostId, {
        scheduled_time: scheduledTime,
        user_edited: editUserEditedRaw,
      });
      // Always format timeDraft (the literal time just picked), never
      // scheduledTime — scheduledTime is null whenever the pick exactly
      // matches this platform's suggested window start (by design, so it
      // keeps tracking the live suggestion instead of storing a redundant
      // override), and formatPostTime(null) falls back to a hardcoded
      // 12:00 default that has nothing to do with this platform's real
      // window. timeDraft is always the concrete "HH:MM" the user chose.
      const time = formatPostTime(timeDraft);
      setEditingPost(prev => prev ? { ...prev, time, scheduledTime } : null);
      setPosts(prev => prev.map(p => p.id === editPostId ? { ...p, time, scheduledTime } : p));
      setIsEditingTime(false);
      showToast(scheduledTime ? `Posting time changed to ${time}` : 'Posting time reset to the suggested time');
    } catch (err: any) {
      console.error('Failed to save posting time:', err);
      showToast(`Error: ${friendlyError(err)}`, 'error');
    } finally {
      setIsSavingTime(false);
    }
  };

  const handleCancelEdit = () => {
    setIsEditingTime(false);
    setEditPostId(null);
    setEditingPost(null);
    setSelectedImageUrl('');
    setSelectedVideoUrl('');
    setEnhancedImageUrl('');
    setAiPrompt('');
    setAiVariations([]);
    setCarouselSlideUrls([]);
    setCurrentSlideIndex(0);
    setShowDeleteConfirm(false);
    setIsDeletingPost(false);
    setPhotoUploadError(null);
    setIsUploadingPhoto(false);
    setIsDraggingMedia(false);
  };

  // Observance Post: the observance on the chosen date is found automatically
  // (the first one); it can be switched when the day has several.
  const isObservancePost = addPostTheme === 'Observance Post';
  const addPostObservances = isObservancePost ? festivalsOn(addPostDate) : [];
  const addPostObservance = addPostObservances.find((o) => o.name === addPostFestival) ?? addPostObservances[0];

  const handleOpenAddPost = async (prefilledDate?: string, festival?: string) => {
    setAddPostError(null);
    setAddPostDate(prefilledDate || '');
    setAddPostFestival(festival || '');
    setAddPostPlatform('LinkedIn');
    setAddPostFormat('Static Image');
    setAddPostTheme(festival ? 'Observance Post' : 'Brand story');
    setAddPostCaption('');
    setAddPostCreativeCopy('');
    setIsManualMode(false);
    setShowAddPostModal(true);

    if (calendarId) {
      try {
        const slotsData = await apiService.getAvailableSlots(calendarId);
        setAvailableSlotsInfo(slotsData);
        if (slotsData?.deleted_slots && slotsData.deleted_slots.length > 0) {
          const matchingSlot = prefilledDate
            ? slotsData.deleted_slots.find((s: any) => s.scheduled_date === prefilledDate)
            : slotsData.deleted_slots[0];

          if (matchingSlot) {
            if (matchingSlot.scheduled_date) {
              setAddPostDate(matchingSlot.scheduled_date);
            }
            if (matchingSlot.deleted_platform) {
              setAddPostPlatform(mapPlatformFromBackend(matchingSlot.deleted_platform));
            }
            if (matchingSlot.allowed_replacement_formats && matchingSlot.allowed_replacement_formats.length > 0) {
              setAddPostFormat(mapFormatFromBackend(matchingSlot.allowed_replacement_formats[0]));
            }
            if (matchingSlot.deleted_theme && !festival) {
              setAddPostTheme(matchingSlot.deleted_theme);
            }
            setIsManualMode(false);
          } else {
            setAddPostDate(prefilledDate || '');
            setIsManualMode(true);
          }
        } else {
          setIsManualMode(true);
        }
      } catch (err) {
        console.warn('Could not fetch available slots:', err);
        setIsManualMode(true);
      }
    } else {
      setIsManualMode(true);
    }
  };

  const handleCreatePost = async () => {
    if (!calendarId) {
      setAddPostError('Calendar ID is not available. Please refresh the page.');
      return;
    }
    if (!addPostDate) {
      setAddPostError('Please select a scheduled date for this post.');
      return;
    }
    if (isObservancePost && !addPostObservance) {
      setAddPostError('No observance on this date. Choose another date or a different theme.');
      return;
    }

    setIsAddingPost(true);
    setAddPostError(null);

    try {
      const backendPlatform = mapPlatformToBackend(addPostPlatform);
      const backendFormat = mapFormatToBackend(addPostFormat);

      const payload: Record<string, any> = {
        platform: backendPlatform,
        format: backendFormat,
        scheduled_date: addPostDate,
        theme: addPostTheme || 'Brand story',
        caption: addPostCaption || addPostCreativeCopy || '',
        creative_copy: addPostCreativeCopy || addPostCaption || '',
      };
      // An Observance Post is written for the observance found on its date.
      if (isObservancePost && addPostObservance) payload.observance_name = addPostObservance.name;

      if (!isManualMode && availableSlotsInfo?.deleted_slots && availableSlotsInfo.deleted_slots.length > 0) {
        const targetSlot = availableSlotsInfo.deleted_slots.find((s: any) => s.scheduled_date === addPostDate) || availableSlotsInfo.deleted_slots[0];
        payload.slot_id = targetSlot.deleted_post_id;
        payload.scheduled_date = targetSlot.scheduled_date || addPostDate;
        payload.is_manual = false;
      } else {
        payload.is_manual = true;
      }

      const response = await apiService.addPost(calendarId, payload);
      const createdPostCard = response?.post;

      if (createdPostCard) {
        const platformName = mapPlatformFromBackend(createdPostCard.platform);
        const formatName = mapFormatFromBackend(createdPostCard.format);
        const statusName = mapStatusFromBackend(createdPostCard.governance_status);

        const newPost: Post = {
          id: createdPostCard.post_id || createdPostCard.id,
          date: createdPostCard.date || addPostDate,
          dayName: getDayName(createdPostCard.date || addPostDate),
          platform: platformName,
          platformIcon: getPlatformIcon(platformName),
          themeType: createdPostCard.theme || addPostTheme || 'Brand story',
          format: formatName,
          formatIcon: '',
          status: statusName,
          caption: createdPostCard.full_caption || createdPostCard.caption_preview || addPostCaption,
          creativeCopy: createdPostCard.creative_copy || addPostCreativeCopy,
          script: createdPostCard.script || '',
          hashtags: createdPostCard.hashtags || [],
          time: formatPostTime(createdPostCard.scheduled_time, getSuggestedResetTime(platformName)),
          scheduledTime: createdPostCard.scheduled_time || null,
          imageUrl: createdPostCard.image_url || createdPostCard.creative_asset?.url,
          carousel_slide_urls: createdPostCard.carousel_slide_urls,
          carouselSlideUrls: createdPostCard.carousel_slide_urls,
          creativeAsset: createdPostCard.creative_asset,
          cta: createdPostCard.cta || '',
          captionLength: createdPostCard.caption_length || '',
          structureFormat: createdPostCard.structure_format || '',
          engagementStyle: createdPostCard.engagement_style || '',
          visualRequirement: createdPostCard.visual_requirement || '',
          rewriteCount: 0,
          userEdited: Boolean(createdPostCard.user_edited),
          governanceStatus: createdPostCard.governance_status || 'NOT_STARTED',
        };

        setPosts((prev) => {
          const updated = [...prev, newPost];
          updated.sort((a, b) => a.date.localeCompare(b.date));
          return updated;
        });

        setShowAddPostModal(false);
        if (isObservancePost && addPostObservance) setDayEventsVersion((v) => v + 1); // its festival now has a post: label the day
        showToast('Post added successfully!');
      } else {
        await loadPosts();
        setShowAddPostModal(false);
        showToast('Post added successfully!');
      }
    } catch (err: any) {
      console.error('Failed to create post:', err);
      const errMsg = err?.message || err?.detail || 'Failed to add post. Please check your inputs.';
      setAddPostError(errMsg);
      showToast(`Error: ${errMsg}`, 'error');
    } finally {
      setIsAddingPost(false);
    }
  };

  const handlePublishPost = async () => {
    if (!editingPost || isPublishing) return;
    const userId = localStorage.getItem('user_id') || 'default_user_id';
    setIsPublishing(true);
    try {
      const response = await apiService.publishPost({ post_id: editingPost.id, user_id: userId });
      if (!response.success) {
        if (response.error_code === 'REAUTH_REQUIRED') {
          showToast(
            (response.error || 'Authentication required.') +
            ' Please re-authenticate from your Profile section.',
            'error'
          );
        } else {
          showToast(friendlyError(response.error || 'Failed to publish post. Please try again.'), 'error');
        }
        return;
      }
      const publishedId = editingPost.id;
      setPublishedPostIds((prev) => new Set(prev).add(publishedId));
      // Live now: locked like a saved post and counted as posted everywhere
      // (the server stores the same: POSTED / SCHEDULED_MANUAL, locked).
      const markPublished = (p: any) =>
        p.id === publishedId ? { ...p, userEdited: true, governanceStatus: 'SCHEDULED_MANUAL' } : p;
      setPosts((prev) => prev.map(markPublished));
      setEditingPost((prev) => (prev ? markPublished(prev) : prev));
      showToast('Post published — it is now locked.');
    } catch (err: any) {
      showToast(friendlyError(err), 'error');
    } finally {
      setIsPublishing(false);
    }
  };

  const handleExportCSV = async () => {
    // exportCalendarCSV expects the calendar's own id, not the user id — the
    // backend filters posts by calendar_id, so passing user_id here would
    // return zero/wrong-calendar rows.
    if (!calendarId) {
      showToast('Calendar is still loading — try again in a moment.', 'info');
      return;
    }
    const userId = localStorage.getItem('user_id') || 'default_user_id';
    try {
      showToast('Generating CSV export...', 'info');
      const csvBlob = await apiService.exportCalendarCSV(calendarId);
      const url = URL.createObjectURL(csvBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `calendar_export_${userId}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      showToast('CSV downloaded successfully');
    } catch (err: any) {
      console.error('Failed to export CSV:', err);
      showToast('Failed to export CSV', 'error');
    }
  };

  // Shared post-generation pipeline for static posts: dispatch the async
  // job, then when the poller reports done/error/timeout apply UI side
  // effects. Also reused by the resume-after-refresh effect below.
  const handleStaticPostJobCallbacks = (postId: string, isRegenerate: boolean) => ({
    onDone: async (
      _campaignId: string,
      outputPath: string | null,
      content: string | null,
      imageOptions?: { url: string; angle?: string; issues?: string[] }[] | null,
      selectedImageIndex?: number | null,
      meta?: { alreadyGenerated?: boolean }
    ) => {
      try {
        if (outputPath) {
          // Prefer the S3/CDN URL — the backend already saved a sanitized SVG there
          if (isOpenInEditor(postId)) {
            setSelectedImageUrl(outputPath);
            setEnhancedImageUrl('');
            setIsManualImageUploaded(false);
            setRegenerateInstructions('');
          }
          setEditingPost(prev => prev && prev.id === postId ? {
            ...prev,
            imageUrl: outputPath,
            imageOptions: imageOptions,
            image_options: imageOptions,
            selectedImageIndex: selectedImageIndex,
            selected_image_index: selectedImageIndex,
          } : prev);
          setPosts(prev => prev.map(p => p.id === postId ? {
            ...p,
            imageUrl: outputPath,
            imageOptions: imageOptions,
            image_options: imageOptions,
            selectedImageIndex: selectedImageIndex,
            selected_image_index: selectedImageIndex,
          } : p));
          try {
            await apiService.persistGeneratedAsset({ post_id: postId, image_url: outputPath });
          } catch (e) {
            console.warn('[StaticPost] Failed to persist asset URL to DB:', e);
          }
          await refreshLogoFields(postId);
          showJobToast(
            postId,
            meta?.alreadyGenerated
              ? 'Post already has a generated image.'
              : imageOptions && imageOptions.length > 0
                ? "Static post generated, but it didn't pass automatic quality checks — review the flagged options below."
                : isRegenerate ? 'Static post regenerated successfully!' : 'Static post generated successfully!'
          );
        } else if (content) {
          // Fallback: render inline via data URI. Sanitize bare '&'
          // chars that break SVG/XML parsing.
          const sanitized = content.replace(/&(?!(?:amp|lt|gt|quot|apos|#[0-9]+|#x[0-9a-fA-F]+);)/g, '&amp;');
          const base64Svg = btoa(unescape(encodeURIComponent(sanitized)));
          const svgDataUrl = `data:image/svg+xml;base64,${base64Svg}`;
          if (isOpenInEditor(postId)) {
            setSelectedImageUrl(svgDataUrl);
            setEnhancedImageUrl('');
            setIsManualImageUploaded(false);
          }
          setEditingPost(prev => prev && prev.id === postId ? { ...prev, imageUrl: svgDataUrl } : prev);
          showJobToast(postId, 'Static post generated successfully!');
          await postActionCheck();
        } else {
          showJobToast(postId, 'Static post generated, but no preview available.', 'info');
        }
      } finally {
        await Promise.all([refreshEntitlements(), invalidateCredits()]);
      }
    },
    // The error itself is kept per post by staticPostGeneration and shown
    // above the Generate button (errorMessageFor) while that post is open.
    onError: async (_campaignId: string, message: string) => {
      console.error('Failed to generate static post:', message);
      showJobToast(postId, `Error: ${friendlyError(message)}`, 'error');
      // The backend refunds credits on generation failure, but the credit
      // balance shown in the UI only updates when we re-fetch it — without
      // this, a failed (and server-side refunded) generation looks like it
      // permanently consumed credits, since the stale pre-refund balance
      // just stays on screen.
      await Promise.all([refreshEntitlements(), invalidateCredits()]);
    },
    onTimeout: async () => {
      showJobToast(postId, 'Generation timed out, try again. The job may still complete server-side.', 'info', 8000);
      await Promise.all([refreshEntitlements(), invalidateCredits()]);
    },
  });

  const handleGenerateStaticPost = async (overrideForceRegenerate?: boolean) => {
    if (!editingPost) return;
    const userId = localStorage.getItem('user_id') || 'default_user_id';

    // No client-side balance pre-check here — the locked-button + confirm-popup
    // flow (see resolveModuleCode/isGenerationLocked) already gated this click
    // against real pool/credit entitlement before handleGenerate was ever
    // invoked. The backend's consume_or_charge is the actual authority and
    // still rejects with InsufficientCreditsException if the balance is
    // somehow stale by the time this request lands.
    //
    // Generation now runs as a FastAPI BackgroundTask on the backend (hotfix:
    // all SMM generation paths run in the background to avoid gateway
    // timeouts) — the POST returns immediately with status="pending" and
    // this dispatches + polls via useStaticPostGeneration instead of
    // awaiting a single blocking response. start() marks this post pending
    // (clearing its previous error) before dispatching.
    showToast('Generating static post... This may take a moment. You can work on other modules while this finishes.', 'info');

    const cleanHashtags = editHashtags.split(/\s+/).filter(Boolean).map(tag => tag.startsWith('#') ? tag.slice(1) : tag);
    const isRegenerate = overrideForceRegenerate === true || Boolean(editingPost.imageUrl || selectedImageUrl);
    const postId = editingPost.id;

    await staticPostGeneration.start(
      {
        user_id: userId,
        caption: editCaption || editingPost.caption,
        creative_copy: editCreativeCopy || editingPost.creative_copy || editingPost.creativeCopy || undefined,
        hashtags: cleanHashtags,
        post_id: postId,
        aspect_ratio: editingPost.platform?.toLowerCase() === 'linkedin' || editingPost.platform?.toLowerCase() === 'youtube' ? '16:9' : '4:5',
        output_format: 'SVG',
        instructions: regenerateInstructions.trim() || undefined,
        layout_note: regenerateInstructions.trim() || undefined,
        force_regenerate: isRegenerate,
      },
      handleStaticPostJobCallbacks(postId, isRegenerate)
    );
  };

  const handleEnhanceImage = async () => {
    if (!editingPost) return;
    // Results are written to this post only; the editor is updated only while
    // it is still the one open (the poll can run for minutes).
    const postId = editingPost.id;
    const slideNumber = currentSlideIndex + 1;
    setEnhancingPostId(postId);
    setEnhanceTimedOutPostId(null);
    showToast('Enhancing image with AI... This may take a moment.', 'info');

    try {
      const isCarousel = editingPost.format?.toLowerCase() === 'carousel' || carouselSlideUrls.length > 0;
      const targetUrl = isCarousel
        ? (carouselSlideUrls[currentSlideIndex] || selectedImageUrl || undefined)
        : (enhancedImageUrl || selectedImageUrl || undefined);

      const payload: { instructions?: string; slide_number?: number; input_image_url?: string } = {
        instructions: regenerateInstructions.trim() || undefined,
        input_image_url: targetUrl,
      };
      if (isCarousel) {
        payload.slide_number = currentSlideIndex + 1;
      }

      // Start background enhancement
      await apiService.enhanceImage(postId, payload);

      // Poll status until DONE or FAILED (max ~5 minutes — an edit can take
      // well over a minute)
      const maxAttempts = 150;
      let attempts = 0;
      let completed = false;

      while (attempts < maxAttempts && !completed) {
        await new Promise((r) => setTimeout(r, 2000));
        attempts++;

        const statusRes = await apiService.getImageStatus(postId);

        if (statusRes?.status === 'DONE') {
          completed = true;
          const stillOpen = isOpenInEditor(postId);
          if (isCarousel && statusRes.carousel_slide_urls?.length) {
            if (stillOpen) {
              setCarouselSlideUrls(statusRes.carousel_slide_urls);
              setSelectedImageUrl(statusRes.carousel_slide_urls[0]);
            }
            setEditingPost((prev) =>
              prev && prev.id === postId
                ? {
                    ...prev,
                    carouselSlideUrls: statusRes.carousel_slide_urls,
                    carousel_slide_urls: statusRes.carousel_slide_urls,
                    imageUrl: statusRes.carousel_slide_urls[0],
                  }
                : prev
            );
            setPosts((prev) =>
              prev.map((p) =>
                p.id === postId
                  ? {
                      ...p,
                      carouselSlideUrls: statusRes.carousel_slide_urls,
                      carousel_slide_urls: statusRes.carousel_slide_urls,
                      imageUrl: statusRes.carousel_slide_urls[0],
                    }
                  : p
              )
            );
          } else if (statusRes.image_url) {
            if (stillOpen) {
              setEnhancedImageUrl(statusRes.image_url);
              setSelectedImageUrl(statusRes.image_url);
            }
            setEditingPost((prev) =>
              prev && prev.id === postId
                ? {
                    ...prev,
                    imageUrl: statusRes.image_url,
                    image_url: statusRes.image_url,
                    imageOptions: statusRes.image_options,
                    image_options: statusRes.image_options,
                    selectedImageIndex: statusRes.selected_image_index,
                    selected_image_index: statusRes.selected_image_index,
                  }
                : prev
            );
            setPosts((prev) =>
              prev.map((p) =>
                p.id === postId
                  ? {
                      ...p,
                      imageUrl: statusRes.image_url,
                      image_url: statusRes.image_url,
                      imageOptions: statusRes.image_options,
                      image_options: statusRes.image_options,
                      selectedImageIndex: statusRes.selected_image_index,
                      selected_image_index: statusRes.selected_image_index,
                    }
                  : p
              )
            );
          }
          // The image job just succeeded, so an older failure no longer applies.
          setEditingPost((prev) => (prev && prev.id === postId ? { ...prev, imageGenStatus: 'DONE', imageGenError: null } : prev));
          if (!isCarousel && stillOpen) await refreshLogoFields(postId);
          showJobToast(
            postId,
            isCarousel ? `Slide ${slideNumber} enhanced successfully!` : 'Image enhanced successfully!'
          );
          if (stillOpen) setRegenerateInstructions('');
          await Promise.all([refreshEntitlements(), invalidateCredits()]);
          break;
        } else if (statusRes?.status === 'FAILED') {
          completed = true;
          throw new Error(statusRes.error_message || 'Image enhancement failed.');
        }
      }

      if (!completed) {
        // Stays on screen (see the Enhance button) — a toast is easy to miss.
        setEnhanceTimedOutPostId(postId);
        showJobToast(postId, 'Enhancement is taking longer than expected. Please check back shortly.', 'info');
      }
    } catch (err: any) {
      console.error('Failed to enhance image:', err);
      showJobToast(postId, `Enhancement failed: ${friendlyError(err)}`, 'error');
    } finally {
      setEnhancingPostId((current) => (current === postId ? null : current));
    }
  };

  const handleSelectVariation = async (selectedIndex: number) => {
    if (!editingPost?.id) return;
    setIsSelectingVariation(true);
    try {
      const res = await apiService.selectImage(editingPost.id, selectedIndex);
      if (res?.image_url) {
        setSelectedImageUrl(res.image_url);
        setEnhancedImageUrl(res.image_url);
        // Carousel per-slide selections also update carousel_slide_urls (the
        // full ordered slide list) — single-image formats never send this.
        if (res.carousel_slide_urls?.length) {
          setCarouselSlideUrls(res.carousel_slide_urls);
        }
        setEditingPost((prev: any) =>
          prev
            ? {
                ...prev,
                imageUrl: res.image_url,
                image_url: res.image_url,
                selectedIndex: selectedIndex,
                selected_image_index: selectedIndex,
                imageOptions: res.image_options,
                image_options: res.image_options,
                ...(res.carousel_slide_urls?.length
                  ? { carouselSlideUrls: res.carousel_slide_urls, carousel_slide_urls: res.carousel_slide_urls }
                  : {}),
              }
            : null
        );
        setPosts((prev: any[]) =>
          prev.map((p) =>
            p.id === editingPost.id
              ? {
                  ...p,
                  imageUrl: res.image_url,
                  image_url: res.image_url,
                  selectedIndex: selectedIndex,
                  selected_image_index: selectedIndex,
                  imageOptions: res.image_options,
                  image_options: res.image_options,
                  ...(res.carousel_slide_urls?.length
                    ? { carouselSlideUrls: res.carousel_slide_urls, carousel_slide_urls: res.carousel_slide_urls }
                    : {}),
                }
              : p
          )
        );
        if (!res.carousel_slide_urls?.length) await refreshLogoFields(editingPost.id);
        showToast('Image variation selected successfully!');
      }
    } catch (err: any) {
      console.error('Failed to select image variation:', err);
      showToast(`Error: ${err?.message || err?.detail || 'Failed to select image variation'}`, 'error');
    } finally {
      setIsSelectingVariation(false);
    }
  };

  const handleGenerateArticle = async () => {
    if (!editingPost?.id) return;
    setIsGeneratingArticle(true);
    try {
      const userId = localStorage.getItem('user_id') || '';
      const response = await apiService.generateArticle({
        post_id: editingPost.id,
        user_id: userId,
        regeneration_input: articleRegenerationInput,
      });
      if (response && response.article_content) {
        setArticleContent(response.article_content);
        setEditingPost(prev => prev ? {
          ...prev,
          creativeCopy: response.article_content,
        } : null);
      } else {
        showToast(`Couldn't generate the article: ${friendlyError(response?.error || 'no article came back. Please try again.')}`, 'error');
      }
    } catch (e: any) {
      console.error('[Article] Generation error:', e);
      showToast(`Couldn't generate the article: ${friendlyError(e?.response?.data?.error || e)}`, 'error');
    } finally {
      setIsGeneratingArticle(false);
    }
  };

  const handleSaveCreativeCopy = async () => {
    if (!editingPost?.id) return;
    setIsSavingCreativeCopy(true);
    try {
      await apiService.updatePostCreativeCopy({
        post_id: editingPost.id,
        creative_copy: editCreativeCopy || '',
      });
      setEditingPost(prev => prev ? {
        ...prev,
        creativeCopy: editCreativeCopy,
        creative_copy: editCreativeCopy,
      } : null);
      showToast('Creative copy saved successfully');
    } catch (e) {
      console.error('[SaveCreativeCopy] Failed:', e);
      showToast(`Failed to save creative copy: ${friendlyError(e)}`, 'error');
    } finally {
      setIsSavingCreativeCopy(false);
    }
  };

  const handleDownloadArticlePost = async (externalPost?: any) => {
    const postToUse = externalPost || editingPost;
    if (!postToUse) return;
    setIsDownloading(true);

    try {
      const JSZip = (await import('jszip')).default;
      const zip = new JSZip();

      const articleText = (externalPost ? null : (articleContent.length > 50 ? articleContent : null))
        || postToUse?.article_text
        || postToUse?.articleText
        || postToUse?.creative_copy
        || postToUse?.creativeCopy
        || '';
      if (articleText) {
        zip.file('article.txt', articleText);
      }

      const captionText = (externalPost ? null : editCaption)
        || postToUse?.full_caption
        || postToUse?.caption
        || '';
      if (captionText) {
        zip.file('caption.txt', captionText);
      }

      const hashtagText = (postToUse.hashtags || []).join('\n');
      if (hashtagText) {
        zip.file('hashtags.txt', hashtagText);
      }

      const ctaText = postToUse.cta
        || postToUse.ctaText
        || postToUse.cta_text
        || '';
      if (ctaText) {
        zip.file('cta.txt', ctaText);
      }

      const imageUrl = (externalPost ? null : selectedImageUrl)
        || postToUse?.image_url
        || postToUse?.imageUrl
        || (postToUse as any)?.creative_asset?.url
        || '';

      if (imageUrl) {
        try {
          let imgArrayBuffer: ArrayBuffer | null = null;
          let ext = '.jpg';

          if (imageUrl.startsWith('<svg')) {
            const sanitized = imageUrl.replace(/&(?!(?:amp|lt|gt|quot|apos|#[0-9]+|#x[0-9a-fA-F]+);)/g, '&amp;');
            const finalSrc = `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(sanitized)))}`;
            const blob = await new Promise<Blob>((resolve, reject) => {
              const img = new Image();
              img.onload = () => {
                const canvas = document.createElement('canvas');
                canvas.width = img.width;
                canvas.height = img.height;
                const ctx = canvas.getContext('2d');
                if (!ctx) return reject(new Error('No canvas context'));
                ctx.fillStyle = '#FFFFFF';
                ctx.fillRect(0, 0, canvas.width, canvas.height);
                ctx.drawImage(img, 0, 0);
                canvas.toBlob((b) => {
                  if (b) resolve(b);
                  else reject(new Error('toBlob failed'));
                }, 'image/jpeg', 0.95);
              };
              img.onerror = () => reject(new Error('Image load failed'));
              img.src = finalSrc;
            });
            imgArrayBuffer = await blob.arrayBuffer();
            ext = '.jpeg';
          } else if (imageUrl.startsWith('data:')) {
            const match = imageUrl.match(/^data:(image\/[a-zA-Z0-9.+-]+);base64,(.*)$/);
            if (match) {
              const mime = match[1];
              const base64Data = match[2];
              const binary = atob(base64Data.replace(/\s/g, ''));
              const bytes = new Uint8Array(binary.length);
              for (let i = 0; i < binary.length; i += 1) {
                bytes[i] = binary.charCodeAt(i);
              }
              imgArrayBuffer = bytes.buffer;
              if (mime.includes('png')) ext = '.png';
              else if (mime.includes('jpeg') || mime.includes('jpg')) ext = '.jpg';
              else if (mime.includes('webp')) ext = '.webp';
              else if (mime.includes('gif')) ext = '.gif';
            }
          } else {
            const imgBlob = await apiService.downloadImage(imageUrl);
            imgArrayBuffer = await imgBlob.arrayBuffer();
            const mime = imgBlob.type || '';
            if (mime.includes('png')) ext = '.png';
            else if (mime.includes('jpeg') || mime.includes('jpg')) ext = '.jpg';
            else if (mime.includes('webp')) ext = '.webp';
            else if (mime.includes('gif')) ext = '.gif';
          }

          if (imgArrayBuffer) {
            zip.file(`cover_image${ext}`, imgArrayBuffer);
          }
        } catch (imgErr) {
          console.warn('[Download] Image fetch/convert failed:', imgErr);
        }
      }

      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const downloadUrl = URL.createObjectURL(zipBlob);
      const link = document.createElement('a');
      const brandSlug = (postToUse.brand_name || 'post')
        .toLowerCase().replace(/\s+/g, '_');
      const dateSlug = new Date().toISOString().slice(0, 10);
      link.href = downloadUrl;
      link.download = `${brandSlug}_article_${dateSlug}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(downloadUrl);
    } catch (e) {
      console.error('[Download] Failed:', e);
      showToast('Download failed. Please try again.', 'error');
    } finally {
      setIsDownloading(false);
    }
  };

  const handleFileUpload = async (file: File, isVideo: boolean) => {
    if (!editingPost) return;
    showToast(isVideo ? 'Uploading video...' : 'Uploading image...', 'info');
    try {
      const assetType = isVideo ? 'VIDEO_MP4' : 'STATIC_IMAGE';
      const ref = await apiService.uploadAsset(editingPost.id, assetType, file);

      setIsManualImageUploaded(true);
      setSelectedImageUrl(ref.url);
      setEnhancedImageUrl('');
      setEditingPost(prev => prev ? {
        ...prev,
        creativeAsset: ref,
        imageUrl: ref.url,
      } : null);
      setPosts(prev => prev.map(p => p.id === editingPost.id ? { ...p, imageUrl: ref.url, creativeAsset: ref } : p));

      showToast(isVideo ? 'Video uploaded successfully!' : 'Image uploaded successfully!');
    } catch (err: any) {
      console.error('Failed to upload file:', err);
      showToast(`Upload failed: ${friendlyError(err)}`, 'error');
    }
  };

  // Photo upload handlers
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => setDragOver(false);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const files = e.dataTransfer.files;
    if (files.length > 0 && files[0].type.startsWith('image/')) {
      handleFileUpload(files[0], false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && file.type.startsWith('image/')) {
      handleFileUpload(file, false);
    }
  };

  const handleGalleryPick = async (url: string) => {
    if (!editingPost) return;
    setSelectedImageUrl(url);
    setEnhancedImageUrl('');
    // Create reference object and update post card
    const ref = {
      asset_id: `asset-${Math.random().toString(36).substr(2, 9)}`,
      asset_type: mapFormatToBackend(editingPost.format) === 'VIDEO' ? 'VIDEO_MP4' : 'STATIC_IMAGE',
      url: url,
      file_name: 'gallery_pick.jpg'
    };
    try {
      await apiService.updatePostCard(editingPost.id, { creative_asset: ref });
      setEditingPost(prev => prev ? { ...prev, creativeAsset: ref, imageUrl: url } : null);
      setPosts(prev => prev.map(p => p.id === editingPost.id ? { ...p, imageUrl: url, creativeAsset: ref } : p));
    } catch (err) {
      console.error('Failed to link gallery asset:', err);
    }
  };

  // Shared post-generation pipeline for video: dispatch the async job, then
  // when the poller reports done/error/timeout apply UI side effects. Also
  // reused by the resume-after-refresh effect below.
  const handleVideoJobCallbacks = () => ({
    onDone: async (postId: string, videoUrl: string) => {
      // try/finally guarantees the loading UI clears no matter what — the
      // video DID finish generating (that's why onDone fired at all), so a
      // failure in a secondary step below (persisting to DB, refreshing
      // credits) must never leave the button/skeleton stuck showing
      // "Generating..." forever over something that has nothing to do with
      // whether the video itself succeeded.
      try {
        if (isOpenInEditor(postId)) {
          setSelectedVideoUrl(videoUrl);
          // Save sends the selected media — without this it sent none (or the
          // previous video) and wiped the new video from the post.
          setSelectedImageUrl(videoUrl);
          setEnhancedImageUrl('');
          setRegenerateInstructions('');
          setFalVideoError(null);
        } else {
          showJobToast(postId, 'Video generated successfully!');
        }
        setEditingPost(prev => prev && prev.id === postId ? { ...prev, imageUrl: videoUrl } : prev);
        setPosts(prev => prev.map(p => p.id === postId ? { ...p, imageUrl: videoUrl } : p));
        try {
          await apiService.updatePostCard(postId, {
            image_url: videoUrl,
            creative_asset: { url: videoUrl, asset_type: 'VIDEO_MP4', file_name: 'generated_video.mp4' },
          });
        } catch (e) {
          console.warn('[Video] Failed to persist video URL to DB:', e);
        }
        await postActionCheck();
        await Promise.all([refreshEntitlements(), invalidateCredits()]);
      } finally {
        localStorage.removeItem(videoGenStartKey(postId));
      }
    },
    onError: async (postId: string, message: string) => {
      try {
        console.error('[Video] Generation failed:', message);
        if (isOpenInEditor(postId)) {
          setFalVideoError(friendlyError(message || 'Video generation failed. Please try again.'));
        }
        // Always toast too: falVideoError is only rendered in the Video panel,
        // which Reel/Short posts don't use.
        showJobToast(postId, `Error: ${friendlyError(message || 'Video generation failed')}`, 'error');
        await Promise.all([refreshEntitlements(), invalidateCredits()]);
      } finally {
        localStorage.removeItem(videoGenStartKey(postId));
      }
    },
    // A timeout is NOT a failure — the background job is still running
    // server-side; the browser only stopped polling. It deliberately doesn't
    // set falVideoError (that renders as a red "failed" state) — the "still
    // processing" banner below (videoGeneration.isTimedOutFor) handles it
    // instead, with a Check again button that resumes polling the SAME job
    // rather than dispatching a new paid attempt. The toast is for Reel/Short
    // posts, whose Media tab doesn't show that banner.
    onTimeout: async (postId: string) => {
      showJobToast(postId, "The video is taking longer than usual. It's still being generated — check back in a few minutes.", 'info', 8000);
    },
  });

  // "Check again" after a poll timeout — resumes polling the existing job
  // (no new dispatch, no new charge) rather than starting a fresh
  // generation. Mirrors the page-refresh resume effect below.
  const handleCheckVideoAgain = () => {
    if (!editingPost?.id) return;
    videoGeneration.resume(editingPost.id, handleVideoJobCallbacks());
  };

  const handleGenerateVideo = async () => {
    if (!editingPost?.id) return;

    // See handleGenerateStaticPost for why there's no client-side balance
    // pre-check here — gating now happens at the locked-button + confirm-popup
    // level, upstream of this function.
    //
    // Generation now runs as a FastAPI BackgroundTask on the backend (hotfix:
    // all SMM generation paths run in the background to avoid gateway
    // timeouts) — the POST returns immediately with status="pending" and
    // this dispatches + polls via useVideoGeneration instead of awaiting a
    // single blocking response.
    setHasAttemptedGenerate(true);
    setFalVideoError(null);
    setVideoGenElapsedSec(0);
    // Read back by the elapsed-timer effect while this post's job is pending.
    localStorage.setItem(videoGenStartKey(editingPost.id), String(Date.now()));
    showToast('Generating video... this usually takes a few minutes. You can work on other modules while this finishes.', 'info');

    const postId = editingPost.id;
    const userId = localStorage.getItem('user_id') || '';
    const aspectRatio =
      editingPost?.platform?.toLowerCase() === 'instagram' ||
        editingPost?.platform?.toLowerCase() === 'facebook'
        ? '9:16'
        : '16:9';
    const isRegenerate = Boolean(editingPost?.imageUrl);

    await videoGeneration.start(
      {
        user_id: userId,
        post_id: postId,
        caption: editCaption || editingPost?.caption || '',
        duration: '15',
        resolution: '480p',
        aspect_ratio: aspectRatio,
        generate_audio: true,
        regeneration_instructions: regenerateInstructions.trim() || undefined,
        force_regenerate: isRegenerate,
      },
      handleVideoJobCallbacks()
    );
  };

  const persistGeneratedMedia = async (postId: string, imageUrl: string) => {
    try {
      await apiService.updatePostCard(postId, {
        image_url: imageUrl,
        creative_asset: { url: imageUrl, asset_type: 'STATIC_IMAGE', file_name: 'media_asset' },
      });
    } catch (e) {
      console.warn('[ContentCalendar] Failed to persist generated asset URL to DB:', e);
    }
  };

  // Applies a finished job's image to its calendar tile, and to the editor
  // only if that post is still the one open.
  const applyGeneratedMedia = (postId: string, imageUrl: string) => {
    if (isOpenInEditor(postId)) {
      setSelectedImageUrl(imageUrl);
      setEnhancedImageUrl('');
      setIsManualImageUploaded(false);
      setRegenerateInstructions('');
    }
    setEditingPost(prev => prev && prev.id === postId ? {
      ...prev,
      imageUrl,
    } : prev);
    setPosts(prev => prev.map(p => p.id === postId ? { ...p, imageUrl } : p));
  };

  // Shared post-generation pipeline for carousel: dispatch the async job,
  // then when the poller reports done/error/timeout apply UI side effects.
  const handleCarouselJobCallbacks = (isRegenerate: boolean) => ({
    onDone: async (
      postId: string,
      imageUrls: string[],
      imageOptions?: { url: string; angle?: string; issues?: string[]; slide_number?: number }[] | null,
      selectedImageIndex?: number | null,
      meta?: { alreadyGenerated?: boolean }
    ) => {
      const firstUrl = imageUrls[0];
      applyGeneratedMedia(postId, firstUrl);
      if (isOpenInEditor(postId)) {
        setCarouselSlideUrls(imageUrls);
        setCurrentSlideIndex(0);
      }

      // Persist carousel slide urls to backend
      try {
        await apiService.updatePostCard(postId, {
          image_url: firstUrl,
          carousel_slide_urls: imageUrls,
          asset_s3_key: JSON.stringify(imageUrls),
          creative_asset: {
            url: firstUrl,
            asset_type: 'CAROUSEL_SLIDE',
            file_name: 'carousel_slides',
          },
        });
      } catch (err) {
        console.warn('Failed to update post card for generated carousel:', err);
      }

      setPosts(prev => prev.map(p => p.id === postId ? {
        ...p,
        imageUrl: firstUrl,
        carousel_slide_urls: imageUrls,
        carouselSlideUrls: imageUrls,
        creativeAsset: { url: firstUrl, asset_type: 'CAROUSEL_SLIDE', file_name: 'carousel_slides' },
        imageOptions: imageOptions,
        image_options: imageOptions,
        selectedImageIndex: selectedImageIndex,
        selected_image_index: selectedImageIndex,
      } : p));

      setEditingPost(prev => prev && prev.id === postId ? {
        ...prev,
        imageUrl: firstUrl,
        carousel_slide_urls: imageUrls,
        carouselSlideUrls: imageUrls,
        creativeAsset: { url: firstUrl, asset_type: 'CAROUSEL_SLIDE', file_name: 'carousel_slides' },
        imageOptions: imageOptions,
        image_options: imageOptions,
        selectedImageIndex: selectedImageIndex,
        selected_image_index: selectedImageIndex,
      } : prev);

      // Picks up raw_image_url/logo_position — not carried by the poll
      // response above, same as the static-post pipeline's onDone.
      await refreshLogoFields(postId);
      showJobToast(
        postId,
        meta?.alreadyGenerated
          ? 'This post already has a carousel. Showing the existing slides.'
          : imageOptions && imageOptions.length > 0
            ? `Carousel generated, but slide ${imageOptions[0]?.slide_number ?? ''} didn't pass automatic quality checks — review the flagged options below.`
            : isRegenerate
              ? 'Carousel regenerated successfully!'
              : 'Carousel generated successfully!'
      );
      await postActionCheck();
      await Promise.all([refreshEntitlements(), invalidateCredits()]);
    },
    onError: async (postId: string, message: string) => {
      showJobToast(postId, `Error: ${friendlyError(message || 'Carousel generation failed')}`, 'error');
      // See handleGenerateStaticPost's catch block for why this is needed —
      // the backend refunds on failure, but the UI's credit balance only
      // reflects that once we re-fetch it.
      await Promise.all([refreshEntitlements(), invalidateCredits()]);
    },
    onTimeout: async (postId: string) => {
      showJobToast(postId, 'Generation timed out, try again. The job may still complete server-side.', 'info', 8000);
      // A timeout doesn't necessarily mean failure (the job may still
      // succeed or fail-and-refund server-side after this), but refreshing
      // here means the balance is never more stale than it needs to be.
      await Promise.all([refreshEntitlements(), invalidateCredits()]);
    },
  });

  const handleGenerateCarousel = async (overrideForceRegenerate?: boolean) => {
    if (!editingPost?.id) return;
    const userId = localStorage.getItem('user_id') || 'default_user_id';

    // See handleGenerateStaticPost for why there's no client-side balance
    // pre-check here — gating now happens at the locked-button + confirm-popup
    // level, upstream of this function. The POST now returns immediately with
    // status="pending"; the job runs in a background task on the backend and
    // this can no longer block on / await the final slides.
    showToast('Generating carousel... this usually takes a couple of minutes. You can work on other modules while this finishes.', 'info');

    const cleanHashtags = editHashtags
      .split(/\s+/)
      .filter(Boolean)
      .map(tag => tag.startsWith('#') ? tag.slice(1) : tag);

    const isRegenerate = overrideForceRegenerate === true || Boolean(editingPost.imageUrl || selectedImageUrl || carouselSlideUrls.length > 0);
    const request = {
      user_id: userId,
      post_id: editingPost.id,
      caption: editCaption || editingPost.caption,
      creative_copy: editCreativeCopy || editingPost.creative_copy || editingPost.creativeCopy || undefined,
      hashtags: cleanHashtags.join(' '),
      aspect_ratio:
        editingPost.platform?.toLowerCase() === 'linkedin' ||
          editingPost.platform?.toLowerCase() === 'youtube'
          ? '16:9'
          : '4:5',
      // Must match what the lock/confirm UI displayed — the carousel
      // controller resolves this to the actual entitlement module code
      // (CAROUSEL_2/4/6_SLIDES) and charges accordingly. Omitting it would
      // silently fall back to the 4-slide default regardless of what was
      // shown as free/locked.
      total_slides: editingPost.carouselSlides ?? undefined,
      regeneration_instructions: regenerateInstructions.trim() || undefined,
      force_regenerate: isRegenerate,
    };

    await carouselGeneration.start(request, handleCarouselJobCallbacks(isRegenerate));
  };

  const handleRetryCarousel = async () => {
    if (!editingPost?.id) return;
    await handleGenerateCarousel(true); // explicit user retry -> force a fresh job
  };

  // Shared post-generation pipeline for infographic: dispatch the async job,
  // then when the poller reports done/error/timeout apply UI side effects.
  const handleInfographicJobCallbacks = (isRegenerate: boolean) => ({
    onDone: async (
      postId: string,
      imageUrl: string,
      imageOptions?: { url: string; angle?: string; issues?: string[] }[] | null,
      selectedImageIndex?: number | null
    ) => {
      applyGeneratedMedia(postId, imageUrl);
      await persistGeneratedMedia(postId, imageUrl);
      // Infographics are generated without the logo: pick up the pre-logo
      // image and logo corner so the logo placement panel shows.
      await refreshLogoFields(postId);

      setPosts(prev => prev.map(p => p.id === postId ? {
        ...p,
        imageOptions: imageOptions,
        image_options: imageOptions,
        selectedImageIndex: selectedImageIndex,
        selected_image_index: selectedImageIndex,
      } : p));
      setEditingPost(prev => prev && prev.id === postId ? {
        ...prev,
        imageOptions: imageOptions,
        image_options: imageOptions,
        selectedImageIndex: selectedImageIndex,
        selected_image_index: selectedImageIndex,
      } : prev);

      showJobToast(
        postId,
        imageOptions && imageOptions.length > 0
          ? "Infographic generated, but it didn't pass automatic quality checks — review the flagged options below."
          : isRegenerate ? 'Infographic regenerated successfully!' : 'Infographic generated successfully!'
      );
      await postActionCheck();
      await Promise.all([refreshEntitlements(), invalidateCredits()]);
    },
    onError: async (postId: string, message: string) => {
      showJobToast(postId, `Error: ${friendlyError(message || 'Infographic generation failed')}`, 'error');
      // See handleGenerateStaticPost's catch block for why this is needed —
      // the backend refunds on failure, but the UI's credit balance only
      // reflects that once we re-fetch it.
      await Promise.all([refreshEntitlements(), invalidateCredits()]);
    },
    onTimeout: async (postId: string) => {
      showJobToast(postId, 'Generation is taking longer than expected. It may still finish — check this post again in a few minutes.', 'info', 8000);
      // A timeout doesn't necessarily mean failure (the job may still
      // succeed or fail-and-refund server-side after this), but refreshing
      // here means the balance is never more stale than it needs to be.
      await Promise.all([refreshEntitlements(), invalidateCredits()]);
    },
  });

  const handleGenerateInfographic = async (overrideForceRegenerate?: boolean) => {
    if (!editingPost?.id) return;
    const userId = localStorage.getItem('user_id') || 'default_user_id';

    // See handleGenerateStaticPost for why there's no client-side balance
    // pre-check here — gating now happens at the locked-button + confirm-popup
    // level, upstream of this function. The POST now returns immediately with
    // status="pending"; the job runs in a background task on the backend and
    // this can no longer block on / await the final image.
    showToast('Generating infographic... this usually takes a couple of minutes. You can work on other modules while this finishes.', 'info');

    const cleanHashtags = editHashtags
      .split(/\s+/)
      .filter(Boolean)
      .map(tag => tag.startsWith('#') ? tag.slice(1) : tag);

    const isRegenerate = overrideForceRegenerate === true || Boolean(editingPost.imageUrl || selectedImageUrl);
    const request = {
      user_id: userId,
      post_id: editingPost.id,
      caption: editCaption || editingPost.caption,
      creative_copy: editCreativeCopy || editingPost.creative_copy || editingPost.creativeCopy || undefined,
      hashtags: cleanHashtags.join(' '),
      aspect_ratio:
        editingPost.platform?.toLowerCase() === 'linkedin' ||
          editingPost.platform?.toLowerCase() === 'youtube'
          ? '16:9'
          : '4:5',
      regeneration_instructions: regenerateInstructions.trim() || undefined,
      force_regenerate: isRegenerate,
    };

    await infographicGeneration.start(request, handleInfographicJobCallbacks(isRegenerate));
  };

  const handleRetryInfographic = async () => {
    if (!editingPost?.id) return;
    await handleGenerateInfographic(true); // explicit user retry -> force a fresh job
  };

  // Resume the "Generating..." UI after a page refresh mid-generation.
  // Without this, a job that's still running (or already finished) on the
  // backend has no client-side trace once the page reloads — the relevant
  // hook resets to idle and the modal falls back to showing the Generate
  // button as if nothing had ever started. imageGenStatus/staticCampaignId
  // (persisted on ContentPost, returned by the workspace list endpoint) are
  // the only signal that a job is still PENDING; keyed on editingPost.id so
  // this only fires once per post opened, not on every in-place update to
  // editingPost made by the generation callbacks themselves.
  useEffect(() => {
    if (!editingPost || editingPost.imageGenStatus !== 'PENDING') return;
    // Started earlier in this session and still being polled in the
    // background — nothing to resume.
    if (isPostGenerating(editingPost.id)) return;
    const format = (editingPost.format || '').toLowerCase();
    // A job dispatched against a post that already had an image is a
    // regenerate — same heuristic handleGenerateCarousel/Infographic/
    // StaticPost use when dispatching, just re-derived here since we never
    // saw the original dispatch (it happened before this page load).
    const isRegenerate = Boolean(editingPost.imageUrl);

    if (format === 'carousel') {
      carouselGeneration.resume(editingPost.id, handleCarouselJobCallbacks(isRegenerate));
    } else if (format === 'infographic') {
      infographicGeneration.resume(editingPost.id, handleInfographicJobCallbacks(isRegenerate));
    } else if (format === 'video' || format === 'reel' || format === 'short') {
      // The elapsed timer picks up the start time persisted at dispatch.
      videoGeneration.resume(editingPost.id, handleVideoJobCallbacks());
    } else if (editingPost.staticCampaignId) {
      staticPostGeneration.resume(
        editingPost.id,
        editingPost.staticCampaignId,
        handleStaticPostJobCallbacks(editingPost.id, isRegenerate),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingPost?.id]);

  const handleGenerate = async (overrideForceRegenerate?: boolean) => {
    setHasAttemptedGenerate(true);
    const format = (editingPost?.format || '').toLowerCase();
    const shouldForceRegenerate = overrideForceRegenerate ?? Boolean(selectedImageUrl || editingPost?.imageUrl || carouselSlideUrls.length > 0);
    if (format === 'carousel') {
      await handleGenerateCarousel(shouldForceRegenerate);
    } else if (format === 'infographic') {
      await handleGenerateInfographic(shouldForceRegenerate);
    } else if (format === 'video' || format === 'reel' || format === 'short') {
      await handleGenerateVideo();
    } else {
      // StaticImage, default
      await handleGenerateStaticPost(shouldForceRegenerate);
    }
  };

  // The single Generate button's module code + lock state for the post
  // currently open in the popup — used by the Media tab's button rendering
  // below and by the confirm-before-charge popup.
  const currentGenerateModuleCode = resolveModuleCode(editingPost?.format, editingPost?.carouselSlides);

  // ── Logo placement (logo-free single-image posts, and independent
  // per-slide corners for a carousel) ──
  // Every format that shares the single-image generation pipeline (not just
  // "Static image") is generated logo-free under the redesign and gets
  // raw_image_url populated the same way — Article/MiniBlog included.
  const isSingleImageFormat = ['static image', 'static', 'article', 'miniblog', 'infographic'].includes((editingPost?.format || '').toLowerCase());
  const isCarouselFormat = (editingPost?.format || '').toLowerCase() === 'carousel';
  // A carousel's logo is independent per slide, so eligibility, the fetched
  // corners, and everything the picker shows are all scoped to whichever
  // slide is CURRENTLY being viewed (currentSlideIndex) — switching slides
  // re-fetches for that slide's own raw image.
  const currentSlideNumber = currentSlideIndex + 1;
  const currentSlideRawUrl = isCarouselFormat
    ? (editingPost?.rawCarouselSlideUrls?.[currentSlideIndex] ?? null)
    : (editingPost?.rawImageUrl ?? null);
  const currentSlideLogoPosition = isCarouselFormat
    ? (editingPost?.carouselLogoPositions?.[currentSlideIndex] ?? null)
    : (editingPost?.logoPosition ?? null);
  const logoPlacementEligible = Boolean(
    editingPost
    && (isSingleImageFormat || isCarouselFormat)
    && currentSlideRawUrl
    && ['DONE', 'DONE_WITH_FAULTS'].includes(editingPost.imageGenStatus || '')
    && !editingPost.userEdited
    && editingPost.governanceStatus !== 'DONE'
  );
  const [pendingLogoCorner, setPendingLogoCorner] = useState<LogoPosition | null>(null);
  // The chosen logo style (which logo, background, rounding); null = untouched.
  const [pendingLogoStyle, setPendingLogoStyle] = useState<LogoStyle | null>(null);

  // Owned here (not inside the Media tab) so the publish guard works even
  // when the Media tab was never opened.
  const logoCorners = useLogoCorners(
    editingPost?.id, currentSlideRawUrl, logoPlacementEligible, isCarouselFormat,
    isCarouselFormat ? currentSlideNumber : undefined,
  );
  const currentLogoStyle: LogoStyle = { ...DEFAULT_LOGO_STYLE, ...(logoCorners.data?.logo_style ?? {}) };
  const pendingStyle = pendingLogoStyle ?? currentLogoStyle;
  // A corner (or logo style) picked but not yet applied — save/publish apply it.
  const hasUnappliedLogoChoice = Boolean(
    logoPlacementEligible && pendingLogoCorner && (
      pendingLogoCorner !== currentSlideLogoPosition
      || (pendingLogoCorner !== 'none' && !sameLogoStyle(pendingStyle, currentLogoStyle))
    )
  );

  // A "pending, not yet applied" corner selection is scoped to whichever
  // slide it was picked on — switching slides must not carry it over and
  // silently apply it to a different slide on save/publish.
  useEffect(() => {
    setPendingLogoCorner(null);
    setPendingLogoStyle(null);
  }, [currentSlideIndex]);

  // regenerate / select / enhance responses don't carry raw_image_url or
  // logo_position — re-read just this post (no calendar refetch).
  const refreshLogoFields = async (postId: string) => {
    try {
      const card = await apiService.getPostCard(postId);
      setEditingPost(prev => prev && prev.id === postId ? {
        ...prev,
        rawImageUrl: card.raw_image_url ?? null,
        logoPosition: card.logo_position ?? null,
        rawCarouselSlideUrls: card.raw_carousel_slide_urls ?? null,
        carouselLogoPositions: card.carousel_logo_positions ?? null,
        imageGenStatus: card.image_gen_status || prev.imageGenStatus,
      } : prev);
    } catch (e) {
      console.warn('[LogoPlacement] Failed to refresh logo fields:', e);
    }
  };

  const handleLogoApplied = (view: ApplyLogoResponse) => {
    // Carousel's preview reads carouselSlideUrls[currentSlideIndex] ahead of
    // selectedImageUrl (see the media-preview src below), so the affected
    // slide's composited image must land there, not in selectedImageUrl.
    // Each slide's corner is independent, so the position read back is that
    // specific slide's — not the single-image scalar logo_position field.
    if (isCarouselFormat && view.carousel_slide_urls?.length) {
      const updatedSlides = view.carousel_slide_urls;
      const updatedRaw = view.raw_carousel_slide_urls ?? editingPost?.rawCarouselSlideUrls ?? null;
      const updatedPositions = view.carousel_logo_positions ?? editingPost?.carouselLogoPositions ?? null;
      const position = (updatedPositions?.[currentSlideIndex] ?? null) as LogoPosition | null;
      setCarouselSlideUrls(updatedSlides);
      setEditingPost(prev => prev && prev.id === view.post_id ? {
        ...prev,
        carouselSlideUrls: updatedSlides,
        carousel_slide_urls: updatedSlides,
        rawCarouselSlideUrls: updatedRaw,
        carouselLogoPositions: updatedPositions,
      } : prev);
      setPosts(prev => prev.map(p => p.id === view.post_id ? {
        ...p,
        carouselSlideUrls: updatedSlides,
        carousel_slide_urls: updatedSlides,
        rawCarouselSlideUrls: updatedRaw,
        carouselLogoPositions: updatedPositions,
      } : p));
      setPendingLogoCorner(position);
    } else {
      const position = (view.logo_position ?? null) as LogoPosition | null;
      const url = view.image_url || '';
      setSelectedImageUrl(url);
      setEnhancedImageUrl('');
      setEditingPost(prev => prev && prev.id === view.post_id ? {
        ...prev,
        imageUrl: url,
        rawImageUrl: view.raw_image_url ?? prev.rawImageUrl,
        logoPosition: position,
      } : prev);
      setPosts(prev => prev.map(p => p.id === view.post_id ? {
        ...p,
        imageUrl: url,
        rawImageUrl: view.raw_image_url ?? p.rawImageUrl,
        logoPosition: position,
      } : p));
      setPendingLogoCorner(position);
    }
    if (logoCorners.data) {
      logoCorners.setData({ ...logoCorners.data, logo_position: pendingLogoCorner, logo_style: pendingStyle });
    }
    showToast('Logo applied successfully!');
  };

  const handlePublishClick = async () => {
    // Only the CURRENTLY VIEWED slide's pending corner is auto-applied here
    // — a carousel's other slides each have their own independent
    // logoPosition already persisted (or still logo-free), unaffected by
    // this guard, which only exists to catch a selection made but not
    // explicitly applied on the slide the user was just looking at.
    if (hasUnappliedLogoChoice && pendingLogoCorner) {
      try {
        const view = isCarouselFormat
          ? await apiService.applyCarouselLogo(editingPost!.id, pendingLogoCorner, currentSlideNumber, pendingStyle)
          : await apiService.applyLogo(editingPost!.id, pendingLogoCorner, pendingStyle);
        if (isCarouselFormat && view.carousel_slide_urls?.length) {
          setCarouselSlideUrls(view.carousel_slide_urls);
        } else if (view.image_url) {
          setSelectedImageUrl(view.image_url);
          setEnhancedImageUrl('');
        }
      } catch (e) {
        // Don't publish without the logo the user picked.
        console.warn('Failed to auto-apply logo before publish:', e);
        showToast(`Couldn't apply the logo: ${friendlyError(e)}`, 'error');
        return;
      }
    }
    if (logoPlacementEligible && logoCorners.data?.has_logo && !currentSlideLogoPosition && pendingLogoCorner === 'none') {
      setShowNoLogoPublishConfirm(true);
      return;
    }
    handlePublishPost();
  };
  const isCurrentGenerateLocked = isGenerationLocked(currentGenerateModuleCode);
  const currentGenerateCost = getLockedCreditCost(currentGenerateModuleCode);
  // Only the open post's own job disables its Generate buttons.
  const isGenerateInFlight = Boolean(openPostId && isPostGenerating(openPostId));
  // The open post has a generation job from this session (running, done,
  // failed or timed out) — the post's saved image_gen_status is then stale.
  const hasGenerationJobThisSession = Boolean(openPostId) && (
    staticPostGeneration.stateFor(openPostId).status !== 'idle' ||
    carouselGeneration.stateFor(openPostId).status !== 'idle' ||
    infographicGeneration.stateFor(openPostId).status !== 'idle' ||
    videoGeneration.stateFor(openPostId).status !== 'idle'
  );

  // Ticks videoGenElapsedSec once a second while the open post's video job is
  // in flight, driving the elapsed timer + estimated-progress bar in the
  // loading card below. Recomputes from the wall-clock start time persisted
  // at dispatch (not a running counter) so it stays correct across
  // re-renders, tab-backgrounding and switching posts. When none exists
  // (e.g. dispatched from another browser) the timer starts now.
  useEffect(() => {
    if (!isGeneratingFALVideo || !openPostId) return;
    const stored = Number(localStorage.getItem(videoGenStartKey(openPostId)));
    const startedAt = stored > 0 ? stored : Date.now();
    if (!(stored > 0)) localStorage.setItem(videoGenStartKey(openPostId), String(startedAt));
    const tick = () => setVideoGenElapsedSec(Math.floor((Date.now() - startedAt) / 1000));
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [isGeneratingFALVideo, openPostId]);

  // Whether this tile has any generated/attached asset yet, gating Save
  // Changes. `editingPost.imageUrl` is the one field every generation path
  // (static/carousel/infographic/video, gallery pick, file upload) reliably
  // updates — `selectedImageUrl`/`generatedVideoUrl` alone previously missed
  // the FAL video-generation path (handleGenerateVideo only sets
  // selectedVideoUrl + editingPost.imageUrl, never generatedVideoUrl), which
  // left Save Changes permanently disabled for Video-format tiles.
  const isEditingArticle = (editingPost?.format || '')?.toLowerCase() === 'article' || (editingPost?.format || '')?.toLowerCase() === 'miniblog';
  const hasGeneratedMedia = Boolean(
    isEditingArticle
      ? (articleContent || editingPost?.creativeCopy || editingPost?.caption || editCaption || editCreativeCopy)
      : (selectedImageUrl || generatedVideoUrl || selectedVideoUrl || editingPost?.imageUrl)
  );

  // Locked buttons don't call handleGenerate directly — they open the
  // confirm-before-charge popup first (Section 0: this is the actual
  // charge-trigger moment, unlike the Website Builder card lock which just
  // gates entry). Unlocked buttons keep calling handleGenerate as before.
  const [pendingForceRegenerate, setPendingForceRegenerate] = useState(false);

  const handleGenerateButtonClick = (overrideForceRegenerate?: boolean | React.MouseEvent) => {
    const isForceBool = typeof overrideForceRegenerate === 'boolean' ? overrideForceRegenerate : undefined;
    const shouldForce = isForceBool ?? Boolean(selectedImageUrl || editingPost?.imageUrl || carouselSlideUrls.length > 0);
    if (isCurrentGenerateLocked) {
      setPendingForceRegenerate(shouldForce);
      setIsGenerateConfirmOpen(true);
      return;
    }
    handleGenerate(shouldForce);
  };

  const handleConfirmGenerate = async () => {
    setIsGenerateConfirmOpen(false);
    await handleGenerate(pendingForceRegenerate);
    setPendingForceRegenerate(false);
  };

  const handleVideoFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && file.type.startsWith('video/')) {
      const userId = localStorage.getItem('user_id') || 'default_user_id';
      if (!editingPost) return;

      showToast('Uploading video...', 'info');
      try {
        const response = await apiService.uploadManualVideo(
          userId,
          editingPost.id,
          file,
          editingPost.caption,
          editingPost.creativeCopy,
          editingPost.hashtags,
          editingPost.date,
          true,
          false
        );
        showToast('Video uploaded successfully!');
        if (response && response.data?.video_url) {
          setGeneratedVideoUrl(response.data.video_url);
        }
      } catch (err: any) {
        console.error('Failed to upload video:', err);
        showToast(`Upload failed: ${friendlyError(err)}`, 'error');
      }
    }
  };

  const handleConfirmDelete = async () => {
    if (!editingPost?.id) return;
    setIsDeletingPost(true);
    try {
      const res = await apiService.deletePost(editingPost.id);
      const deletedId = editingPost.id;
      const replacementPostCard = res?.replacement_post;

      if (replacementPostCard) {
        const platformName = mapPlatformFromBackend(replacementPostCard.platform);
        const formatName = mapFormatFromBackend(replacementPostCard.format);
        const statusName = mapStatusFromBackend(replacementPostCard.governance_status);

        let initialCarouselSlides: string[] = [];
        if (replacementPostCard.carousel_slide_urls && Array.isArray(replacementPostCard.carousel_slide_urls) && replacementPostCard.carousel_slide_urls.length > 0) {
          initialCarouselSlides = replacementPostCard.carousel_slide_urls;
        }

        const newReplacementPost: Post = {
          id: replacementPostCard.post_id || replacementPostCard.id,
          date: replacementPostCard.date || editingPost.date,
          dayName: getDayName(replacementPostCard.date || editingPost.date),
          platform: platformName,
          platformIcon: getPlatformIcon(platformName),
          themeType: replacementPostCard.theme || editingPost.themeType || 'Brand story',
          format: formatName,
          formatIcon: '',
          status: statusName,
          caption: replacementPostCard.full_caption || replacementPostCard.caption_preview || '',
          creativeCopy: replacementPostCard.creative_copy || '',
          script: replacementPostCard.script || '',
          hashtags: replacementPostCard.hashtags || [],
          time: formatPostTime(replacementPostCard.scheduled_time, getSuggestedResetTime(platformName)),
          scheduledTime: replacementPostCard.scheduled_time || null,
          imageUrl: replacementPostCard.image_url || replacementPostCard.creative_asset?.url,
          carousel_slide_urls: replacementPostCard.carousel_slide_urls,
          carouselSlideUrls: replacementPostCard.carousel_slide_urls,
          carouselSlides: initialCarouselSlides.length > 0 ? initialCarouselSlides.length : (replacementPostCard.carousel_slides || null),
          creativeAsset: replacementPostCard.creative_asset,
          cta: replacementPostCard.cta || '',
          captionLength: replacementPostCard.caption_length || '',
          structureFormat: replacementPostCard.structure_format || '',
          engagementStyle: replacementPostCard.engagement_style || '',
          visualRequirement: replacementPostCard.visual_requirement || '',
          rewriteCount: 0,
          userEdited: Boolean(replacementPostCard.user_edited),
          governanceStatus: replacementPostCard.governance_status || 'NOT_STARTED',
          allowed_replacement_formats: replacementPostCard.allowed_replacement_formats,
          isManual: Boolean(replacementPostCard.is_manual),
        };

        setPosts((prev) => prev.map((p) => (p.id === deletedId ? newReplacementPost : p)));
        showToast('AI post deleted and new replacement post generated!');
      } else {
        setPosts((prev) => prev.filter((p) => p.id !== deletedId));
        showToast('Post deleted successfully');
      }
      // A festival labelled only because of this post loses its label.
      if (/observance/i.test(editingPost.themeType || '')) setDayEventsVersion((v) => v + 1);

      setShowDeleteConfirm(false);
      setEditPostId(null);
      setEditingPost(null);
    } catch (err: any) {
      console.error('Failed to delete post:', err);
      showToast(`Delete failed: ${friendlyError(err)}`, 'error');
    } finally {
      setIsDeletingPost(false);
    }
  };

  const handleManualPhotoUpload = async (fileOrFiles: File | FileList | File[]) => {
    setPhotoUploadError(null);
    if (!editingPost?.id || editingPost.userEdited) return;

    const files = Array.isArray(fileOrFiles)
      ? fileOrFiles
      : fileOrFiles instanceof FileList
      ? Array.from(fileOrFiles)
      : [fileOrFiles];

    if (files.length === 0) return;

    const isCarousel = editingPost.format?.toLowerCase() === 'carousel';

    // 1. Format validation (JPG, PNG, WEBP)
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    const validExts = ['.jpg', '.jpeg', '.png', '.webp'];
    const maxSizeBytes = 5 * 1024 * 1024;

    // photoUploadError is only rendered when the post has no media yet (Add
    // Slide uploads happen with media present), so toast it as well.
    const rejectUpload = (message: string) => {
      setPhotoUploadError(message);
      showToast(message, 'error');
    };

    for (const file of files) {
      const hasValidType = validTypes.includes(file.type.toLowerCase());
      const hasValidExt = validExts.some((ext) => file.name.toLowerCase().endsWith(ext));

      if (!hasValidType && !hasValidExt) {
        rejectUpload('Unsupported file format. Please upload JPG, PNG, or WEBP images.');
        return;
      }

      if (file.size > maxSizeBytes) {
        rejectUpload(`File "${file.name}" exceeds 5MB limit. Please upload a smaller image.`);
        return;
      }
    }

    if (isCarousel && carouselSlideUrls.length + files.length > 4) {
      rejectUpload(`Carousel posts can have at most 4 slides. Currently has ${carouselSlideUrls.length}.`);
      return;
    }

    setIsUploadingPhoto(true);
    showToast(files.length > 1 ? `Uploading ${files.length} photos...` : 'Uploading photo...', 'info');
    try {
      const postId = editingPost.id;
      const assetType = isCarousel ? 'CAROUSEL' : 'STATIC_IMAGE';

      if (isCarousel) {
        let currentSlides = carouselSlideUrls.length > 0
          ? [...carouselSlideUrls]
          : (selectedImageUrl ? [selectedImageUrl] : []);

        for (const file of files) {
          if (currentSlides.length >= 4) break;
          const response = await apiService.uploadAsset(postId, assetType, file);
          const newImageUrl = response?.url || response?.data?.url || response?.asset?.url || '';
          if (newImageUrl) {
            currentSlides.push(newImageUrl);
          }
        }

        setIsManualImageUploaded(true);
        setCarouselSlideUrls(currentSlides);
        setCurrentSlideIndex(currentSlides.length - 1);
        setSelectedImageUrl(currentSlides[0]);
        setEditingPost((prev) => (prev ? { ...prev, imageUrl: currentSlides[0], carousel_slide_urls: currentSlides, carouselSlideUrls: currentSlides, creativeAsset: { url: currentSlides[0] } } : null));
        setPosts((prev) =>
          prev.map((p) =>
            p.id === postId
              ? {
                  ...p,
                  imageUrl: currentSlides[0],
                  carousel_slide_urls: currentSlides,
                  carouselSlideUrls: currentSlides,
                  creativeAsset: { url: currentSlides[0], asset_type: 'CAROUSEL_SLIDE', file_name: 'carousel_slides' },
                  userEdited: false,
                }
              : p
          )
        );

        // Persist immediately to backend
        await apiService.updatePostCard(postId, {
          image_url: currentSlides[0],
          creative_asset: { url: currentSlides[0], asset_type: 'CAROUSEL_SLIDE', file_name: 'carousel_slides' },
          carousel_slide_urls: currentSlides,
          asset_s3_key: JSON.stringify(currentSlides),
        });
        showToast('Carousel slide(s) uploaded successfully!');
      } else {
        const file = files[0];
        const response = await apiService.uploadAsset(postId, assetType, file);
        const newImageUrl = response?.url || response?.data?.url || response?.asset?.url || '';

        if (newImageUrl) {
          setIsManualImageUploaded(true);
          setSelectedImageUrl(newImageUrl);
          setEnhancedImageUrl('');
          setCarouselSlideUrls([]);
          setEditingPost((prev) => (prev ? { ...prev, imageUrl: newImageUrl, creativeAsset: { url: newImageUrl } } : null));
          setPosts((prev) =>
            prev.map((p) =>
              p.id === postId
                ? {
                    ...p,
                    imageUrl: newImageUrl,
                    creativeAsset: { url: newImageUrl, asset_type: 'STATIC_IMAGE', file_name: file.name || 'media_asset' },
                    carousel_slide_urls: [],
                    carouselSlideUrls: [],
                    userEdited: false,
                  }
                : p
            )
          );

          // Persist immediately to backend
          await apiService.updatePostCard(postId, {
            image_url: newImageUrl,
            creative_asset: { url: newImageUrl, asset_type: 'STATIC_IMAGE', file_name: file.name || 'media_asset' },
            carousel_slide_urls: [],
            asset_s3_key: '',
          });
          showToast('Photo uploaded and saved!');
        } else {
          showToast('Photo uploaded successfully');
        }
      }
    } catch (err: any) {
      console.error('Failed to upload photo:', err);
      setPhotoUploadError(friendlyError(err));
      showToast(`Upload failed: ${friendlyError(err)}`, 'error');
    } finally {
      setIsUploadingPhoto(false);
    }
  };

  const handleDeleteImage = async () => {
    if (!editingPost?.id || editingPost.userEdited) return;
    const postId = editingPost.id;
    const isCarousel = editingPost.format?.toLowerCase() === 'carousel';
    // The image is removed from the screen before the server call; put it
    // back if that call fails.
    const previous = {
      slides: carouselSlideUrls,
      slideIndex: currentSlideIndex,
      isManual: isManualImageUploaded,
      selected: selectedImageUrl,
      enhanced: enhancedImageUrl,
      editingPost,
      post: posts.find((p) => p.id === postId),
    };

    try {
      if (isCarousel && carouselSlideUrls.length > 0) {
        const remainingSlides = carouselSlideUrls.filter((_, idx) => idx !== currentSlideIndex);
        const newSlideIdx = Math.max(0, Math.min(currentSlideIndex, remainingSlides.length - 1));
        setCarouselSlideUrls(remainingSlides);
        setCurrentSlideIndex(newSlideIdx);

        if (remainingSlides.length === 0) {
          setIsManualImageUploaded(false);
          setSelectedImageUrl('');
          setEnhancedImageUrl('');
          setEditingPost((prev) => (prev ? { ...prev, imageUrl: undefined, creativeAsset: undefined, carousel_slide_urls: [], carouselSlideUrls: [] } : null));
          setPosts((prev) => prev.map((p) => p.id === postId ? { ...p, imageUrl: undefined, creativeAsset: undefined, carousel_slide_urls: [], carouselSlideUrls: [] } : p));

          await apiService.updatePostCard(postId, {
            image_url: null,
            creative_asset: null,
            carousel_slide_urls: [],
            asset_s3_key: '',
          });
          showToast('Image removed');
        } else {
          const hasRemainingManual = remainingSlides.some((url) => Boolean(url && url.includes('smm-uploads')));
          setIsManualImageUploaded(hasRemainingManual);
          setSelectedImageUrl(remainingSlides[0]);
          setEditingPost((prev) => (prev ? { ...prev, imageUrl: remainingSlides[0], carousel_slide_urls: remainingSlides, carouselSlideUrls: remainingSlides } : null));
          setPosts((prev) => prev.map((p) => p.id === postId ? { ...p, imageUrl: remainingSlides[0], creativeAsset: { url: remainingSlides[0] }, carousel_slide_urls: remainingSlides, carouselSlideUrls: remainingSlides } : p));

          await apiService.updatePostCard(postId, {
            image_url: remainingSlides[0],
            creative_asset: { url: remainingSlides[0], asset_type: 'CAROUSEL_SLIDE', file_name: 'carousel_slides' },
            carousel_slide_urls: remainingSlides,
            asset_s3_key: JSON.stringify(remainingSlides),
          });
          showToast('Slide removed');
        }
      } else {
        setIsManualImageUploaded(false);
        setSelectedImageUrl('');
        setEnhancedImageUrl('');
        setCarouselSlideUrls([]);
        setEditingPost((prev) => (prev ? { ...prev, imageUrl: undefined, creativeAsset: undefined, carousel_slide_urls: [], carouselSlideUrls: [] } : null));
        setPosts((prev) => prev.map((p) => p.id === postId ? { ...p, imageUrl: undefined, creativeAsset: undefined, carousel_slide_urls: [], carouselSlideUrls: [] } : p));

        await apiService.updatePostCard(postId, {
          image_url: null,
          creative_asset: null,
          carousel_slide_urls: [],
          asset_s3_key: '',
        });
        showToast('Image removed');
      }
    } catch (err: any) {
      console.error('Failed to remove image:', err);
      setCarouselSlideUrls(previous.slides);
      setCurrentSlideIndex(previous.slideIndex);
      setIsManualImageUploaded(previous.isManual);
      setSelectedImageUrl(previous.selected);
      setEnhancedImageUrl(previous.enhanced);
      setEditingPost((prev) => (prev && prev.id === postId ? previous.editingPost : prev));
      const previousPost = previous.post;
      if (previousPost) {
        setPosts((prev) => prev.map((p) => (p.id === postId ? previousPost : p)));
      }
      showToast(`Couldn't remove the image: ${friendlyError(err)}`, 'error');
    }
  };

  const handleDragStart = (e: React.DragEvent, postId: string) => {
    const post = posts.find(p => p.id === postId);
    if (post?.userEdited) {
      e.preventDefault();
      return;
    }
    setDraggingPostId(postId);
    e.dataTransfer.setData('text/plain', postId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragEnd = () => {
    setDraggingPostId(null);
    setDragOverDay(null);
  };

  const handleDayDragOver = (e: React.DragEvent, dateIso: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverDay(dateIso);
  };

  const handleDayDragLeave = () => {
    setDragOverDay(null);
  };

  const handleDayDrop = async (e: React.DragEvent, dateIso: string) => {
    e.preventDefault();
    const postId = e.dataTransfer.getData('text/plain');
    if (!postId) return;

    const newDate = dateIso;
    const newDayName = getDayName(newDate);
    const userId = localStorage.getItem('user_id') || 'default_user_id';

    try {
      await apiService.reschedulePost(userId, postId, newDate);

      setPosts((prev) =>
        prev.map((p) =>
          p.id === postId
            ? { ...p, date: newDate, dayName: newDayName }
            : p
        )
      );
      showToast(`Post moved to ${formatDayMonthLabel(newDate)}`);
    } catch (err: any) {
      console.error('Failed to move post:', err);
      showToast(`Reschedule failed: ${err?.message || 'Constraints violated'}`, 'error');
    }

    setDraggingPostId(null);
    setDragOverDay(null);
  };

  const isVideoFormat = editingPost?.format === 'Video';

  // AI Regeneration helpers
  const handleRegenerateCaption = async () => {
    if (!editingPost) return;
    const checkRes = await checkCreditsForAction(1);
    if (!checkRes.allowed) return;
    setIsRegeneratingCaption(true);

    try {
      const prompt = aiCaptionPrompt.trim() || 'Improve and regenerate this caption to be engaging, professional, and on-brand.';
      const response = await apiService.regenerateCaption(editingPost.id, prompt, 'caption');
      if (response && response.full_caption) {
        setEditCaption(response.full_caption);
        const newCount = response.ai_rewrite_history ? response.ai_rewrite_history.length : 0;
        setEditingPost(prev => prev ? { ...prev, full_caption: response.full_caption, rewriteCount: newCount } : null);
        setPosts(prev => prev.map(p => p.id === editingPost.id ? { ...p, full_caption: response.full_caption, rewriteCount: newCount } : p));
        showToast('Caption regenerated with AI');
        await postActionCheck();
      } else {
        showToast('AI returned empty response', 'error');
      }
    } catch (err: any) {
      console.error('Failed to regenerate caption:', err);
      showToast(`Error: ${friendlyError(err)}`, 'error');
    } finally {
      setIsRegeneratingCaption(false);
    }
  };

  const handleRegenerateCreative = async () => {
    if (!editingPost) return;
    const checkRes = await checkCreditsForAction(1);
    if (!checkRes.allowed) return;
    setIsRegeneratingCreative(true);

    try {
      const prompt = aiCreativePrompt.trim() || 'Improve and regenerate this creative copy to be visually compelling, clear, and on-brand.';
      const response = await apiService.regenerateCaption(editingPost.id, prompt, 'creative_copy');
      // Use creative_copy field only — never fall back to full_caption here,
      // that would overwrite the Creative Copy textarea with the caption text.
      const newCreativeCopy = response?.creative_copy;
      if (response && newCreativeCopy) {
        setEditCreativeCopy(newCreativeCopy);
        const newCount = response.ai_rewrite_history ? response.ai_rewrite_history.length : 0;
        setEditingPost(prev => prev ? { ...prev, creativeCopy: newCreativeCopy, creative_copy: newCreativeCopy, rewriteCount: newCount } : null);
        setPosts(prev => prev.map(p => p.id === editingPost.id ? { ...p, creative_copy: newCreativeCopy, creativeCopy: newCreativeCopy, rewriteCount: newCount } : p));
        showToast('Creative copy regenerated with AI');
        await postActionCheck();
      } else {
        showToast('AI returned empty response', 'error');
      }
    } catch (err: any) {
      console.error('Failed to regenerate creative copy:', err);
      showToast(`Error: ${friendlyError(err)}`, 'error');
    } finally {
      setIsRegeneratingCreative(false);
    }
  };

  const handleRegenerateScript = async () => {
    if (!editingPost) return;
    const checkRes = await checkCreditsForAction(1);
    if (!checkRes.allowed) return;
    setIsRegeneratingScript(true);

    try {
      const prompt = aiScriptPrompt.trim() ? `Write a script: ${aiScriptPrompt}` : 'Write a structured video script for this post format and theme.';
      const response = await apiService.regenerateCaption(editingPost.id, prompt, 'script');
      const newScript = response?.script_structure || response?.script || response?.full_caption;
      if (response && newScript) {
        setEditScript(newScript);
        const newCount = response.ai_rewrite_history ? response.ai_rewrite_history.length : 0;
        setEditingPost(prev => prev ? { ...prev, script: newScript, script_structure: newScript, rewriteCount: newCount } : null);
        setPosts(prev => prev.map(p => p.id === editingPost.id ? { ...p, script: newScript, script_structure: newScript, rewriteCount: newCount } : p));
        showToast('Script regenerated with AI');
        await postActionCheck();
      } else {
        showToast('AI returned empty response', 'error');
      }
    } catch (err: any) {
      console.error('Failed to regenerate script:', err);
      showToast(`Error: ${friendlyError(err)}`, 'error');
    } finally {
      setIsRegeneratingScript(false);
    }
  };

  const handleRegenerateCta = async () => {
    if (!editingPost) return;
    const checkRes = await checkCreditsForAction(1);
    if (!checkRes.allowed) return;
    setIsRegeneratingCta(true);

    try {
      const prompt = aiCtaPrompt.trim() ? `Generate call-to-action for: ${aiCtaPrompt}` : 'Generate an engaging organic call-to-action.';
      const response = await apiService.regenerateCaption(editingPost.id, prompt, 'cta');
      // Use the cta field only — full_caption is the caption, not the new CTA.
      const newCta = response?.cta;
      if (response && newCta) {
        setEditCta(newCta);
        const newCount = response.ai_rewrite_history ? response.ai_rewrite_history.length : 0;
        setEditingPost(prev => prev ? { ...prev, cta: newCta, rewriteCount: newCount } : null);
        setPosts(prev => prev.map(p => p.id === editingPost.id ? { ...p, cta: newCta, rewriteCount: newCount } : p));
        showToast('CTA regenerated with AI');
        await postActionCheck();
      } else {
        // Nothing came back: leave the CTA as it was rather than slipping in a random preset.
        showToast("AI didn't return a CTA — your CTA is unchanged. Please try again.", 'error');
      }
    } catch (err: any) {
      console.error('Failed to regenerate CTA:', err);
      showToast(`Error: ${friendlyError(err)}`, 'error');
    } finally {
      setIsRegeneratingCta(false);
    }
  };

  const handleRegenerateHashtags = async () => {
    if (!editingPost) return;
    const checkRes = await checkCreditsForAction(1);
    if (!checkRes.allowed) return;
    setIsRegeneratingHashtags(true);

    try {
      // field_type 'hashtags' saves them as the post's hashtags (the default 'caption' overwrote the caption).
      const response = await apiService.regenerateCaption(editingPost.id, 'Suggest 5 relevant hashtags for this post, return only the hashtags separated by spaces.', 'hashtags');
      const found: string[] = Array.isArray(response?.hashtags) ? response.hashtags : [];
      if (found.length > 0) {
        setEditHashtags(found.join(' '));
        const newCount = response.ai_rewrite_history ? response.ai_rewrite_history.length : 0;
        setEditingPost(prev => prev ? { ...prev, rewriteCount: newCount } : null);
        setPosts(prev => prev.map(p => p.id === editingPost.id ? { ...p, rewriteCount: newCount } : p));
        showToast('Hashtags regenerated with AI');
        await postActionCheck();
      } else {
        // Nothing came back: leave the hashtags as they were rather than adding generic ones.
        showToast("AI didn't return any hashtags — your hashtags are unchanged. Please try again.", 'error');
      }
    } catch (err: any) {
      console.error('Failed to regenerate hashtags:', err);
      showToast(`Error: ${friendlyError(err)}`, 'error');
    } finally {
      setIsRegeneratingHashtags(false);
    }
  };

  const softEngagementCtas = [
    'Save this post for later inspiration.',
    'Double-tap if you found this helpful.',
    'Tag a teammate who needs to see this.',
    'Bookmark this for your next strategy session.',
  ];

  const discussionPrompts = [
    'What would you add? Let us know in the comments.',
    'Share your experience — we read every reply.',
    'Which tip resonated most with you?',
    'Drop your thoughts below. Let\'s discuss.',
    'Agree or disagree? Tell us why.',
  ];

  const awarenessNudges = [
    'Follow for weekly insights like this one.',
    'Turn on notifications so you never miss an update.',
    'Join the conversation — link in bio for the full guide.',
    'Stay tuned for Part 2 next week.',
  ];

  return (
    <div className="space-y-6 relative">
      {/* Toast — above every modal: the post editor and its dialogs (z-50 / z-[60])
          and LowCreditModal / CreditDeductionConfirmModal (z-[100], rendered later). */}
      {toast && (
        <div
          role={toast.type === 'error' ? 'alert' : 'status'}
          className="fixed top-4 right-4 z-[200] max-w-md bg-gray-900 text-white text-sm px-4 py-2.5 rounded-lg shadow-lg flex items-start gap-2"
        >
          {toast.type === 'success' && <i className="ri-check-line text-emerald-400 shrink-0"></i>}
          {toast.type === 'error' && <i className="ri-error-warning-line text-rose-400 shrink-0"></i>}
          <span>{toast.message}</span>
        </div>
      )}

      {/* Expired Subscription Card / Lock View */}
      {isSubscriptionExpired && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-8 text-center space-y-4 my-6 shadow-sm">
          <div className="w-16 h-16 bg-amber-100 text-amber-700 rounded-full flex items-center justify-center mx-auto text-3xl">
            <i className="ri-lock-2-line"></i>
          </div>
          <div className="max-w-md mx-auto space-y-2">
            <h3 className="text-xl font-bold text-gray-900">Subscription Expired</h3>
            <p className="text-sm text-gray-600">
              Your subscription has ended. Your Content Calendar workspace is locked. Renew your subscription to continue generating and managing your social media content calendar.
            </p>
          </div>
          <div className="pt-2">
            <button
              onClick={() => window.location.href = '/dashboard/settings/subscription'}
              className="px-6 py-2.5 bg-amber-600 hover:bg-amber-700 text-white font-medium text-sm rounded-lg transition-colors shadow-sm inline-flex items-center gap-2 cursor-pointer"
            >
              <i className="ri-refresh-line"></i>
              Renew Subscription to Continue
            </button>
          </div>
        </div>
      )}

      {/* First visit in a cycle: its calendar is being created */}
      {isCreatingCalendar && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 mb-6 shadow-sm flex items-start gap-3">
          <div className="w-10 h-10 bg-blue-100 text-blue-600 rounded-lg flex items-center justify-center text-xl shrink-0">
            <i className="ri-loader-4-line animate-spin"></i>
          </div>
          <div>
            <h4 className="text-base font-semibold text-gray-900">Creating your content calendar for this cycle</h4>
            <p className="text-sm text-gray-600 mt-0.5">
              This can take a couple of minutes. Your posts will appear here when it's ready.
            </p>
          </div>
        </div>
      )}

      {/* API Key Credit Exhaustion / Generation Error Banner */}
      {isApiExhausted && !isSubscriptionExpired && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 mb-6 shadow-sm flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-red-100 text-red-600 rounded-lg flex items-center justify-center text-xl shrink-0">
              <i className="ri-error-warning-line"></i>
            </div>
            <div>
              <h4 className="text-base font-semibold text-gray-900">Content Calendar Generation Failed</h4>
              <p className="text-sm text-gray-600 mt-0.5">
                {error || "AI service credit is exhausted or unavailable. No blank content calendar was saved."}
              </p>
            </div>
          </div>
          <button
            onClick={handleRetryCalendarGeneration}
            disabled={isRegeneratingCalendar}
            className="px-5 py-2.5 bg-red-600 hover:bg-red-700 text-white font-medium text-sm rounded-lg transition-colors shadow-sm flex items-center gap-2 shrink-0 disabled:opacity-50 cursor-pointer"
          >
            <i className={`ri-refresh-line ${isRegeneratingCalendar ? 'animate-spin' : ''}`}></i>
            {isRegeneratingCalendar ? 'Retrying Generation...' : 'Retry Calendar Generation'}
          </button>
        </div>
      )}

      {/* Any other load error (subscription period, posts, calendar regeneration) */}
      {error && !isApiExhausted && !isSubscriptionExpired && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 mb-6 shadow-sm flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-red-100 text-red-600 rounded-lg flex items-center justify-center text-xl shrink-0">
              <i className="ri-error-warning-line"></i>
            </div>
            <div>
              <h4 className="text-base font-semibold text-gray-900">Something went wrong</h4>
              <p className="text-sm text-gray-600 mt-0.5">{error}</p>
            </div>
          </div>
          <button
            onClick={handleRetryLoad}
            disabled={isLoading}
            className="px-5 py-2.5 bg-red-600 hover:bg-red-700 text-white font-medium text-sm rounded-lg transition-colors shadow-sm flex items-center gap-2 shrink-0 disabled:opacity-50 cursor-pointer"
          >
            <i className={`ri-refresh-line ${isLoading ? 'animate-spin' : ''}`}></i>
            {isLoading ? 'Retrying...' : 'Retry'}
          </button>
        </div>
      )}

      {/* Edit Modal */}
      {editPostId && editingPost && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-4 pt-8">
          <div className="bg-white rounded-xl border border-gray-200 shadow-xl w-full max-w-3xl max-h-[92vh] overflow-y-auto">
            {/* Header */}
            <div className="sticky top-0 bg-white z-20 px-6 pt-6 pb-4 border-b border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-base font-semibold text-gray-900">Content Editor powered by AI</h3>
                <button
                  onClick={handleCancelEdit}
                  className="text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
                >
                  <i className="ri-close-line text-xl"></i>
                </button>
              </div>

              {/* Read-only metadata */}
              <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">Post Details</p>
                  <div className="flex items-center gap-1.5 text-xs">
                    <span className="text-gray-500 font-medium">Format:</span>
                    <span style={{
                      padding: '4px 12px',
                      background: '#f3f4f6',
                      borderRadius: '6px',
                      fontSize: '13px',
                      fontWeight: '500',
                      color: '#374151'
                    }}>
                      {editingPost?.format || 'Static Image'}
                    </span>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded border ${getPlatformColor(editingPost.platform)}`}>
                    <i className={`${editingPost.platformIcon} text-xs`}></i>
                    {editingPost.platform}
                  </span>
                  <span className={`text-xs px-2 py-1 rounded font-medium ${getFormatBadge(editingPost.format)}`}>
                    Current: {editingPost.format}
                  </span>
                  <span className={`text-xs px-2 py-1 rounded border ${getStatusColor(editingPost.status)}`}>
                    {getStatusLabel(editingPost.status)}
                  </span>
                  {isEditingTime ? (
                    <span className="text-xs px-1.5 py-0.5 rounded border border-amber-300 bg-white flex items-center gap-1">
                      <i className="ri-time-line text-[10px] text-gray-500"></i>
                      <input
                        type="time"
                        value={timeDraft}
                        onChange={(e) => e.target.value && setTimeDraft(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleSaveTime();
                          if (e.key === 'Escape') setIsEditingTime(false);
                        }}
                        className="text-xs text-gray-700 bg-transparent outline-none"
                        aria-label="Posting time"
                        autoFocus
                      />
                      <button
                        onClick={handleSaveTime}
                        disabled={isSavingTime}
                        title="Save posting time"
                        className="text-green-600 hover:text-green-700 disabled:opacity-50 cursor-pointer"
                      >
                        <i className={isSavingTime ? 'ri-loader-4-line animate-spin' : 'ri-check-line'}></i>
                      </button>
                      <button
                        onClick={() => setIsEditingTime(false)}
                        disabled={isSavingTime}
                        title="Cancel"
                        className="text-gray-400 hover:text-gray-600 disabled:opacity-50 cursor-pointer"
                      >
                        <i className="ri-close-line"></i>
                      </button>
                    </span>
                  ) : (
                    <span
                      className={`text-xs px-2 py-1 rounded flex items-center gap-1 ${editingPost.scheduledTime ? 'bg-amber-50 text-amber-700 border border-amber-200' : 'bg-gray-100 text-gray-600'}`}
                      title={
                        editingPost.scheduledTime
                          ? (() => {
                              const window = getSuggestedWindowForPlatform(editingPost.platform);
                              return window
                                ? `Custom time (suggested window: ${formatSuggestedWindowLabel(window)})`
                                : 'Custom time';
                            })()
                          : getSuggestedWindowForPlatform(editingPost.platform)
                            ? 'Suggested posting time'
                            : 'No suggested posting time for this platform yet'
                      }
                    >
                      <i className="ri-time-line text-[10px]"></i>
                      {postTimeLabel(editingPost)}
                      {editingPost.status !== 'published' && (
                        <button
                          onClick={() => {
                            setTimeDraft(editingPost.scheduledTime || getSuggestedResetTime(editingPost.platform));
                            setIsEditingTime(true);
                          }}
                          title="Change posting time"
                          aria-label="Change posting time"
                          className="ml-0.5 text-gray-400 hover:text-blue-600 cursor-pointer"
                        >
                          <i className="ri-pencil-line text-[11px]"></i>
                        </button>
                      )}
                    </span>
                  )}
                  <span className="text-xs px-2 py-1 rounded bg-gray-100 text-gray-600 flex items-center gap-1">
                    <i className="ri-palette-line text-[10px]"></i>
                    {themeLabel(editingPost.themeType)}
                  </span>
                </div>

                {/* Warn when the drafted time falls outside this platform's real
                    suggested window. Stays silent if we don't have (or can't
                    parse) that platform's window, rather than warning against
                    a made-up default. */}
                {isEditingTime && (() => {
                  const window = getSuggestedWindowForPlatform(editingPost.platform);
                  if (!window || isHourWithinWindow(timeDraft, window)) return null;
                  const resetTime = getSuggestedResetTime(editingPost.platform);
                  return (
                    <div className="mt-2 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-2.5 py-2 text-[11px] text-amber-800">
                      <i className="ri-error-warning-line text-sm leading-none"></i>
                      <p className="flex-1">
                        <span className="font-semibold">{formatSuggestedWindowLabel(window)} is {editingPost.platform}'s suggested posting-time window.</span>{' '}
                        {formatPostTime(timeDraft)} is outside that window and may get less reach and engagement.
                      </p>
                      <button
                        onClick={() => setTimeDraft(resetTime)}
                        className="shrink-0 font-semibold underline hover:text-amber-900 cursor-pointer"
                      >
                        Use suggested
                      </button>
                    </div>
                  );
                })()}

                {/* Recalculated Platform format rules */}
                {(editingPost.captionLength || editingPost.structureFormat || editingPost.engagementStyle || editingPost.visualRequirement) && (
                  <div className="border-t border-gray-200 mt-2.5 pt-2.5 grid grid-cols-2 gap-2 text-[11px] text-gray-600">
                    {editingPost.captionLength && (
                      <div>
                        <span className="font-semibold text-gray-500">Caption length: </span>{editingPost.captionLength}
                      </div>
                    )}
                    {editingPost.structureFormat && (
                      <div>
                        <span className="font-semibold text-gray-500">Structure: </span>{editingPost.structureFormat}
                      </div>
                    )}
                    {editingPost.engagementStyle && (
                      <div>
                        <span className="font-semibold text-gray-500">Engagement: </span>{editingPost.engagementStyle}
                      </div>
                    )}
                    {editingPost.visualRequirement && (
                      <div className="col-span-2">
                        <span className="font-semibold text-gray-500">Visuals: </span>{editingPost.visualRequirement}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Modal Tabs */}
              <div className="flex items-center justify-between border-b border-gray-200 mt-4 -mx-6 px-6 gap-2">
                <div className="flex overflow-x-auto">
                  {[
                    { id: 'content' as const, label: 'Content', icon: 'ri-file-text-line' },
                    { id: 'cta' as const, label: 'CTA & Messaging', icon: 'ri-chat-1-line' },
                    { id: 'hashtags' as const, label: 'Hashtags', icon: 'ri-hashtag' },
                    { id: 'media' as const, label: 'Media', icon: 'ri-image-line' },
                  ].map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setEditTab(tab.id)}
                      className={`flex items-center gap-1.5 px-4 py-2.5 text-xs font-medium whitespace-nowrap border-b-2 transition-colors cursor-pointer ${editTab === tab.id
                        ? 'border-blue-600 text-blue-600 bg-blue-50'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                        }`}
                    >
                      <i className={tab.icon}></i>
                      {tab.label}
                    </button>
                  ))}
                  {(editingPost?.format?.toLowerCase() === 'article' ||
                    editingPost?.format?.toLowerCase() === 'miniblog') && (
                    <button
                      onClick={() => setEditTab('article')}
                      className={`flex items-center gap-1.5 px-4 py-2.5 text-xs font-medium whitespace-nowrap border-b-2 transition-colors cursor-pointer ${editTab === 'article'
                        ? 'border-indigo-600 text-indigo-600 bg-indigo-50 font-semibold'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                        }`}
                    >
                      ✍️ Article
                    </button>
                  )}
                </div>

                {/* Delete Post Action */}
                {editingPost && (
                  <button
                    onClick={() => setShowDeleteConfirm(true)}
                    disabled={isDeletingPost}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors border shrink-0 mb-1 text-red-600 hover:text-red-700 hover:bg-red-50 border-transparent hover:border-red-200 cursor-pointer disabled:opacity-50"
                    title="Delete this post"
                  >
                    <i className="ri-delete-bin-line text-sm"></i>
                    <span>Delete Post</span>
                  </button>
                )}
              </div>
            </div>

            <div className="px-6 py-5 space-y-5">
              {/* ===== Content Tab ===== */}
              {editTab === 'content' && (
                <div className="space-y-6">
                  {/* Caption */}
                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <label className="text-xs font-semibold text-gray-700">Caption</label>
                      <span className="text-[10px] text-gray-400">{editCaption.length} chars</span>
                    </div>
                    <textarea
                      disabled={editingPost.userEdited}
                      value={editCaption}
                      onChange={(e) => setEditCaption(e.target.value)}
                      rows={3}
                      maxLength={300}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none disabled:bg-gray-50 disabled:text-gray-600 disabled:cursor-not-allowed"
                    />
                    {/* AI Caption Regeneration */}
                    {!editingPost.userEdited && (
                      <div className="mt-2 bg-violet-50 rounded-lg border border-violet-100 p-3">
                        <div className="flex items-center gap-1.5 mb-1.5">
                          <i className="ri-sparkling-line text-violet-600 text-xs"></i>
                          <span className="text-[10px] font-semibold text-violet-700 uppercase tracking-wider">AI Caption Regenerator</span>
                        </div>
                        <textarea
                          value={aiCaptionPrompt}
                          onChange={(e) => setAiCaptionPrompt(e.target.value)}
                          placeholder="e.g. Make it more engaging, shorter, add a question hook..."
                          rows={2}
                          maxLength={200}
                          className="w-full border border-violet-200 rounded-md px-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-violet-400 resize-none bg-white"
                        />
                        <div className="flex items-center justify-between mt-1.5">
                          <span className="text-[10px] text-violet-400">{aiCaptionPrompt.length}/200</span>
                          <button
                            onClick={handleRegenerateCaption}
                            disabled={isRegeneratingCaption}
                            className="px-3 py-1.5 bg-violet-600 hover:bg-violet-700 disabled:bg-gray-300 text-white rounded-md text-xs font-medium transition-colors whitespace-nowrap flex items-center gap-1.5 cursor-pointer"
                          >
                            {isRegeneratingCaption ? (
                              <><i className="ri-loader-4-line animate-spin"></i> Generating...</>
                            ) : (
                              <><i className="ri-magic-line"></i> Regenerate with AI</>
                            )}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Creative Copy */}
                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <label className="text-xs font-semibold text-gray-700">Creative Copy</label>
                      <span className="text-[10px] text-gray-400">{editCreativeCopy.length} chars</span>
                    </div>
                    <textarea
                      disabled={editingPost.userEdited}
                      value={editCreativeCopy}
                      onChange={(e) => setEditCreativeCopy(e.target.value)}
                      rows={3}
                      maxLength={2000}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none disabled:bg-gray-50 disabled:text-gray-600 disabled:cursor-not-allowed"
                    />
                    {/* Carousel creative copy validation */}
                    {editingPost.format === 'Carousel' && editCreativeCopy.trim() && (() => {
                      const slideLines = editCreativeCopy.split('\n').filter(l => /^Slide \d+:/i.test(l.trim()));
                      const maxSlides = editingPost.carouselSlides || 4;
                      const hasValidFormat = slideLines.length > 0;
                      const overLimit = slideLines.length > maxSlides;
                      if (!hasValidFormat) {
                        return (
                          <p className="text-[10px] text-amber-600 mt-1">
                            Tip: Use "Slide 1: &lt;description&gt;" format, one per line.
                          </p>
                        );
                      }
                      if (overLimit) {
                        return (
                          <p className="text-[10px] text-red-500 mt-1">
                            {slideLines.length} slides detected — max allowed is {maxSlides}. Remove excess slides.
                          </p>
                        );
                      }
                      return null;
                    })()}
                    <div style={{
                      display: 'flex', justifyContent: 'flex-end',
                      marginTop: '8px'
                    }}>
                      <button
                        onClick={handleSaveCreativeCopy}
                        disabled={isSavingCreativeCopy}
                        style={{
                          padding: '8px 16px',
                          backgroundColor: isSavingCreativeCopy ? '#9ca3af' : '#6366f1',
                          color: 'white', border: 'none', borderRadius: '6px',
                          fontWeight: '600', fontSize: '13px',
                          cursor: isSavingCreativeCopy ? 'not-allowed' : 'pointer',
                        }}
                      >
                        {isSavingCreativeCopy ? 'Saving...' : '💾 Save Creative Copy'}
                      </button>
                    </div>
                    {/* AI Creative Copy Regeneration */}
                    {!editingPost.userEdited && (
                      <div className="mt-2 bg-violet-50 rounded-lg border border-violet-100 p-3">
                        <div className="flex items-center gap-1.5 mb-1.5">
                          <i className="ri-sparkling-line text-violet-600 text-xs"></i>
                          <span className="text-[10px] font-semibold text-violet-700 uppercase tracking-wider">AI Creative Copy Regenerator</span>
                        </div>
                        <textarea
                          value={aiCreativePrompt}
                          onChange={(e) => setAiCreativePrompt(e.target.value)}
                          placeholder="e.g. Add emotional appeal, include social proof, make it data-driven..."
                          rows={2}
                          maxLength={200}
                          className="w-full border border-violet-200 rounded-md px-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-violet-400 resize-none bg-white"
                        />
                        <div className="flex items-center justify-between mt-1.5">
                          <span className="text-[10px] text-violet-400">{aiCreativePrompt.length}/200</span>
                          <button
                            onClick={handleRegenerateCreative}
                            disabled={isRegeneratingCreative}
                            className="px-3 py-1.5 bg-violet-600 hover:bg-violet-700 disabled:bg-gray-300 text-white rounded-md text-xs font-medium transition-colors whitespace-nowrap flex items-center gap-1.5 cursor-pointer"
                          >
                            {isRegeneratingCreative ? (
                              <><i className="ri-loader-4-line animate-spin"></i> Generating...</>
                            ) : (
                              <><i className="ri-magic-line"></i> Regenerate with AI</>
                            )}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Script / Structure */}
                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <label className="text-xs font-semibold text-gray-700">Script / Structure</label>
                      <span className="text-[10px] text-gray-400">{editScript.length} chars</span>
                    </div>
                    <textarea
                      disabled={editingPost.userEdited}
                      value={editScript}
                      onChange={(e) => setEditScript(e.target.value)}
                      rows={4}
                      maxLength={1000}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none disabled:bg-gray-50 disabled:text-gray-600 disabled:cursor-not-allowed"
                    />
                    {/* AI Script Regeneration */}
                    {!editingPost.userEdited && (
                      <div className="mt-2 bg-violet-50 rounded-lg border border-violet-100 p-3">
                        <div className="flex items-center gap-1.5 mb-1.5">
                          <i className="ri-sparkling-line text-violet-600 text-xs"></i>
                          <span className="text-[10px] font-semibold text-violet-700 uppercase tracking-wider">AI Script Regenerator</span>
                        </div>
                        <textarea
                          value={aiScriptPrompt}
                          onChange={(e) => setAiScriptPrompt(e.target.value)}
                          placeholder="e.g. Improve retention, strengthen the hook, add a twist ending..."
                          rows={2}
                          maxLength={200}
                          className="w-full border border-violet-200 rounded-md px-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-violet-400 resize-none bg-white"
                        />
                        <div className="flex items-center justify-between mt-1.5">
                          <span className="text-[10px] text-violet-400">{aiScriptPrompt.length}/200</span>
                          <button
                            onClick={handleRegenerateScript}
                            disabled={isRegeneratingScript}
                            className="px-3 py-1.5 bg-violet-600 hover:bg-violet-700 disabled:bg-gray-300 text-white rounded-md text-xs font-medium transition-colors whitespace-nowrap flex items-center gap-1.5 cursor-pointer"
                          >
                            {isRegeneratingScript ? (
                              <><i className="ri-loader-4-line animate-spin"></i> Generating...</>
                            ) : (
                              <><i className="ri-magic-line"></i> Regenerate with AI</>
                            )}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* ===== CTA & Messaging Tab ===== */}
              {editTab === 'cta' && (
                <div className="space-y-5">
                  {/* Current CTA */}
                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="flex items-center gap-2">
                        <label className="text-xs font-semibold text-gray-700">Call-to-Action / Messaging</label>
                        {!editingPost.cta && (
                          <span className="text-[10px] bg-blue-50 text-blue-600 px-2 py-0.5 rounded font-medium flex items-center gap-0.5">
                            <i className="ri-magic-line text-[9px]"></i> Platform Suggested
                          </span>
                        )}
                      </div>
                      <span className="text-[10px] text-gray-400">{editCta.length} chars</span>
                    </div>
                    <textarea
                      disabled={editingPost.userEdited}
                      value={editCta}
                      onChange={(e) => setEditCta(e.target.value)}
                      rows={3}
                      maxLength={200}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none disabled:bg-gray-50 disabled:text-gray-600 disabled:cursor-not-allowed"
                      placeholder="Enter your organic CTA message..."
                    />
                  </div>

                  {/* AI CTA Regenerator & Presets */}
                  {!editingPost.userEdited && (
                    <>
                      {/* AI CTA Regenerator */}
                      <div className="bg-violet-50 rounded-lg border border-violet-100 p-3">
                        <div className="flex items-center gap-1.5 mb-1.5">
                          <i className="ri-sparkling-line text-violet-600 text-xs"></i>
                          <span className="text-[10px] font-semibold text-violet-700 uppercase tracking-wider">AI CTA Generator</span>
                        </div>
                        <textarea
                          value={aiCtaPrompt}
                          onChange={(e) => setAiCtaPrompt(e.target.value)}
                          placeholder="e.g. Soft nudge for awareness, discussion starter, community engagement..."
                          rows={2}
                          maxLength={200}
                          className="w-full border border-violet-200 rounded-md px-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-violet-400 resize-none bg-white"
                        />
                        <div className="flex items-center justify-between mt-1.5">
                          <span className="text-[10px] text-violet-400">{aiCtaPrompt.length}/200</span>
                          <button
                            onClick={handleRegenerateCta}
                            disabled={isRegeneratingCta}
                            className="px-3 py-1.5 bg-violet-600 hover:bg-violet-700 disabled:bg-gray-300 text-white rounded-md text-xs font-medium transition-colors whitespace-nowrap flex items-center gap-1.5 cursor-pointer"
                          >
                            {isRegeneratingCta ? (
                              <><i className="ri-loader-4-line animate-spin"></i> Generating...</>
                            ) : (
                              <><i className="ri-magic-line"></i> Generate with AI</>
                            )}
                          </button>
                        </div>
                      </div>

                      {/* Organic CTA Presets */}
                      <div className="space-y-4">
                        {/* Soft Engagement CTAs */}
                        <div>
                          <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                            <i className="ri-heart-line text-rose-500"></i>
                            Soft Engagement CTAs
                          </p>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            {softEngagementCtas.map((preset, idx) => (
                              <button
                                key={idx}
                                onClick={() => setEditCta(preset)}
                                className={`text-left px-3 py-2 rounded-lg border text-xs transition-all cursor-pointer ${editCta === preset
                                  ? 'border-rose-300 bg-rose-50 text-rose-800'
                                  : 'border-gray-200 text-gray-700 hover:bg-gray-50 hover:border-gray-300'
                                  }`}
                              >
                                {preset}
                              </button>
                            ))}
                          </div>
                        </div>

                        {/* Discussion Prompts */}
                        <div>
                          <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                            <i className="ri-discuss-line text-sky-500"></i>
                            Discussion Prompts
                          </p>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            {discussionPrompts.map((preset, idx) => (
                              <button
                                key={idx}
                                onClick={() => setEditCta(preset)}
                                className={`text-left px-3 py-2 rounded-lg border text-xs transition-all cursor-pointer ${editCta === preset
                                  ? 'border-sky-300 bg-sky-50 text-sky-800'
                                  : 'border-gray-200 text-gray-700 hover:bg-gray-50 hover:border-gray-300'
                                  }`}
                              >
                                {preset}
                              </button>
                            ))}
                          </div>
                        </div>

                        {/* Awareness Nudges */}
                        <div>
                          <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                            <i className="ri-eye-line text-amber-500"></i>
                            Awareness Nudges
                          </p>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            {awarenessNudges.map((preset, idx) => (
                              <button
                                key={idx}
                                onClick={() => setEditCta(preset)}
                                className={`text-left px-3 py-2 rounded-lg border text-xs transition-all cursor-pointer ${editCta === preset
                                  ? 'border-amber-300 bg-amber-50 text-amber-800'
                                  : 'border-gray-200 text-gray-700 hover:bg-gray-50 hover:border-gray-300'
                                  }`}
                              >
                                {preset}
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>
                    </>
                  )}

                  {/* Info callout */}
                  <div className="bg-emerald-50 rounded-lg p-3 border border-emerald-200 flex items-start gap-2.5">
                    <i className="ri-information-line text-emerald-600 text-sm mt-0.5"></i>
                    <div>
                      <p className="text-xs font-medium text-emerald-900">Organic CTA Guidelines</p>
                      <p className="text-[11px] text-emerald-700 mt-0.5 leading-relaxed">
                        These CTAs are designed for organic engagement — not direct conversion.
                        Focus on soft engagement, sparking discussion, and building awareness rather than pushing sales.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* ===== Hashtags Tab ===== */}
              {editTab === 'hashtags' && (
                <div className="space-y-5">
                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <label className="text-xs font-semibold text-gray-700">Hashtags (space separated)</label>
                      <span className="text-[10px] text-gray-400">{editHashtags.split(/\s+/).filter(Boolean).length} tags</span>
                    </div>
                    <textarea
                      disabled={editingPost.userEdited}
                      value={editHashtags}
                      onChange={(e) => setEditHashtags(e.target.value)}
                      rows={3}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none disabled:bg-gray-50 disabled:text-gray-600 disabled:cursor-not-allowed"
                      placeholder="#Example #Hashtag #Set"
                    />
                  </div>

                  {/* AI Hashtag Regenerator */}
                  {!editingPost.userEdited && (
                    <div className="bg-violet-50 rounded-lg border border-violet-100 p-3">
                      <div className="flex items-center gap-1.5 mb-1.5">
                        <i className="ri-sparkling-line text-violet-600 text-xs"></i>
                        <span className="text-[10px] font-semibold text-violet-700 uppercase tracking-wider">AI Hashtag Suggestions</span>
                      </div>
                      <button
                        onClick={handleRegenerateHashtags}
                        disabled={isRegeneratingHashtags}
                        className="px-3 py-1.5 bg-violet-600 hover:bg-violet-700 disabled:bg-gray-300 text-white rounded-md text-xs font-medium transition-colors whitespace-nowrap flex items-center gap-1.5 cursor-pointer"
                      >
                        {isRegeneratingHashtags ? (
                          <><i className="ri-loader-4-line animate-spin"></i> Generating...</>
                        ) : (
                          <><i className="ri-magic-line"></i> Auto-Generate Hashtags</>
                        )}
                      </button>
                    </div>
                  )}

                  {/* Suggested hashtags preview */}
                  <div className="flex flex-wrap gap-2">
                    {editHashtags.split(/\s+/).filter(Boolean).map((tag) => {
                      const cleanTag = tag.startsWith('#') ? tag.slice(1) : tag;
                      return (
                        <span key={tag} className="inline-flex items-center gap-1 text-xs text-blue-700 bg-blue-50 px-2.5 py-1 rounded-lg">
                          {cleanTag}
                          {!editingPost.userEdited && (
                            <button
                              onClick={() => {
                                const tags = editHashtags.split(/\s+/).filter((t) => t !== tag && t !== '');
                                setEditHashtags(tags.join(' '));
                              }}
                              className="text-blue-400 hover:text-blue-600 cursor-pointer"
                            >
                              <i className="ri-close-line text-[10px]"></i>
                            </button>
                          )}
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* ===== Media Tab ===== */}
              {editTab === 'media' && (
                <div className="space-y-6">
                  {/* Without plan usage every generate button below looks locked. */}
                  {entitlementsError && (
                    <div className="flex items-center justify-between gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-2.5">
                      <span className="flex items-start gap-2">
                        <i className="ri-error-warning-line text-sm mt-0.5 shrink-0"></i>
                        <span>Couldn't load your plan usage, so generate buttons may show as locked.</span>
                      </span>
                      <button
                        onClick={() => refreshEntitlements()}
                        className="shrink-0 px-2.5 py-1 bg-amber-600 hover:bg-amber-700 text-white rounded-md text-xs font-medium transition-colors cursor-pointer"
                      >
                        Retry
                      </button>
                    </div>
                  )}

                  {/* A generation that failed while nobody was watching (e.g. it
                      finished after a refresh). A job run in this session shows
                      its own state in the banners below instead; once the post has
                      media (e.g. a photo uploaded after the failure) it no longer applies. */}
                  {editingPost.imageGenStatus === 'FAILED' && !hasGenerationJobThisSession
                    && !(selectedImageUrl || enhancedImageUrl || selectedVideoUrl || carouselSlideUrls.length > 0) && (
                    <div className="flex items-start gap-2 text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-md p-2.5">
                      <i className="ri-error-warning-line text-sm mt-0.5 shrink-0"></i>
                      <span>
                        The last image generation for this post failed
                        {editingPost.imageGenError ? `: ${friendlyError(editingPost.imageGenError)}` : '.'}
                      </span>
                    </div>
                  )}

                  {/* Photo Formats UI */}
                  {!isVideoFormat && (
                    <>
                      {(() => {
                        const isCarousel = editingPost.format?.toLowerCase() === 'carousel';
                        const hasMedia = Boolean(selectedImageUrl || enhancedImageUrl || carouselSlideUrls.length > 0);
                        const postFormatLower = (editingPost?.format || '').toLowerCase();
                        const resolvedCost = currentGenerateCost ?? (
                          postFormatLower === 'carousel' ? 100 :
                          postFormatLower === 'video' || postFormatLower === 'reel' || postFormatLower === 'short' ? 300 :
                          25
                        );

                        const isMediaManual = Boolean(
                          isManualImageUploaded ||
                          [
                            selectedImageUrl,
                            enhancedImageUrl,
                            editingPost?.imageUrl,
                            editingPost?.creativeAsset?.url,
                            ...(carouselSlideUrls || []),
                          ].some((url) => Boolean(url && typeof url === 'string' && (url.includes('smm-uploads') || url.includes('/smm-uploads/'))))
                        );

                        // The open post's own generation job (Reel/Short run the
                        // video job but use this layout). Its failure / timeout is
                        // shown above the Generate button (no media yet) or the
                        // Regenerate button (media present), not only in a toast.
                        const isReelOrShort = postFormatLower === 'reel' || postFormatLower === 'short';
                        const generationJob =
                          postFormatLower === 'carousel' ? carouselGeneration
                            : postFormatLower === 'infographic' ? infographicGeneration
                              : isReelOrShort ? videoGeneration
                                : staticPostGeneration;
                        const generateErrorMessage = generationJob.errorMessageFor(openPostId);
                        const generateError = generateErrorMessage ? friendlyError(generateErrorMessage) : null;
                        // Reopening the post clears an image job's timed-out state
                        // and resumes a still-running job (not so for video).
                        const generateTimedOut = !isReelOrShort && generationJob.isTimedOutFor(openPostId);
                        const renderGenerationNotices = (showError: boolean) => (
                          <>
                            {showError && generateError && (
                              <div className="mb-3 flex items-start gap-2 text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-md p-2.5">
                                <i className="ri-error-warning-line text-sm mt-0.5 shrink-0"></i>
                                <span>{generateError}</span>
                              </div>
                            )}
                            {generateTimedOut && (
                              <div className="mb-3 flex items-start gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-2.5">
                                <i className="ri-time-line text-sm mt-0.5 shrink-0"></i>
                                <span>This is taking longer than usual and may still finish in the background. Close and reopen this post in a few minutes to check before generating again.</span>
                              </div>
                            )}
                          </>
                        );

                        return (
                          <>
                            {/* CASE 1: No Photo Present yet -> Upload Photo Dropzone OR Generate Button */}
                            {!hasMedia && !editingPost.userEdited && (
                              <div className="space-y-5">
                                <div>
                                  <div className="flex items-center gap-2 mb-3">
                                    <div className="w-6 h-6 rounded bg-sky-50 text-sky-600 flex items-center justify-center">
                                      <i className="ri-image-line text-sm"></i>
                                    </div>
                                    <h4 className="text-sm font-semibold text-gray-900">Upload Photo</h4>
                                  </div>

                                  {/* Upload Error Banner */}
                                  {photoUploadError && (
                                    <div className="mb-3 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-xs flex items-center justify-between gap-2">
                                      <div className="flex items-center gap-2">
                                        <i className="ri-error-warning-line text-sm shrink-0"></i>
                                        <span>{photoUploadError}</span>
                                      </div>
                                      <button
                                        onClick={() => setPhotoUploadError(null)}
                                        className="text-red-400 hover:text-red-600 cursor-pointer"
                                      >
                                        <i className="ri-close-line text-sm"></i>
                                      </button>
                                    </div>
                                  )}

                                  {/* Dropzone Upload Area */}
                                  <div
                                    onDragOver={(e) => {
                                      e.preventDefault();
                                      setIsDraggingMedia(true);
                                    }}
                                    onDragLeave={() => setIsDraggingMedia(false)}
                                    onDrop={(e) => {
                                      e.preventDefault();
                                      setIsDraggingMedia(false);
                                      if (e.dataTransfer.files?.length) {
                                        handleManualPhotoUpload(e.dataTransfer.files);
                                      }
                                    }}
                                    onClick={() => !isUploadingPhoto && photoInputRef.current?.click()}
                                    className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${
                                      isDraggingMedia
                                        ? 'border-blue-500 bg-blue-50/50'
                                        : 'border-gray-300 hover:border-blue-400 bg-gray-50/30 hover:bg-gray-50'
                                    }`}
                                  >
                                    <input
                                      ref={photoInputRef}
                                      type="file"
                                      accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
                                      multiple={isCarousel}
                                      className="hidden"
                                      onChange={(e) => {
                                        if (e.target.files?.length) {
                                          handleManualPhotoUpload(e.target.files);
                                        }
                                        e.target.value = '';
                                      }}
                                    />

                                    {isUploadingPhoto ? (
                                      <div className="flex flex-col items-center justify-center py-2">
                                        <i className="ri-loader-4-line animate-spin text-3xl text-blue-600 mb-2"></i>
                                        <p className="text-sm font-medium text-gray-700">Uploading photo...</p>
                                        <p className="text-xs text-gray-400 mt-0.5">Attaching asset to post</p>
                                      </div>
                                    ) : (
                                      <div className="flex flex-col items-center justify-center">
                                        <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-500 mb-3 group-hover:bg-blue-100 group-hover:text-blue-600 transition-colors">
                                          <i className="ri-upload-cloud-2-line text-xl"></i>
                                        </div>
                                        <p className="text-sm font-semibold text-gray-800 mb-1">
                                          Drag {isCarousel ? 'up to 4 photos' : 'photo'} here or <span className="text-blue-600 underline">click to browse</span>
                                        </p>
                                        <p className="text-xs text-gray-400">
                                          JPG, PNG, WEBP — max 5MB {isCarousel ? '(up to 4 slides)' : ''}
                                        </p>
                                      </div>
                                    )}
                                  </div>
                                </div>

                                {/* Divider with 'or' */}
                                <div className="relative flex py-2 items-center">
                                  <div className="flex-grow border-t border-gray-200"></div>
                                  <span className="flex-shrink mx-4 text-xs font-medium text-gray-400 uppercase tracking-wider">or</span>
                                  <div className="flex-grow border-t border-gray-200"></div>
                                </div>

                                {/* Generate Error Banner — surfaces the last failed
                                    generation attempt's message (after the backend has
                                    already retried and given up) directly above the
                                    button, instead of only the transient toast.
                                    Video isn't handled here — this whole CASE 1/2 block
                                    is skipped for isVideoFormat (see the !isVideoFormat
                                    guard above); its own Generate button and error
                                    banner live in the dedicated video panel below. */}
                                {renderGenerationNotices(true)}

                                {/* Generate Button */}
                                <div>
                                  {(() => {
                                    const postFormatLower = (editingPost?.format || '').toLowerCase();
                                    const resolvedCost = currentGenerateCost ?? (
                                      postFormatLower === 'carousel' ? 100 :
                                      postFormatLower === 'video' || postFormatLower === 'reel' || postFormatLower === 'short' ? 300 :
                                      25
                                    );
                                    return (
                                      <button
                                        onClick={handleGenerateButtonClick}
                                        disabled={isGenerateInFlight}
                                        className={
                                          isCurrentGenerateLocked
                                            ? "w-full px-4 py-2.5 bg-amber-50 hover:bg-amber-100 disabled:opacity-60 text-amber-700 border border-amber-200 rounded-md text-sm font-medium transition-colors whitespace-nowrap flex items-center justify-center gap-2 cursor-pointer disabled:cursor-not-allowed"
                                            : "w-full px-4 py-2.5 bg-violet-600 hover:bg-violet-700 disabled:bg-violet-400 text-white rounded-md text-sm font-medium transition-colors whitespace-nowrap flex items-center justify-center gap-2 cursor-pointer disabled:cursor-not-allowed shadow-sm"
                                        }
                                      >
                                        {isGenerateInFlight ? (
                                          <>
                                            <i className="ri-loader-4-line animate-spin text-sm"></i>
                                            <span>Generating...</span>
                                          </>
                                        ) : (
                                          <>
                                            <i className={isCurrentGenerateLocked ? "ri-lock-2-line text-sm" : "ri-magic-line text-sm"}></i>
                                            <span>
                                              {isCurrentGenerateLocked
                                                ? `Generate - ${resolvedCost} credits required`
                                                : `Generate ${editingPost?.format || 'Post'}`}
                                            </span>
                                          </>
                                        )}
                                      </button>
                                    );
                                  })()}
                                  {isGenerateInFlight && (
                                    <div className="mt-2 flex items-start gap-2 text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded-md p-2.5">
                                      <i className="ri-information-line text-sm mt-0.5 shrink-0"></i>
                                      <span>Generating in the background — feel free to work on other modules while this finishes.</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}

                            {/* CASE 2: Media is Present -> Media Preview Box + Merged AI Enhancement Section */}
                            {hasMedia && (
                              <div className="space-y-6">
                                {/* Media Header with optional Add Slide button */}
                                <div>
                                  <div className="flex items-center justify-between mb-3">
                                    <div className="flex items-center gap-2">
                                      <div className="w-6 h-6 rounded bg-sky-50 text-sky-600 flex items-center justify-center">
                                        <i className="ri-image-line text-sm"></i>
                                      </div>
                                      <h4 className="text-sm font-semibold text-gray-900">Media</h4>
                                    </div>
                                    {isCarousel && carouselSlideUrls.length < 4 && !editingPost.userEdited && (
                                      isUploadingPhoto ? (
                                        <div className="flex items-center gap-1.5 text-xs text-blue-600 font-medium">
                                          <i className="ri-loader-4-line animate-spin text-sm"></i>
                                          <span>Uploading slide...</span>
                                        </div>
                                      ) : (
                                        <button
                                          onClick={() => photoInputRef.current?.click()}
                                          disabled={isUploadingPhoto}
                                          className="text-xs text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1 cursor-pointer"
                                        >
                                          <i className="ri-add-line"></i> Add Slide ({carouselSlideUrls.length}/4)
                                        </button>
                                      )
                                    )}
                                  </div>

                                  {/* Hidden Input for adding more carousel slides */}
                                  <input
                                    ref={photoInputRef}
                                    type="file"
                                    accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
                                    multiple={isCarousel}
                                    className="hidden"
                                    onChange={(e) => {
                                      if (e.target.files?.length) {
                                        handleManualPhotoUpload(e.target.files);
                                      }
                                      e.target.value = '';
                                    }}
                                  />

                                  {/* Media Preview Box */}
                                  <div className="rounded-lg border border-gray-200 overflow-hidden bg-gray-50 p-2">
                                    <div className="relative">
                                      {isCarousel && carouselSlideUrls.length > 0 ? (
                                        <div style={{ position: 'relative', width: '100%' }}>
                                          {/* Active Slide Image */}
                                          <img
                                            src={carouselSlideUrls[currentSlideIndex] || carouselSlideUrls[0]}
                                            alt={`Slide ${currentSlideIndex + 1}`}
                                            className="w-full rounded-lg object-contain"
                                          />

                                          {/* Delete Active Slide Button */}
                                          {!editingPost.userEdited && (
                                            <button
                                              onClick={handleDeleteImage}
                                              className="absolute top-2 right-2 w-7 h-7 bg-black/60 hover:bg-black/80 rounded-full flex items-center justify-center text-white transition-colors cursor-pointer z-[1] shadow-md"
                                              title="Remove this slide"
                                            >
                                              <i className="ri-close-line text-sm"></i>
                                            </button>
                                          )}

                                          {/* Slide Counter */}
                                          <div className="text-center mt-2 text-xs font-semibold text-gray-600">
                                            Slide {currentSlideIndex + 1} of {carouselSlideUrls.length}
                                          </div>

                                          {/* Navigation Arrows */}
                                          {carouselSlideUrls.length > 1 && (
                                            <>
                                              <button
                                                onClick={() => setCurrentSlideIndex((i) => Math.max(0, i - 1))}
                                                disabled={currentSlideIndex === 0}
                                                className="absolute top-1/2 left-2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/50 hover:bg-black/70 text-white flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                                              >
                                                <i className="ri-arrow-left-s-line text-lg"></i>
                                              </button>

                                              <button
                                                onClick={() =>
                                                  setCurrentSlideIndex((i) =>
                                                    Math.min(carouselSlideUrls.length - 1, i + 1)
                                                  )
                                                }
                                                disabled={currentSlideIndex === carouselSlideUrls.length - 1}
                                                className="absolute top-1/2 right-2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/50 hover:bg-black/70 text-white flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                                              >
                                                <i className="ri-arrow-right-s-line text-lg"></i>
                                              </button>
                                            </>
                                          )}

                                          {/* Dot Indicators */}
                                          {carouselSlideUrls.length > 1 && (
                                            <div className="flex justify-center gap-1.5 mt-2">
                                              {carouselSlideUrls.map((_, i) => (
                                                <button
                                                  key={i}
                                                  onClick={() => setCurrentSlideIndex(i)}
                                                  className={`w-2 h-2 rounded-full transition-all ${
                                                    i === currentSlideIndex
                                                      ? 'bg-violet-600 w-4'
                                                      : 'bg-gray-300 hover:bg-gray-400'
                                                  }`}
                                                />
                                              ))}
                                            </div>
                                          )}
                                        </div>
                                      ) : (
                                        <>
                                          <img
                                            src={enhancedImageUrl || selectedImageUrl}
                                            alt="Uploaded / Generated"
                                            className="w-full rounded-lg object-contain"
                                          />
                                          {enhancedImageUrl && (
                                            <div className="absolute top-2 left-2 bg-emerald-600 text-white text-[10px] px-2 py-0.5 rounded-full font-medium flex items-center gap-1 shadow-sm">
                                              <i className="ri-sparkling-line text-[10px]"></i> AI Enhanced
                                            </div>
                                          )}
                                          {!editingPost.userEdited && (
                                            <button
                                              onClick={handleDeleteImage}
                                              className="absolute top-2 right-2 w-7 h-7 bg-black/60 hover:bg-black/80 rounded-full flex items-center justify-center text-white transition-colors cursor-pointer z-[1] shadow-md"
                                              title="Remove image"
                                            >
                                              <i className="ri-close-line text-sm"></i>
                                            </button>
                                          )}
                                        </>
                                      )}
                                    </div>
                                  </div>

                                  {/* Image Option Picker — fires for two distinct mechanisms that share
                                      the same ContentPost.image_options/selected_image_index columns:
                                      (1) the older single-image "hard failure" 3-angle recovery
                                      (options carry `angle`), and (2) the quality-gate faulty-image
                                      picker (options carry `issues`, Carousel ones also `slide_number`).
                                      Distinguished by whether any option carries `issues`. */}
                                  {(() => {
                                    const allOpts: Array<{ url: string; angle?: string; issues?: string[]; slide_number?: number; selected?: boolean }> =
                                      (editingPost as any)?.image_options || (editingPost as any)?.imageOptions || [];
                                    if (!Array.isArray(allOpts) || allOpts.length === 0 || editingPost?.userEdited || editingPost?.governanceStatus === 'DONE') {
                                      return null;
                                    }

                                    const isCarouselFormat = (editingPost.format || '').toLowerCase() === 'carousel';
                                    const hasSlideNumbers = allOpts.some(o => typeof o.slide_number === 'number');
                                    // A carousel can have several independently-faulty slides at
                                    // once (the pipeline no longer stops at the first one) — each
                                    // slide's candidates are tagged with that slide's own
                                    // slide_number, so scope the picker to whichever slide is
                                    // currently open rather than showing every slide's candidates
                                    // mixed together.
                                    const rawOpts = isCarouselFormat && hasSlideNumbers
                                      ? allOpts.filter(o => o.slide_number === currentSlideIndex + 1)
                                      : allOpts;
                                    if (rawOpts.length === 0) {
                                      return null;
                                    }

                                    const isFaulty = rawOpts.some(o => Array.isArray(o.issues) && o.issues.length > 0);
                                    // Fallback only for data persisted before per-option `selected`
                                    // existed — once set, `selected` is authoritative per slide/group
                                    // (a single global index can't represent "resolved" independently
                                    // for several unrelated faulty slides at once).
                                    const legacyActiveIndex = (editingPost as any)?.selected_image_index ?? (editingPost as any)?.selectedImageIndex ?? 0;

                                    return (
                                      <div className="mt-3 p-3.5 bg-amber-50/90 border border-amber-200 rounded-lg space-y-3 shadow-xs">
                                        <div className="flex items-start justify-between gap-2">
                                          <div className="flex items-start gap-2 text-amber-900">
                                            <i className="ri-error-warning-line text-base text-amber-600 mt-0.5 shrink-0"></i>
                                            <div>
                                              <h5 className="text-xs font-semibold text-amber-900">
                                                {isFaulty
                                                  ? (isCarouselFormat ? `Slide ${currentSlideIndex + 1} didn't pass quality checks` : "Didn't pass quality checks")
                                                  : 'Recovery Variations'}
                                              </h5>
                                              <p className="text-[11px] text-amber-700 leading-snug">
                                                {isFaulty
                                                  ? 'These have flaws (see below) — use one anyway, or try again for a fresh generation.'
                                                  : "First attempt didn't work out — pick one of these generated variations:"}
                                              </p>
                                            </div>
                                          </div>
                                          {isFaulty && (
                                            <button
                                              type="button"
                                              disabled={isGenerateInFlight}
                                              onClick={() => handleGenerate()}
                                              className="shrink-0 text-[11px] font-semibold px-2.5 py-1.5 rounded-md bg-white border border-amber-300 text-amber-800 hover:bg-amber-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                            >
                                              Try Again
                                            </button>
                                          )}
                                        </div>

                                        <div className={`grid gap-2 ${rawOpts.length === 1 ? 'grid-cols-1' : rawOpts.length === 2 ? 'grid-cols-2' : 'grid-cols-3'}`}>
                                          {rawOpts.map((opt) => {
                                            const idx = allOpts.indexOf(opt);
                                            const isSelected = opt.selected === true
                                              || (opt.selected === undefined && idx === legacyActiveIndex);
                                            const label = opt.angle || (opt.issues?.length ? opt.issues.join('; ') : `Option ${idx + 1}`);
                                            return (
                                              <div
                                                key={idx}
                                                onClick={() => handleSelectVariation(idx)}
                                                className={`group relative rounded-lg border overflow-hidden bg-white cursor-pointer transition-all ${
                                                  isSelected
                                                    ? 'border-violet-600 ring-2 ring-violet-500/20 shadow-sm'
                                                    : 'border-amber-200 hover:border-amber-400'
                                                }`}
                                              >
                                                <div className="aspect-square relative bg-gray-100 overflow-hidden">
                                                  <img
                                                    src={opt.url}
                                                    alt={label}
                                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                                                  />
                                                  {isSelected && (
                                                    <div className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-violet-600 text-white flex items-center justify-center shadow-xs">
                                                      <i className="ri-check-line text-xs font-bold"></i>
                                                    </div>
                                                  )}
                                                </div>
                                                <div className="p-2 text-center bg-white border-t border-gray-100">
                                                  <p className="text-[10px] font-medium text-gray-700 truncate" title={label}>
                                                    {opt.angle || (opt.issues?.length ? `${opt.issues.length} issue${opt.issues.length > 1 ? 's' : ''}` : `Option ${idx + 1}`)}
                                                  </p>
                                                  <button
                                                    type="button"
                                                    disabled={isSelectingVariation}
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      handleSelectVariation(idx);
                                                    }}
                                                    className={`mt-1.5 w-full text-[10px] py-1 px-1.5 rounded font-semibold transition-colors ${
                                                      isSelected
                                                        ? 'bg-violet-50 text-violet-700 border border-violet-200'
                                                        : 'bg-gray-50 hover:bg-violet-600 hover:text-white text-gray-600 border border-gray-200'
                                                    }`}
                                                  >
                                                    {isSelected ? 'Active Image' : 'Use This Image'}
                                                  </button>
                                                </div>
                                              </div>
                                            );
                                          })}
                                        </div>
                                      </div>
                                    );
                                  })()}
                                </div>

                                {logoPlacementEligible && currentSlideRawUrl && (
                                  <LogoPlacementSection
                                    key={isCarouselFormat ? `slide-${currentSlideNumber}` : 'single'}
                                    postId={editingPost.id}
                                    rawImageUrl={currentSlideRawUrl}
                                    logoPosition={currentSlideLogoPosition}
                                    corners={logoCorners.data}
                                    cornersError={logoCorners.error}
                                    cornersLoading={logoCorners.loading}
                                    onApplied={handleLogoApplied}
                                    onCornerSelect={setPendingLogoCorner}
                                    onStyleChange={setPendingLogoStyle}
                                    isCarousel={isCarouselFormat}
                                    slideNumber={isCarouselFormat ? currentSlideNumber : undefined}
                                    totalSlides={isCarouselFormat ? (editingPost.rawCarouselSlideUrls?.length || carouselSlideUrls.length) : undefined}
                                  />
                                )}

                                {/* A failed or timed-out regenerate keeps the old image on
                                    screen — say so here too. A carousel's failure has its
                                    own banner with Retry in the AI Enhancement section. */}
                                {renderGenerationNotices(!isCarousel)}

                                {/* Regenerate Post Button - Just above AI Enhancement line (Hidden if manual image uploaded) */}
                                {!editingPost.userEdited && !isMediaManual && (
                                  <div>
                                    <button
                                      onClick={() => handleGenerateButtonClick(true)}
                                      disabled={isGenerateInFlight}
                                      className={
                                        isCurrentGenerateLocked
                                          ? "w-full px-4 py-2.5 bg-amber-50 hover:bg-amber-100 disabled:opacity-60 text-amber-700 border border-amber-200 rounded-md text-sm font-medium transition-colors whitespace-nowrap flex items-center justify-center gap-2 cursor-pointer disabled:cursor-not-allowed"
                                          : "w-full px-4 py-2.5 bg-violet-600 hover:bg-violet-700 disabled:bg-violet-400 text-white rounded-md text-sm font-medium transition-colors whitespace-nowrap flex items-center justify-center gap-2 cursor-pointer disabled:cursor-not-allowed shadow-sm"
                                      }
                                    >
                                      {isGenerateInFlight ? (
                                        <>
                                          <i className="ri-loader-4-line animate-spin text-sm"></i>
                                          <span>Regenerating Post...</span>
                                        </>
                                      ) : isCurrentGenerateLocked ? (
                                        <>
                                          <i className="ri-lock-2-line text-sm"></i>
                                          <span>Regenerate Post - {resolvedCost} credits required</span>
                                        </>
                                      ) : (
                                        <>
                                          <i className="ri-refresh-line text-sm"></i>
                                          <span>Regenerate Post - {resolvedCost} credits required</span>
                                        </>
                                      )}
                                    </button>
                                    {isGenerateInFlight && (
                                      <div className="mt-2 flex items-start gap-2 text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded-md p-2.5">
                                        <i className="ri-information-line text-sm mt-0.5 shrink-0"></i>
                                        <span>Generating in the background — feel free to work on other modules while this finishes.</span>
                                      </div>
                                    )}
                                  </div>
                                )}

                                {/* Merged AI Enhancement Section */}
                                {!editingPost.userEdited && (
                                  <div className="border-t border-gray-200 pt-5">
                                    <div className="flex items-center gap-2 mb-1">
                                      <div className="w-6 h-6 rounded bg-violet-50 text-violet-600 flex items-center justify-center">
                                        <i className="ri-sparkling-line text-sm"></i>
                                      </div>
                                      <h4 className="text-sm font-semibold text-gray-900">AI Enhancement</h4>
                                    </div>
                                    <p className="text-xs text-gray-500 mb-3 ml-8">
                                      Any specific instructions for this image? (optional)
                                    </p>

                                    <div className="mb-3">
                                      <textarea
                                        value={regenerateInstructions}
                                        onChange={(e) => setRegenerateInstructions(e.target.value)}
                                        placeholder="write any specific req......"
                                        rows={2}
                                        className="w-full text-sm border border-gray-300 rounded-md p-2.5 focus:outline-none focus:ring-2 focus:ring-violet-500 resize-none"
                                      />
                                    </div>

                                    {editingPost.copyChangedSinceImage && (
                                      <div className="mb-3 flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 text-amber-800 rounded-lg text-xs font-semibold animate-pulse">
                                        <i className="ri-error-warning-line text-amber-600 text-sm"></i>
                                        Copy updated — image may be outdated
                                      </div>
                                    )}

                                    {/* Carousel Async Generation Status */}
                                    {isCarousel && carouselGeneration.isPendingFor(openPostId) && (
                                      <div className="mb-3 flex items-start gap-2 text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded-md p-2.5">
                                        <span>Generating slides… This can take a few minutes — the slides will appear here automatically once they're ready. Feel free to work on other modules while this finishes.</span>
                                      </div>
                                    )}
                                    {isCarousel && carouselGeneration.isErrorFor(openPostId) && (
                                      <div className="mb-3 flex items-center justify-between gap-2 text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-md p-2.5">
                                        <span className="flex items-start gap-2">
                                          <i className="ri-error-warning-line text-sm mt-0.5 shrink-0"></i>
                                          <span>{friendlyError(carouselGeneration.errorMessageFor(openPostId) || 'Generation failed.')}</span>
                                        </span>
                                        <button
                                          onClick={handleRetryCarousel}
                                          className="shrink-0 px-2.5 py-1 bg-rose-600 hover:bg-rose-700 disabled:opacity-60 text-white rounded-md text-xs font-medium transition-colors cursor-pointer disabled:cursor-not-allowed"
                                          disabled={isGenerateInFlight}
                                        >
                                          Retry
                                        </button>
                                      </div>
                                    )}

                                    {/* Enhance Image stopped being watched before it finished */}
                                    {enhanceTimedOutPostId === editingPost.id && enhancingPostId !== editingPost.id && (
                                      <div className="mb-3 flex items-start gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-2.5">
                                        <i className="ri-time-line text-sm mt-0.5 shrink-0"></i>
                                        <span>The enhancement is taking longer than expected and may still finish in the background. Close and reopen this post in a few minutes to see the result.</span>
                                      </div>
                                    )}

                                    {/* Enhance Button */}
                                    <button
                                      onClick={handleEnhanceImage}
                                      disabled={enhancingPostId === editingPost.id || isGenerateInFlight}
                                      className="w-full px-4 py-2.5 bg-violet-600 hover:bg-violet-700 disabled:bg-violet-400 text-white rounded-md text-sm font-medium transition-colors whitespace-nowrap flex items-center justify-center gap-2 cursor-pointer disabled:cursor-not-allowed shadow-sm"
                                    >
                                      {enhancingPostId === editingPost.id ? (
                                        <>
                                          <i className="ri-loader-4-line animate-spin text-sm"></i>
                                          <span>Enhancing {isCarousel ? `Slide ${currentSlideIndex + 1}...` : 'Image...'}</span>
                                        </>
                                      ) : (
                                        <>
                                          <i className={isCurrentGenerateLocked ? "ri-lock-2-line text-sm" : "ri-sparkling-line text-sm"}></i>
                                          <span>
                                            {isCurrentGenerateLocked
                                              ? "Enhance - 25 credits required"
                                              : "Enhance Image"}
                                          </span>
                                        </>
                                      )}
                                    </button>
                                  </div>
                                )}
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </>
                  )}

                  {/* Video Formats UI */}
                  {isVideoFormat && (
                    <div className="space-y-5">
                      {/* AI Video Generator Section */}
                      {!editingPost.userEdited && (
                        <div className="border-t border-gray-200 pt-5">
                          <div className="flex items-center gap-2 mb-3">
                            <div className="w-7 h-7 bg-violet-50 rounded-md flex items-center justify-center">
                              <i className="ri-sparkling-line text-violet-600 text-sm"></i>
                            </div>
                            <h4 className="text-sm font-semibold text-gray-900">AI Video Generator</h4>
                          </div>

                          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 space-y-4">
                            {Boolean(generatedVideoUrl || editingPost.imageUrl) && (
                              <div>
                                <p className="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1">
                                  Regeneration instructions (optional)
                                </p>
                                <textarea
                                  value={regenerateInstructions}
                                  onChange={(e) => setRegenerateInstructions(e.target.value)}
                                  placeholder="Describe what you'd like changed, e.g. 'faster pace, more upbeat music'"
                                  rows={2}
                                  className="w-full text-sm border border-gray-300 rounded-md p-2.5 focus:outline-none focus:ring-2 focus:ring-violet-500 resize-none"
                                />
                              </div>
                            )}

                            {/* Generate Error Banner — surfaces the last failed
                                generation attempt's message (after the backend has
                                already retried and given up) directly above the
                                button, instead of only the transient toast. */}
                            {falVideoError && (
                              <div className="flex items-start gap-2 text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-md p-2.5">
                                <i className="ri-error-warning-line text-sm mt-0.5 shrink-0"></i>
                                <span>{falVideoError}</span>
                              </div>
                            )}

                            {/* Poll-timeout notice — the browser stopped checking, but the
                                job itself is still running server-side (video is the
                                slowest SMM generation type and can occasionally outlast
                                even a generous poll window). Deliberately not styled as an
                                error: "Check again" just resumes polling the same job, no
                                new charge. */}
                            {!isGenerateInFlight && videoGeneration.isTimedOutFor(openPostId) && (
                              <div className="flex items-center justify-between gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-2.5">
                                <span className="flex items-start gap-2">
                                  <i className="ri-time-line text-sm mt-0.5 shrink-0"></i>
                                  <span>Still working — this one's taking longer than usual. It'll appear here as soon as it's ready.</span>
                                </span>
                                <button
                                  onClick={handleCheckVideoAgain}
                                  className="shrink-0 px-2.5 py-1 bg-amber-600 hover:bg-amber-700 disabled:opacity-60 text-white rounded-md text-xs font-medium transition-colors cursor-pointer disabled:cursor-not-allowed"
                                >
                                  Check again
                                </button>
                              </div>
                            )}

                            <div className="flex items-center justify-between">
                              <span className="text-xs font-medium text-gray-600 flex items-center gap-1.5">
                                Status:
                                {/* Derived from the same real signals used around this block —
                                    the actual async video-generation job state, not a
                                    separate tracked value. A poll timeout shows as still
                                    "PROCESSING" (amber), not "FAILED" — the job hasn't
                                    actually failed, the browser just stopped checking. */}
                                <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border ${isGenerateInFlight ? 'bg-blue-100 text-blue-800 border-blue-200' :
                                  videoGeneration.isTimedOutFor(openPostId) ? 'bg-amber-100 text-amber-800 border-amber-200' :
                                    falVideoError ? 'bg-rose-100 text-rose-800 border-rose-200' :
                                      (generatedVideoUrl || editingPost.imageUrl) ? 'bg-emerald-100 text-emerald-800 border-emerald-200' :
                                        'bg-gray-100 text-gray-800 border-gray-200'
                                  }`}>
                                  {isGenerateInFlight ? 'GENERATING' : videoGeneration.isTimedOutFor(openPostId) ? 'PROCESSING' : falVideoError ? 'FAILED' : (generatedVideoUrl || editingPost.imageUrl) ? 'DONE' : 'IDLE'}
                                </span>
                              </span>

                              <button
                                // Forces a full remount (rather than an in-place children
                                // patch) whenever the button's meaning changes — e.g. right
                                // as generation completes and it flips from "Generating..."
                                // to "Regenerate". Observed in the wild as the button
                                // occasionally settling with its icon but no label text
                                // immediately after a generation finished, self-resolving
                                // on modal close/reopen or a page refresh — both of which
                                // force exactly this kind of remount. No logic path in this
                                // ternary can itself produce empty text, so this guards
                                // against a stale in-place reconciliation rather than a
                                // wrong value.
                                key={`video-generate-btn-${isGenerateInFlight ? 'inflight' : (generatedVideoUrl || editingPost.imageUrl || hasAttemptedGenerate) ? 'regenerate' : 'generate'}-${isCurrentGenerateLocked ? 'locked' : 'unlocked'}`}
                                onClick={handleGenerateButtonClick}
                                disabled={isGenerateInFlight || !(editScript || editingPost?.script || editingPost?.creativeCopy)}
                                className={
                                  isCurrentGenerateLocked
                                    ? "px-4 py-2 bg-amber-50 hover:bg-amber-100 disabled:opacity-60 text-amber-700 border border-amber-200 rounded-md text-xs font-medium transition-colors whitespace-nowrap flex items-center gap-1.5 cursor-pointer disabled:cursor-not-allowed"
                                    : "px-4 py-2 bg-violet-600 hover:bg-violet-700 disabled:bg-gray-300 text-white rounded-md text-xs font-medium transition-colors whitespace-nowrap flex items-center gap-1.5 cursor-pointer"
                                }
                              >
                                {isGenerateInFlight ? (
                                  <><i className="ri-loader-4-line animate-spin"></i> {generatedVideoUrl || editingPost.imageUrl ? 'Regenerating...' : 'Generating...'}</>
                                 ) : (generatedVideoUrl || editingPost.imageUrl || hasAttemptedGenerate) ? (
                                   <>
                                     <i className={isCurrentGenerateLocked ? "ri-lock-2-line" : "ri-refresh-line"}></i>
                                     {isCurrentGenerateLocked
                                       ? (currentGenerateCost != null
                                           ? `Regenerate (${currentGenerateCost.toLocaleString()} credits)`
                                           : 'Regenerate (With Credits)')
                                       : `Regenerate ${editingPost?.format || 'Post'}`}
                                   </>
                                 ) : isCurrentGenerateLocked ? (
                                  <>
                                    <i className="ri-lock-2-line"></i>
                                    {currentGenerateCost != null
                                      ? `${currentGenerateCost.toLocaleString()} CREDITS REQUIRED`
                                      : 'Generate (With Credits)'}
                                  </>
                                ) : (
                                  <><i className="ri-magic-line"></i> {`Generate ${editingPost?.format || 'Post'}`}</>
                                )}
                              </button>
                            </div>
                            {isGenerateInFlight && (
                              <div className="mt-2 rounded-md border border-violet-200 bg-violet-50 p-3 space-y-2">
                                <div className="flex items-center justify-between text-xs">
                                  <span className="flex items-center gap-1.5 font-medium text-violet-800">
                                    <i className="ri-sparkling-2-line animate-pulse"></i>
                                    {getVideoGenStageMessage(videoGenElapsedSec)}
                                  </span>
                                  <span className="text-violet-600 tabular-nums">{formatElapsedTime(videoGenElapsedSec)}</span>
                                </div>
                                <div className="h-1.5 w-full rounded-full bg-violet-100 overflow-hidden">
                                  <div
                                    className="h-full rounded-full bg-violet-500 transition-all duration-1000 ease-linear"
                                    style={{ width: `${getVideoGenProgressPct(videoGenElapsedSec)}%` }}
                                  />
                                </div>
                                <p className="text-[11px] text-violet-600">
                                  Usually takes 2-4 minutes — feel free to work on other modules while this finishes.
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Video preview / play — a pulsing skeleton in place of the
                          video before one exists yet, or the real player with a
                          dimmed loading overlay on top while regenerating. */}
                      {(isGenerateInFlight || generatedVideoUrl || editingPost.imageUrl) && (
                        <div className="border-t border-gray-200 pt-5">
                          <h4 className="text-sm font-semibold text-gray-900 mb-3">Video Asset Preview</h4>
                          <div className="rounded-lg border border-gray-200 overflow-hidden bg-black aspect-video flex items-center justify-center relative">
                            {(generatedVideoUrl || editingPost.imageUrl) ? (
                              <video
                                src={generatedVideoUrl || editingPost.imageUrl}
                                controls
                                className="w-full h-full max-h-64 object-contain"
                              />
                            ) : (
                              <div className="absolute inset-0 bg-gray-200 animate-pulse" />
                            )}
                            {isGenerateInFlight && (
                              <div className="absolute inset-0 bg-black/55 flex flex-col items-center justify-center gap-2 text-white text-center px-4">
                                <i className="ri-loader-4-line animate-spin text-2xl"></i>
                                <span className="text-xs font-medium">{getVideoGenStageMessage(videoGenElapsedSec)}</span>
                                <span className="text-[11px] text-white/70 tabular-nums">{formatElapsedTime(videoGenElapsedSec)}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* ===== Article Tab ===== */}
              {editTab === 'article' && (
                <div style={{ padding: '16px 0' }}>
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{
                      display: 'block', fontSize: '13px',
                      fontWeight: '600', color: '#374151', marginBottom: '8px'
                    }}>
                      Article Content
                    </label>
                    <textarea
                      disabled={editingPost.userEdited}
                      value={articleContent || editingPost?.creativeCopy || editingPost?.creative_copy || ''}
                      onChange={(e) => setArticleContent(e.target.value)}
                      rows={18}
                      style={{
                        width: '100%', padding: '12px',
                        border: '1px solid #e5e7eb', borderRadius: '8px',
                        fontSize: '14px', lineHeight: '1.6',
                        color: '#1f2937', resize: 'vertical',
                        fontFamily: 'Georgia, serif',
                        backgroundColor: editingPost.userEdited ? '#f9fafb' : 'white',
                      }}
                      placeholder="Click Generate Article to create a long-form LinkedIn article..."
                    />
                    <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px' }}>
                      {(articleContent || '').length} characters
                    </div>
                  </div>
                  {!editingPost.userEdited && (
                    <>
                      <div style={{ marginBottom: '16px' }}>
                        <label style={{
                          display: 'block', fontSize: '13px',
                          fontWeight: '600', color: '#374151', marginBottom: '6px'
                        }}>
                          Additional Input for Regeneration
                        </label>
                        <textarea
                          value={articleRegenerationInput}
                          onChange={(e) => setArticleRegenerationInput(e.target.value)}
                          rows={3}
                          style={{
                            width: '100%', padding: '10px 12px',
                            border: '1px solid #e5e7eb', borderRadius: '8px',
                            fontSize: '13px', lineHeight: '1.5',
                            color: '#1f2937', resize: 'vertical',
                          }}
                          placeholder="Add specific points, data, stats, or instructions you want included in the article..."
                        />
                        <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px' }}>
                          This input will be positively considered during regeneration.
                        </div>
                      </div>
                      <button
                        onClick={handleGenerateArticle}
                        disabled={isGeneratingArticle}
                        style={{
                          width: '100%', padding: '12px',
                          backgroundColor: isGeneratingArticle ? '#9ca3af' : '#6366f1',
                          color: 'white', border: 'none', borderRadius: '8px',
                          fontWeight: '600', fontSize: '14px',
                          cursor: isGeneratingArticle ? 'not-allowed' : 'pointer',
                        }}
                      >
                        {isGeneratingArticle ? '✍️ Regenerating Article...' : '✍️ Regenerate Article'}
                      </button>
                    </>
                  )}
                </div>
              )}

              {/* Footer Actions */}
              <div className="sticky bottom-0 bg-white pt-4 pb-2 border-t border-gray-200 flex gap-3 z-20">
                {!editingPost.userEdited ? (
                  <div className="flex flex-col w-full gap-1">
                    <div className="flex gap-3">
                      <button
                        onClick={() => setShowSaveConfirm(true)}
                        disabled={!hasGeneratedMedia}
                        title={!hasGeneratedMedia ? 'Generate a post image or video first before saving' : ''}
                        className="flex-1 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-md text-sm font-medium transition-colors whitespace-nowrap cursor-pointer"
                      >
                        Save Changes
                      </button>
                      <button
                        onClick={handleCancelEdit}
                        className="flex-1 px-4 py-2.5 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer"
                      >
                        Cancel
                      </button>
                    </div>
                    {!hasGeneratedMedia && (
                      <p style={{ color: '#ef4444', fontSize: '12px', textAlign: 'center', marginTop: '4px' }}>
                        Please generate a post first before saving.
                      </p>
                    )}
                    {isEditingPostPublished ? (
                      <div className="w-full px-4 py-2.5 bg-green-50 border border-green-200 text-green-700 rounded-md text-sm font-medium text-center flex items-center justify-center gap-1.5">
                        <i className="ri-checkbox-circle-line" />
                        Published to {editingPost.platform || 'the platform'} — locked
                      </div>
                    ) : (selectedImageUrl || selectedVideoUrl || editingPost?.imageUrl) && (
                      <button
                        onClick={handlePublishClick}
                        disabled={isPublishing}
                        className="w-full px-4 py-2.5 bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-md text-sm font-medium transition-colors whitespace-nowrap cursor-pointer"
                      >
                        {isPublishing ? 'Publishing...' : `Publish to ${editingPost.platform || 'Platform'}`}
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="flex flex-col gap-2 w-full">
                    {isEditingPostPublished ? (
                      <div className="w-full px-4 py-2.5 bg-green-50 border border-green-200 text-green-700 rounded-md text-sm font-medium text-center flex items-center justify-center gap-1.5">
                        <i className="ri-checkbox-circle-line" />
                        Published to {editingPost.platform || 'the platform'} — locked
                      </div>
                    ) : (selectedImageUrl || selectedVideoUrl || editingPost?.imageUrl) && (
                      <button
                        onClick={handlePublishClick}
                        disabled={isPublishing}
                        className="w-full px-4 py-2.5 bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-md text-sm font-medium transition-colors whitespace-nowrap cursor-pointer"
                      >
                        {isPublishing ? 'Publishing...' : `Publish to ${editingPost.platform || 'Platform'}`}
                      </button>
                    )}
                    <button
                      onClick={handleCancelEdit}
                      className="w-full px-4 py-2.5 bg-gray-900 hover:bg-gray-800 text-white rounded-md text-sm font-medium transition-colors whitespace-nowrap cursor-pointer"
                    >
                      Close
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Publish-without-logo guard */}
      {showNoLogoPublishConfirm && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl border border-gray-200 shadow-2xl w-full max-w-sm p-6 space-y-4">
            <div className="text-center">
              <div className="w-12 h-12 bg-amber-50 rounded-full flex items-center justify-center text-amber-500 mx-auto mb-3">
                <i className="ri-error-warning-line text-2xl"></i>
              </div>
              <h4 className="text-base font-bold text-gray-900">No logo applied yet</h4>
              <p className="text-xs text-gray-500 mt-2 leading-relaxed">
                No logo applied yet. Publish without logo?
              </p>
            </div>
            <div className="flex gap-3 pt-2">
              <button
                onClick={() => setShowNoLogoPublishConfirm(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-xs font-medium text-gray-700 hover:bg-gray-100 transition-colors cursor-pointer"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowNoLogoPublishConfirm(false);
                  handlePublishPost();
                }}
                className="flex-1 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-xs font-medium transition-colors cursor-pointer"
              >
                Publish anyway
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Save Confirmation Modal */}
      {showSaveConfirm && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl border border-gray-200 shadow-2xl w-full max-w-sm p-6 space-y-4">
            <div className="text-center">
              <div className="w-12 h-12 bg-amber-50 rounded-full flex items-center justify-center text-amber-500 mx-auto mb-3">
                <i className="ri-error-warning-line text-2xl"></i>
              </div>
              <h4 className="text-base font-bold text-gray-900">Lock & Save Post?</h4>
              <p className="text-xs text-gray-500 mt-2 leading-relaxed">
                Once saved, this post is finalized. You will not be able to edit its content or drag-and-drop it to another date.
              </p>
            </div>
            <div className="flex gap-3 pt-2">
              <button
                onClick={() => setShowSaveConfirm(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-xs font-medium text-gray-700 hover:bg-gray-100 transition-colors cursor-pointer"
              >
                Go Back
              </button>
              <button
                onClick={async () => {
                  setShowSaveConfirm(false);
                  await handleSaveEdit();
                }}
                className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-medium transition-colors cursor-pointer"
              >
                Confirm & Lock
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl border border-gray-200 shadow-2xl w-full max-w-sm p-6 space-y-4">
            <div className="text-center">
              <div className="w-12 h-12 bg-red-50 rounded-full flex items-center justify-center text-red-600 mx-auto mb-3">
                <i className="ri-delete-bin-line text-2xl"></i>
              </div>
              <h4 className="text-base font-bold text-gray-900">Delete this post?</h4>
              <p className="text-xs text-gray-500 mt-2 leading-relaxed">
                This will permanently remove the post from your calendar and free up a replacement slot. This action cannot be undone.
              </p>
            </div>
            <div className="flex gap-3 pt-2">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                disabled={isDeletingPost}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-xs font-medium text-gray-700 hover:bg-gray-100 transition-colors cursor-pointer disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                disabled={isDeletingPost}
                className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white rounded-lg text-xs font-medium transition-colors cursor-pointer flex items-center justify-center gap-1.5"
              >
                {isDeletingPost ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i> Deleting...
                  </>
                ) : (
                  'Delete Post'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* A day's festivals / holidays / observance days */}
      {eventsPopupDate && (
        <div
          className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
          onClick={() => setEventsPopupDate(null)}
        >
          <div
            role="dialog"
            aria-label={`Events on ${formatDayMonthLabel(eventsPopupDate)}`}
            className="bg-white rounded-xl w-full max-w-sm shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <div>
                <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Events</p>
                <h3 className="text-sm font-semibold text-gray-900">{formatDayMonthLabel(eventsPopupDate)}</h3>
              </div>
              <button
                onClick={() => setEventsPopupDate(null)}
                className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors cursor-pointer"
                aria-label="Close"
              >
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>
            <ul className="max-h-80 overflow-y-auto px-5 py-3 space-y-3">
              {(dayEvents[eventsPopupDate] || []).map((obs) => (
                <li key={obs.name} className="flex items-start gap-2.5">
                  <i className="ri-calendar-event-line text-teal-600 text-sm mt-0.5"></i>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-800">{obs.name}</p>
                    <p className="text-xs text-gray-500">{obs.description || obs.type}</p>
                  </div>
                  <button
                    onClick={() => {
                      const date = eventsPopupDate;
                      setEventsPopupDate(null);
                      handleOpenAddPost(date, obs.name);
                    }}
                    className="shrink-0 text-[11px] font-semibold text-blue-600 hover:text-blue-800 cursor-pointer"
                    title={`Add a post for ${obs.name}`}
                  >
                    + Post
                  </button>
                </li>
              ))}
            </ul>
            <div className="flex items-center justify-between px-5 py-3 border-t border-gray-100">
              <Link to="/profile?tab=social" className="text-xs font-semibold text-[#0859B8] hover:underline">
                Manage observances
              </Link>
              <button
                onClick={() => {
                  const date = eventsPopupDate;
                  setEventsPopupDate(null);
                  handleOpenAddPost(date);
                }}
                className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded-lg transition-colors flex items-center gap-1 cursor-pointer"
              >
                <i className="ri-add-line"></i> Add Post
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Post Modal */}
      {showAddPostModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl border border-gray-200 shadow-2xl w-full max-w-lg overflow-hidden animate-fadeIn">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-gray-50/50">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center font-bold">
                  <i className="ri-add-line text-lg"></i>
                </div>
                <div>
                  <h3 className="text-base font-bold text-gray-900">Add New Post</h3>
                  <p className="text-xs text-gray-500">Create a new post for your content calendar</p>
                </div>
              </div>
              <button
                onClick={() => setShowAddPostModal(false)}
                className="text-gray-400 hover:text-gray-600 p-1.5 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-4 max-h-[75vh] overflow-y-auto">
              {addPostError && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-3.5 py-2.5 rounded-lg text-xs flex items-start gap-2">
                  <i className="ri-error-warning-line text-sm mt-0.5 shrink-0"></i>
                  <div className="flex-1 leading-relaxed">{addPostError}</div>
                </div>
              )}

              {/* Mode indicator & toggle */}
              {availableSlotsInfo?.deleted_slots && availableSlotsInfo.deleted_slots.length > 0 ? (
                <div className="flex items-center justify-between p-3 rounded-lg border bg-gradient-to-r from-amber-50/70 to-blue-50/70 border-gray-200 text-xs">
                  <div className="flex items-center gap-2">
                    <i className={isManualMode ? 'ri-edit-line text-blue-600' : 'ri-refresh-line text-amber-600'}></i>
                    <span className="font-semibold text-gray-800">
                      {isManualMode ? 'Mode: Manual Post' : `Mode: Fill AI Deletion Slot (${availableSlotsInfo.deleted_slots[0].deleted_format})`}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      const nextMode = !isManualMode;
                      setIsManualMode(nextMode);
                      if (!nextMode && availableSlotsInfo?.deleted_slots && availableSlotsInfo.deleted_slots[0]) {
                        if (availableSlotsInfo.deleted_slots[0].scheduled_date) {
                          setAddPostDate(availableSlotsInfo.deleted_slots[0].scheduled_date);
                        }
                        if (availableSlotsInfo.deleted_slots[0].allowed_replacement_formats?.length > 0) {
                          setAddPostFormat(mapFormatFromBackend(availableSlotsInfo.deleted_slots[0].allowed_replacement_formats[0]));
                        }
                      }
                    }}
                    className="px-2.5 py-1 text-[11px] font-semibold rounded border border-blue-300 bg-white text-blue-600 hover:bg-blue-50 transition-colors cursor-pointer"
                  >
                    {isManualMode ? 'Switch to AI Slot' : 'Switch to Manual Post'}
                  </button>
                </div>
              ) : (
                <div className="p-2.5 rounded-lg border border-blue-100 bg-blue-50/60 text-blue-800 text-xs flex items-center gap-2">
                  <i className="ri-information-line text-sm text-blue-600"></i>
                  <span>Adding a <strong>Manual Post</strong> (Unlimited posts of any format).</span>
                </div>
              )}

              {/* Platform Selector */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">Platform</label>
                <div className="grid grid-cols-4 gap-2">
                  {['LinkedIn', 'Instagram', 'Facebook', 'YouTube'].map((plat) => {
                    const isSelected = addPostPlatform === plat;
                    return (
                      <button
                        key={plat}
                        type="button"
                        onClick={() => setAddPostPlatform(plat)}
                        className={`flex flex-col items-center justify-center p-2.5 rounded-lg border text-xs font-medium transition-all cursor-pointer ${
                          isSelected
                            ? 'border-blue-600 bg-blue-50/70 text-blue-700 font-semibold shadow-xs'
                            : 'border-gray-200 hover:border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
                        }`}
                      >
                        <i className={`${getPlatformIcon(plat)} text-lg mb-1`}></i>
                        <span>{plat}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Format Selector */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="block text-xs font-semibold text-gray-700">Content Format</label>
                  {!isManualMode && availableSlotsInfo?.deleted_slots && availableSlotsInfo.deleted_slots.length > 0 && (
                    <span className="text-[10px] text-amber-700 font-medium bg-amber-50 px-2 py-0.5 rounded border border-amber-200">
                      Allowed: {availableSlotsInfo.deleted_slots[0].allowed_replacement_formats?.join(', ')}
                    </span>
                  )}
                  {isManualMode && (
                    <span className="text-[10px] text-blue-700 font-medium bg-blue-50 px-2 py-0.5 rounded border border-blue-200">
                      Manual: Any format
                    </span>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  {['Static Image', 'Carousel', 'Infographic', 'Video', 'Article'].map((fmt) => {
                    const isSelected = addPostFormat === fmt;
                    const backendFmt = mapFormatToBackend(fmt);
                    const isAllowed = isManualMode || !availableSlotsInfo?.deleted_slots || availableSlotsInfo.deleted_slots.length === 0 || (
                      availableSlotsInfo.deleted_slots[0].allowed_replacement_formats &&
                      availableSlotsInfo.deleted_slots[0].allowed_replacement_formats.includes(backendFmt)
                    );

                    return (
                      <button
                        key={fmt}
                        type="button"
                        disabled={!isAllowed}
                        onClick={() => setAddPostFormat(fmt)}
                        className={`px-3 py-1.5 rounded-md border text-xs font-medium transition-all cursor-pointer ${
                          !isAllowed
                            ? 'opacity-40 bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed'
                            : isSelected
                            ? 'border-blue-600 bg-blue-600 text-white font-semibold shadow-xs'
                            : 'border-gray-200 hover:border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                        title={!isAllowed ? 'Format restricted by AI deletion replacement rule' : fmt}
                      >
                        {fmt}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Scheduled Date & Theme Row */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="block text-xs font-semibold text-gray-700">
                      Scheduled Date <span className="text-red-500">*</span>
                    </label>
                    {!isManualMode && availableSlotsInfo?.deleted_slots && availableSlotsInfo.deleted_slots.length > 0 && (
                      <span className="text-[10px] text-amber-700 font-medium bg-amber-50 px-1.5 py-0.5 rounded border border-amber-200 flex items-center gap-1">
                        <i className="ri-lock-line text-[10px]"></i> Locked to slot date
                      </span>
                    )}
                  </div>
                  <input
                    type="date"
                    disabled={!isManualMode && Boolean(availableSlotsInfo?.deleted_slots && availableSlotsInfo.deleted_slots.length > 0)}
                    value={addPostDate}
                    onChange={(e) => setAddPostDate(e.target.value)}
                    min={subscriptionRange?.start}
                    max={subscriptionRange?.end}
                    className={`w-full border rounded-md px-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      !isManualMode && availableSlotsInfo?.deleted_slots && availableSlotsInfo.deleted_slots.length > 0
                        ? 'bg-gray-100 border-gray-300 text-gray-600 cursor-not-allowed'
                        : 'border-gray-300 bg-white text-gray-800'
                    }`}
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">Strategic Theme</label>
                  <select
                    value={addPostTheme}
                    onChange={(e) => setAddPostTheme(e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-gray-800"
                  >
                    <option value="Brand story">Brand Story</option>
                    <option value="Educational Post">Educational Post</option>
                    <option value="Product Post">Product Post</option>
                    <option value="Service Post">Service Post</option>
                    <option value="Engagement Post">Engagement Post</option>
                    <option value="Authority Post">Authority Post</option>
                    <option value="Awareness Post">Awareness Post</option>
                    <option value="Testimonial Post">Testimonial Post</option>
                    <option value="Offer Post">Offer Post</option>
                    <option value="Promotional Post">Promotional Post</option>
                    <option value="Event Post">Event Post</option>
                    <option value="Award Post">Award Post</option>
                    <option value="Observance Post">Observance Post</option>
                  </select>
                </div>

                {isObservancePost && (
                  <div className="sm:col-span-2">
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">Observance</label>
                    {addPostObservance ? (
                      <>
                        <select
                          value={addPostObservance.name}
                          onChange={(e) => setAddPostFestival(e.target.value)}
                          disabled={addPostObservances.length === 1}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-gray-800 disabled:bg-gray-50 disabled:text-gray-800"
                        >
                          {addPostObservances.map((o) => (
                            <option key={o.name} value={o.name}>{o.name}</option>
                          ))}
                        </select>
                        <p className="text-[10px] text-gray-500 mt-1">
                          Found for this date. The post will be written for {addPostObservance.name} and kept a static image.
                        </p>
                      </>
                    ) : (
                      <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                        {addPostDate ? 'No observance on this date. Choose another date or a different theme.' : 'Pick a date to find its observance.'}
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* Caption / Topic Outline */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-xs font-semibold text-gray-700">Caption / Topic Outline</label>
                  <span className="text-[10px] text-gray-400">Optional</span>
                </div>
                <textarea
                  value={addPostCaption}
                  onChange={(e) => setAddPostCaption(e.target.value)}
                  placeholder="Enter initial post topic, hook, or caption..."
                  rows={3}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none bg-white text-gray-800"
                />
              </div>
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-3.5 border-t border-gray-200 bg-gray-50 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowAddPostModal(false)}
                disabled={isAddingPost}
                className="px-4 py-2 border border-gray-300 rounded-lg text-xs font-medium text-gray-700 hover:bg-gray-100 transition-colors cursor-pointer disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleCreatePost}
                disabled={isAddingPost || !addPostDate}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg text-xs font-semibold shadow-xs transition-colors cursor-pointer flex items-center gap-1.5"
              >
                {isAddingPost ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i> Adding Post...
                  </>
                ) : (
                  <>
                    <i className="ri-add-line"></i> Add Post
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Stats Row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-5 border border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-600">This Period</span>
            <i className="ri-calendar-event-line text-emerald-600 text-xl"></i>
          </div>
          <p className="text-3xl font-bold text-gray-900">{stats.thisMonthPosts}</p>
          <p className="text-xs text-gray-500 mt-1">Posts planned</p>
        </div>
        <div className="bg-white rounded-xl p-5 border border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-600">Saved</span>
            <i className="ri-calendar-check-line text-emerald-600 text-xl"></i>
          </div>
          <p className="text-3xl font-bold text-gray-900">{stats.totalScheduled}</p>
          <p className="text-xs text-gray-500 mt-1">Ready to publish</p>
        </div>
        <div className="bg-white rounded-xl p-5 border border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-600">In Draft</span>
            <i className="ri-draft-line text-amber-600 text-xl"></i>
          </div>
          <p className="text-3xl font-bold text-gray-900">{stats.totalDraft}</p>
          <p className="text-xs text-gray-500 mt-1">Need refinement</p>
        </div>
        <div className="bg-white rounded-xl p-5 border border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-600">Published</span>
            <i className="ri-global-line text-slate-600 text-xl"></i>
          </div>
          <p className="text-3xl font-bold text-gray-900">{stats.totalPublished}</p>
          <p className="text-xs text-gray-500 mt-1">Already live</p>
        </div>
      </div>

      {/* Toolbar */}
      <div className="bg-white rounded-xl p-4 border border-gray-200 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          {/* The calendar always shows the full active subscription period as
              one continuous view — there is no month to page through. */}
          <span className="text-lg font-bold text-gray-900 text-center">{subscriptionRangeLabel}</span>
        </div>
        
        <div className="flex items-center gap-3">
          {/* Observances chosen in setup are edited in Profile → Social Media */}
          <Link
            to="/profile?tab=social"
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50 border border-gray-200 rounded-lg transition-colors"
            title="Choose the festivals and days you want posts for"
          >
            <i className="ri-calendar-event-line"></i>
            <span>Manage observances</span>
          </Link>

          <div className="flex bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode('calendar')}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all ${
                viewMode === 'calendar'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <i className="ri-calendar-line mr-1.5"></i>Calendar
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all ${
                viewMode === 'list'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <i className="ri-list-check-2 mr-1.5"></i>List
            </button>
          </div>

          {/* Global + Add Post Button */}
          <button
            onClick={() => handleOpenAddPost()}
            className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium shadow-sm transition-colors cursor-pointer"
            title="Create a new post"
          >
            <i className="ri-add-line text-base font-semibold"></i>
            <span>Add Post</span>
          </button>
        </div>
      </div>

      {/* Calendar View */}
      {viewMode === 'calendar' && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          {/* Day Headers */}
          <div className="grid grid-cols-7 border-b border-gray-200">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
              <div key={d} className="py-2.5 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50">
                {d}
              </div>
            ))}
          </div>
          {/* Calendar Grid */}
          <div className="grid grid-cols-7 auto-rows-fr">
            {calendarCells.map((dateIso, idx) => (
              <div
                key={idx}
                onDragOver={(e) => dateIso && handleDayDragOver(e, dateIso)}
                onDragLeave={handleDayDragLeave}
                onDrop={(e) => dateIso && handleDayDrop(e, dateIso)}
                className={`min-h-[140px] border-r border-b border-gray-100 p-2 transition-colors ${dateIso
                  ? dragOverDay === dateIso
                    ? 'bg-blue-50 border-blue-300'
                    : 'bg-white hover:bg-gray-50'
                  : 'bg-gray-50/50'
                  }`}
              >
                {dateIso && (
                  <>
                    <div className="flex items-center justify-between mb-1.5">
                      <span className={`text-sm font-semibold ${dateIso === todayIso ? 'w-fit px-2 h-7 flex items-center justify-center bg-blue-600 text-white rounded-full' : 'text-gray-700'
                        }`}>{formatDayMonthLabel(dateIso)}</span>
                      {postsByDay[dateIso] && (
                        <span className="text-xs text-gray-400">{postsByDay[dateIso].length} post{postsByDay[dateIso].length > 1 ? 's' : ''}</span>
                      )}
                    </div>
                    {dayEvents[dateIso]?.length ? (
                      <button
                        type="button"
                        onClick={() => setEventsPopupDate(dateIso)}
                        className="w-full flex items-center gap-1 mb-1.5 text-left cursor-pointer"
                        title={dayEvents[dateIso].map((obs) => obs.name).join(' · ')}
                      >
                        <span className="min-w-0 flex-1 truncate text-[10px] leading-4 font-medium text-teal-700 bg-teal-50 border border-teal-100 rounded px-1.5 hover:bg-teal-100 transition-colors">
                          <i className="ri-calendar-event-line mr-0.5"></i>
                          {dayEvents[dateIso][0].name}
                        </span>
                        {dayEvents[dateIso].length > 1 && (
                          <span className="shrink-0 text-[10px] leading-4 font-semibold text-teal-700 bg-teal-100 rounded px-1.5 hover:bg-teal-200 transition-colors">
                            +{dayEvents[dateIso].length - 1}
                          </span>
                        )}
                      </button>
                    ) : null}
                    <div className="space-y-1.5">
                      {postsByDay[dateIso]?.map((post) => (
                        <div
                          key={post.id}
                          draggable={!post.userEdited}
                          onDragStart={(e) => handleDragStart(e, post.id)}
                          onDragEnd={handleDragEnd}
                          onClick={() => handleEditClick(post)}
                          className={`p-2 rounded-lg border border-gray-200 bg-white hover:border-blue-300 hover:shadow-sm transition-all group ${post.userEdited
                            ? 'opacity-80 border-gray-300 bg-gray-50/50 cursor-pointer'
                            : 'cursor-pointer active:cursor-grabbing'
                            } ${draggingPostId === post.id ? 'opacity-50' : ''}`}
                        >
                          <div className="flex items-center gap-1.5 mb-1">
                            <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${getPlatformColor(post.platform)}`}>
                              <i className={`${post.platformIcon} text-xs`}></i>
                            </span>
                            <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${getFormatBadge(post.format)}`}>
                              {post.format}
                            </span>
                            {post.userEdited && (
                              <span className="text-[9px] bg-slate-100 text-slate-600 px-1 rounded flex items-center gap-0.5">
                                <i className="ri-lock-line"></i> Saved
                              </span>
                            )}
                            {isPostGenerating(post.id) && (
                              <span className="text-[9px] bg-blue-50 text-blue-700 px-1 rounded flex items-center gap-0.5" title="Media is generating">
                                <i className="ri-loader-4-line animate-spin"></i> Generating
                              </span>
                            )}
                          </div>
                          {post.imageUrl && (
                            <div className="mb-2 mt-1 rounded border border-gray-100 overflow-hidden bg-gray-50 h-20 w-full flex items-center justify-center">
                              {post.imageUrl.startsWith('<svg') ? (
                                <div className="w-full h-full [&>svg]:w-full [&>svg]:h-full [&>svg]:object-contain" dangerouslySetInnerHTML={{ __html: post.imageUrl }} />
                              ) : isVideoAssetFormat(post.format) ? (
                                <video
                                  src={post.imageUrl}
                                  muted
                                  loop
                                  autoPlay
                                  playsInline
                                  preload="metadata"
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <img src={post.imageUrl} alt="Preview" className="w-full h-full object-cover" />
                              )}
                            </div>
                          )}
                          <p className="text-xs text-gray-700 line-clamp-2 leading-snug">{post.caption}</p>
                          <div className="flex items-center justify-between mt-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                            <span className={`text-[10px] px-1.5 py-0.5 rounded border ${getStatusColor(post.status)}`}>
                              {getStatusLabel(post.status)}
                            </span>
                            <div className="flex items-center gap-1">
                              <button
                                onClick={(e) => { e.stopPropagation(); handleEditClick(post); }}
                                className="w-5 h-5 flex items-center justify-center rounded text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-colors cursor-pointer"
                                title={post.userEdited ? "View post" : "Edit post"}
                              >
                                <i className={post.userEdited ? "ri-eye-line text-[11px]" : "ri-edit-line text-[11px]"}></i>
                              </button>
                              <button
                                onClick={(e) => { e.stopPropagation(); handleDownload(post); }}
                                className="w-5 h-5 flex items-center justify-center rounded text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 transition-colors cursor-pointer"
                                title="Download post"
                              >
                                <i className="ri-download-line text-[11px]"></i>
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEditingPost(post);
                                  setEditPostId(post.id);
                                  setShowDeleteConfirm(true);
                                }}
                                className="w-5 h-5 flex items-center justify-center rounded text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors cursor-pointer"
                                title="Delete post"
                              >
                                <i className="ri-delete-bin-line text-[11px]"></i>
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    {!postsByDay[dateIso] && (
                      <button
                        onClick={() => handleOpenAddPost(dateIso)}
                        className="w-full py-3.5 rounded-lg border border-dashed border-gray-200 hover:border-blue-400 bg-gray-50/60 hover:bg-blue-50/50 text-center text-xs font-medium text-gray-400 hover:text-blue-600 transition-all flex items-center justify-center gap-1 cursor-pointer group"
                        title={`Add post on ${formatDayMonthLabel(dateIso)}`}
                      >
                        <i className="ri-add-line text-sm text-gray-400 group-hover:text-blue-600"></i>
                        <span>Add Post</span>
                      </button>
                    )}
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* List View */}
      {viewMode === 'list' && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <div className="min-w-[900px]">
              <div className="p-4 border-b border-gray-200 bg-gray-50 flex items-center gap-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                <span className="w-24 shrink-0">Date</span>
                <span className="w-28 shrink-0">Platform</span>
                <span className="w-28 shrink-0">Format</span>
                <span className="flex-1">Caption Preview</span>
                <span className="w-24 shrink-0">Status</span>
                <span className="w-24 shrink-0 text-right">Actions</span>
              </div>
              <div className="divide-y divide-gray-100">
                {posts.map((post) => (
              <div
                key={post.id}
                draggable={!post.userEdited}
                onDragStart={(e) => handleDragStart(e, post.id)}
                onDragEnd={handleDragEnd}
                onClick={() => handleEditClick(post)}
                className={`p-4 flex items-center gap-4 hover:bg-gray-50 transition-colors group ${post.userEdited
                  ? 'opacity-80 bg-gray-50/50 cursor-pointer'
                  : 'cursor-pointer active:cursor-grabbing'
                  } ${draggingPostId === post.id ? 'opacity-50' : ''}`}
              >
                <div className="w-28 shrink-0 flex items-center gap-1.5 text-sm text-gray-900">
                  <i className="ri-drag-move-line text-gray-300 cursor-grab"></i>
                  <span>{post.dayName}, {formatDayMonthLabel(post.date)}</span>
                </div>
                <div className="w-28 shrink-0">
                  <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded border ${getPlatformColor(post.platform)}`}>
                    <i className={`${post.platformIcon} text-xs`}></i>
                    {post.platform}
                  </span>
                </div>
                <div className="w-28 shrink-0">
                  <span className={`text-xs px-2 py-1 rounded font-medium ${getFormatBadge(post.format)}`}>
                    {post.format}
                  </span>
                  {post.userEdited && (
                    <span className="ml-1.5 inline-flex items-center text-[9px] bg-slate-100 text-slate-600 px-1 py-0.5 rounded font-medium">
                      <i className="ri-lock-line mr-0.5"></i>Saved
                    </span>
                  )}
                  {isPostGenerating(post.id) && (
                    <span className="ml-1.5 inline-flex items-center text-[9px] bg-blue-50 text-blue-700 px-1 py-0.5 rounded font-medium" title="Media is generating">
                      <i className="ri-loader-4-line animate-spin mr-0.5"></i>Generating
                    </span>
                  )}
                </div>
                <div className="flex-1 min-w-0 flex items-center gap-3">
                  {post.imageUrl && (
                    <div className="w-12 h-12 flex-shrink-0 rounded bg-gray-100 overflow-hidden border border-gray-200 flex items-center justify-center">
                      {post.imageUrl.startsWith('<svg') ? (
                        <div className="w-full h-full [&>svg]:w-full [&>svg]:h-full [&>svg]:object-contain" dangerouslySetInnerHTML={{ __html: post.imageUrl }} />
                      ) : (
                        <img src={post.imageUrl} alt="Preview" className="w-full h-full object-cover" />
                      )}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-900 line-clamp-2 whitespace-normal break-words">{post.caption}</p>
                    <div className="flex items-center gap-1.5 mt-1">
                      <span className="text-[10px] text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">{themeLabel(post.themeType)}</span>
                      <span className="text-[10px] text-gray-400"><i className="ri-hashtag mr-0.5"></i>{post.hashtags.length} tags</span>
                      <span className="text-[10px] text-gray-400"><i className="ri-time-line mr-0.5"></i>{postTimeLabel(post)}</span>
                    </div>
                  </div>
                </div>
                <div className="w-24 shrink-0">
                  <span className={`text-xs px-2 py-1 rounded border ${getStatusColor(post.status)}`}>
                    {getStatusLabel(post.status)}
                  </span>
                </div>
                <div className="w-24 shrink-0 flex items-center justify-end gap-1">
                  <button
                    onClick={(e) => { e.stopPropagation(); handleEditClick(post); }}
                    className="w-7 h-7 flex items-center justify-center rounded text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-colors cursor-pointer"
                    title={post.userEdited ? "View post" : "Edit post"}
                  >
                    <i className={post.userEdited ? "ri-eye-line text-sm" : "ri-edit-line text-sm"}></i>
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); handleDownload(post); }}
                    className="w-7 h-7 flex items-center justify-center rounded text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 transition-colors cursor-pointer"
                    title="Download post"
                  >
                    <i className="ri-download-line text-sm"></i>
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditingPost(post);
                      setEditPostId(post.id);
                      setShowDeleteConfirm(true);
                    }}
                    className="w-7 h-7 flex items-center justify-center rounded text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors cursor-pointer"
                    title="Delete post"
                  >
                    <i className="ri-delete-bin-line text-sm"></i>
                  </button>
                </div>
              </div>
            ))}
          </div>
            </div>
          </div>
        </div>
      )}

      {/* Drag & Drop Instruction */}
      <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200 flex items-center gap-3">
        <div className="w-9 h-9 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
          <i className="ri-drag-move-line text-emerald-600"></i>
        </div>
        <div>
          <p className="text-sm font-semibold text-emerald-900">Drag & Drop is Active</p>
          <p className="text-xs text-emerald-700 mt-0.5">Drag any post from the list view or calendar and drop it onto another day to reschedule. The post date and day name update automatically.</p>
        </div>
      </div>
      <LowCreditModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        currentCredits={modalCredits}
        requiredCredits={modalRequired}
        isBlocker={modalIsBlocker}
      />
      {/*
        Confirm-before-charge popup for the Media tab's locked Generate
        button. Reuses the Website Builder's spend-confirmation modal shell
        (CreditDeductionConfirmModal) — deliberately not CreditGateModal,
        which is a hard access gate with no proceed action and would be the
        wrong fit here (Section 0: this popup's confirm click is the actual
        charge-trigger moment, via handleGenerate -> consume_or_charge).
      */}
      <CreditDeductionConfirmModal
        isOpen={isGenerateConfirmOpen}
        onClose={() => setIsGenerateConfirmOpen(false)}
        onConfirm={handleConfirmGenerate}
        currentCredits={balance?.total_credits_balance ?? 0}
        deductCredits={currentGenerateCost ?? 0}
        isLoading={isLoadingBalance}
        isProcessing={isGenerateInFlight}
        description={`Spend ${(currentGenerateCost ?? 0).toLocaleString()} credits to generate this ${editingPost?.format?.toLowerCase() || 'post'}?`}
      />
    </div>
  );
}
